import React, {
  useEffect,
  useMemo,
  useCallback,
  useState,
  useRef,
} from "react";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Container from "react-bootstrap/Container";
import ListGroup from "react-bootstrap/ListGroup";
import { FaSearch, FaCheck } from "react-icons/fa";
import "./CodeSearch.css";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import { useSelector, useDispatch } from "react-redux";
import { useDebounce } from "use-debounce";
import { useLazyGetData2Query } from "../../../store/apiSlice";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { toast, ToastContainer } from "react-toastify";
import {
  clusterIdGenerator,
  prioritizeParts,
  smartSplitWithBracket,
} from "./utils";
import {
  updateCodePathHistory,
  clearCodePathHistory,
  handleSelectedCodeDetails,
  handleStoreParentAndChilds,
  setChildFilterTerm,
  updateFinalScreenState,
  removeSecondLastFromHistory,
  handleResetSelectedDetails,
  updateLocalState,
  resetLocalState,
  setSelectedCategory,
  setSelectedIndex,
  setFilteredCodes,
  clearSelectedCodeDetails,
  resetInstructions,
  clearPendingInstructions,
  setPendingInstructions,
  setCurrentWarningMessage,
  clearWarningMessage,
  setArrivedViaBackToFinal,
  setRangeChildren,
  setInitialInjuryState,
  setIsInjuryWithoutActivityFlow,
  setNextRangeToProcess,
  setInjuryFlowTracker,
  setInjuryCodeFlowTracker,
  setFinalRangeExecuted,
  setCurrentClusterId,
  addToClusterHistory,
  setPendingInjuryAfterInstructions,
  setPendingInjuryCode,
  removeCurrentPathwayFromHistory,
  setPendingManifestationAfterInstructions,
  setManifestationWarningMessage,
  clearManifestationState,
  savePathwaySnapshot,
  restoreToPathwayStep,
} from "./redux/codeSearchSlice";
import {
  setRawSearchQuery,
  setSearchByCodeId,
  updateAcsCode,
  updateNcaCode,
} from "../../CodeBook/redux/CodeBookSlicer";
import {
  handleResetSearch,
  setActiveTab,
  setActiveIcon,
} from "../../CodeBook/redux/CodeBookSlicer";
import { normalizeSearchText } from "../../CodingScreen/utils";
import achiCodesData from "../../CodingScreen/achi_codes.json";


const createNoOption = () => ({
  rawName: "No or already coded",
  rawDescription: "No or already coded",
  name: "No or already coded",
  description: "No or already coded",
  children: [],
  isFromTabular: false,
  _isCustomOption: true,
});
const normalizeCodeObject = (code) => {
  if (!code) return null;
  return {
    ...code,
    rawName: code.rawName || code.name || "",
    rawDescription: code.rawDescription || code.description || "",
  };
};
const extractSeeAlsoTerm = (seealso) => {
  if (!seealso || typeof seealso !== "string") return "";
  return seealso
    .trim()
    .replace(/^\(?\s*(?:see also|see)\s+/i, "")
    .replace(/\)\s*$/, "")
    .trim();
};
const NAV_EXCLUDED_KEYS = new Set([
  "injuryWithActivity",
  "externalCauseCodes",
  "placeOfOccurrence",
  "activityCodes",
  "injuryWithoutActivity",
  "injuries",
  "burns",
  "frostbite",
  "poisoning",
  "effectsOfExternalCauses",
  "earlyComplicationsOfTrauma",
  "complicationOfSurgicalAndMedicalCare",
  "otherComplicationOfTrauma",
  "sequelaeOfInjuries",
  "chronicCodes",
  "pathwaySnapshots",
]);

const toNavState = (state) => {
  const light = {};
  for (const key of Object.keys(state || {})) {
    if (!NAV_EXCLUDED_KEYS.has(key)) light[key] = state[key];
  }
  return light;
};
const CodeSearch = React.memo(
  ({
    setShowCodeSearch,
    onFinalizeCode,
    codingscreendata,
    setfilecode,
    setIsFetchingData1,
    isFetchingData1,
    enableFinalize,
    handleCodeAssistAdding,
    diagnosisStates,
    skipCategoryResetRef,
  }) => {
    // Replace useReducer with Redux selectors
    const localState = useSelector((state) => state.codeSearch);
    const {
      icd10amTabularData13,
      icd10amIndexData13,
      achiIndexData13,
      achiTabularData13,
      codePathHistory,
    } = useSelector((state) => ({
      ...state.codeBook,
      codePathHistory: state.codeSearch?.codePathHistory || [],
    }));
    const {
      slectedParntesandChilds,
      childFilterTerm,
      arrivedViaBackToFinal,
      injuryWithActivity,
      externalCauseCodes,
      placeOfOccurrence,
      activityCodes,
      injuryWithoutActivity,
      initialInjuryState,
      isInjuryWithoutActivityFlow,
      nextRangeToProcess,
      injuries,
      burns,
      frostbite,
      poisoning,
      effectsOfExternalCauses,
      earlyComplicationsOfTrauma,
      complicationOfSurgicalAndMedicalCare,
      otherComplicationOfTrauma,
      sequelaeOfInjuries,
      injuryFlowTracker,
      injuryCodeFlowTracker,
      finalRangeExecuted,
      currentClusterId,
    } = useSelector((state) => state.codeSearch);
    const rangeChildren = useMemo(
      () => ({
        injuryWithActivity,
        externalCauseCodes,
        placeOfOccurrence,
        activityCodes,
        injuryWithoutActivity,
        injuries,
        burns,
        frostbite,
        poisoning,
        effectsOfExternalCauses,
        earlyComplicationsOfTrauma,
        complicationOfSurgicalAndMedicalCare,
        otherComplicationOfTrauma,
        sequelaeOfInjuries,
      }),
      [
        injuryWithActivity,
        externalCauseCodes,
        placeOfOccurrence,
        activityCodes,
        injuryWithoutActivity,
        injuries,
        burns,
        frostbite,
        poisoning,
        effectsOfExternalCauses,
        earlyComplicationsOfTrauma,
        complicationOfSurgicalAndMedicalCare,
        otherComplicationOfTrauma,
        sequelaeOfInjuries,
      ],
    );
    const reduxDispatch = useDispatch();
    const [stateList, setStateList] = useState([]);
    const [isBackProcessing, setIsBackProcessing] = useState(false);
    const [fetchData1] = useLazyGetData2Query();
    const [startNewCycle, setStartNewCycle] = useState(false);
    const [noInjuryFlowState, setNoInjuryFlowState] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const restartInjuryCycle = sessionStorage.getItem("newcycle") === "true";
    const searchCancelRef = useRef(0);
    const perfLogRef = useRef({ lastPop: 0, lastPush: 0 });
    const suppressAutoFocusRef = useRef(false);
    const isKeyboardNavigatingRef = useRef(false);
    const forwardStackRef = useRef([]);
    const searchInputRef = useRef(null);

    const focusSearchInput = useCallback(() => {
      if (!suppressAutoFocusRef.current) {
        searchInputRef.current?.focus();
      }
    }, []);
    const [pathwayStartSnapshot, setPathwayStartSnapshot] = useState(null);
    const [pathwayHistoryCheckpoint, setPathwayHistoryCheckpoint] = useState(0);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const {
      searchTerm,
      selectedIndex,
      filteredCodes,
      selectedCategory,
      currentCodeSearchData,
      showFinalScreen,
      selectedFinalCode,
      initialSearchDone,
      selectedCodes: localSelectedCodes,
      showError,
      selectedParent,
      topLevelParent,
      lastAddedCode,
      originalCode,
      currentPath,
      originalSpecialCodesWithSymbols,
      // nextRangeToProcess
    } = localState;
    const isLockedCoder = codingscreendata?.data?.meta?.is_locked;
    const currentPathRef = useRef(currentPath);
    const detectSearchType = (term) => {
      const value = term.trim();

      const startsWithLetterNumber = /^[A-Za-z]\d/.test(value);
      const startsWithNumber = /^\d/.test(value);

      //  DIAGNOSIS TAB → ONLY ICD
      if (
        selectedCategory === "Principal Diagnosis" ||
        selectedCategory === "Additional Diagnoses"
      ) {
        if (startsWithLetterNumber) return "ICD_TABULAR";
        return "INDEX"; //  block ACHI here
      }

      //  PROCEDURE TAB → ONLY ACHI
      if (selectedCategory === "Procedure Codes") {
        if (startsWithLetterNumber || startsWithNumber) return "ACHI_TABULAR";
        return "INDEX";
      }

      return "INDEX";
    };
    const capturePathwaySnapshot = useCallback(() => {
      setPathwayStartSnapshot(
        JSON.parse(JSON.stringify(localState.selectedCodes)),
      );
      setPathwayHistoryCheckpoint(codePathHistory.length);
    }, [localState.selectedCodes, codePathHistory.length]);
    useEffect(() => {
      currentPathRef.current = currentPath;
    }, [currentPath]);
    // Add this useEffect after your other useEffect declarations
    // useEffect(() => {
    //   if (showFinalScreen) {
    //     // Check if Principal Diagnosis has more than 1 code
    //     const principalCodes = localSelectedCodes["Principal Diagnosis"] || [];

    //     if (principalCodes.length > 1) {
    //       // Keep the first code in Principal, move the rest to Additional
    //       const firstPrincipalCode = principalCodes[0];
    //       const remainingPrincipalCodes = principalCodes.slice(1);

    //       const existingAdditionalCodes = localSelectedCodes["Additional Diagnoses"] || [];

    //       // Remove the principal code from Additional if it already exists
    //       const filteredAdditionalCodes = existingAdditionalCodes.filter(
    //         code => code !== firstPrincipalCode
    //       );

    //       // Merge and remove duplicates
    //       const uniqueAdditionalCodes = [
    //         ...new Set([...remainingPrincipalCodes, ...filteredAdditionalCodes]),
    //       ];

    //       // Update the selected codes
    //       const updatedSelectedCodes = {
    //         ...localSelectedCodes,
    //         "Principal Diagnosis": [firstPrincipalCode],
    //         "Additional Diagnoses": uniqueAdditionalCodes,
    //       };

    //       // Update the Redux state
    //       reduxDispatch(updateLocalState({
    //         selectedCodes: updatedSelectedCodes
    //       }));
    //     }
    //   }
    // }, [showFinalScreen, localSelectedCodes]);

    const dataSource = useMemo(() => {
      const searchType = detectSearchType(searchTerm);

      // console.log(" Data Source Type:", searchType);

      // TABULAR SWITCH
      if (searchType === "ICD_TABULAR") {
        return icd10amTabularData13 || [];
      }

      if (searchType === "ACHI_TABULAR") {
        return achiTabularData13 || [];
      }

      // EXISTING INDEX FLOW (UNCHANGED)
      if (
        selectedCategory === "Principal Diagnosis" ||
        selectedCategory === "Additional Diagnoses"
      ) {
        return currentCodeSearchData || icd10amIndexData13 || [];
      } else if (selectedCategory === "Procedure Codes") {
        return currentCodeSearchData || achiIndexData13 || [];
      }

      return [];
    }, [
      searchTerm,
      currentCodeSearchData,
      icd10amIndexData13,
      achiIndexData13,
      icd10amTabularData13,
      achiTabularData13,
      selectedCategory,
    ]);
    const filterCodes = useMemo(
      () => (term, data, isInitialSearchDone) => {
        if (!(term.length >= 3 && !isInitialSearchDone))
          return isInitialSearchDone ? data : [];

        const normalizedTerm = normalizeSearchText(term);
        const normalizeText = normalizeSearchText;

        const results = data.flatMap((item) => {
          const parentMatches = normalizeText(item.name).startsWith(
            normalizedTerm,
          )
            ? [
                {
                  // parent object properties
                  _type: "parent",
                  id: item.id,
                  name: item.name,
                  description: item.description,
                  rawName: item.name,
                  rawDescription: item.description,
                  children: item.subcodes || item.children || [],
                  Next_parent_code: item.Next_parent_code,
                  Warning_message: item.Warning_message,
                  see: item.see,
                  seealso: item.seealso,
                  acs: item.acs,
                  nca: item.nca,
                  code_first_instruction: item.code_first_instruction,
                  use_additional_instruction: item.use_additional_instruction,
                  codealsowhenperf_instruction:
                    item.codealsowhenperf_instruction,
                  codeto_instruction: item.codeto_instruction,
                  manual_added: item.manual_added,
                },
              ]
            : [];

          const childMatches = (item.subcodes || item.children || [])
            .filter((child) =>
              normalizeText(child.name).startsWith(normalizedTerm),
            )
            .map((child) => ({
              // child object properties
              _type: "child",
              id: child.id,
              name: child.name,
              description: child.description,
              rawName: child.name,
              rawDescription: child.description,
              Next_parent_code: child.Next_parent_code,
              Warning_message: child.Warning_message,
              children: item.subcodes || item.children || [],
              see: child.see,
              seealso: child.seealso,
              parentName: item.name,
              parentDescription: item.description,
              acs: child.acs,
              nca: child.nca,
              parentId: item.id,
              parentAcs: item.acs,
            }));

          return [...parentMatches, ...childMatches];
        });

        // return results.sort((a, b) => {
        //   if (a._type === "parent" && b._type !== "parent") return -1;
        //   if (a._type !== "parent" && b._type === "parent") return 1;
        //   return (a.rawName || "").localeCompare(b.rawName || "");
        // });

        return results.sort((a, b) => {
          const order = (item) => {
            const isParent = item._type === "parent";
            const isChild = item._type === "child";
            const hasSee = !!item.see;

            // Priority mapping
            if (isParent && !hasSee) return 1;
            if (isParent && hasSee) return 2;
            if (isChild && hasSee) return 3;
            if (isChild && !hasSee) return 4;
            return 5; // fallback
          };

          const diff = order(a) - order(b);
          if (diff !== 0) return diff;

          // Fallback alphabetical order
          return (a.rawName || "").localeCompare(b.rawName || "");
        });
      },
      [],
    );

    // Does NOT touch normalizeSearchText globally, so other search flows are unaffected.
    const stripCodeSeparators = (str) => (str || "").replace(/[.\-/]/g, "");
    const filterDirectCodes = (term, data) => {
      if (term.length < 2) return [];

      const normalizedTerm = normalizeSearchText(term);
      const normalizeText = normalizeSearchText;

      // comparison-only versions (separators removed)
      const compareTerm = stripCodeSeparators(normalizedTerm);

      let exactMatches = [];
      let broadMatches = [];

      const traverse = (nodes) => {
        nodes.forEach((node) => {
          const name = normalizeText(node.name);
          const compareName = stripCodeSeparators(name);
          const isLeaf = !node.children || node.children.length === 0;

          //ONLY MATCH LEAF NODES (MAIN FIX)
          if (isLeaf) {
            if (compareName === compareTerm) {
              exactMatches.push({
                _type: "leaf",
                id: node.id,
                name: node.name,
                description: node.description,
                rawName: node.name,
                rawDescription: node.description,
                code: node.code,
                // acs: node.acs,
                // code_first_instruction: node.code_first_instruction,
                // use_additional_instruction: node.use_additional_instruction,
                // codealsowhenperf_instruction: node.codealsowhenperf_instruction,
                isFromTabular: true,
                isDirectCode: true,
                children: [],
              });
            } else if (compareName.startsWith(compareTerm)) {
              broadMatches.push({
                _type: "leaf",
                id: node.id,
                name: node.name,
                description: node.description,
                rawName: node.name,
                rawDescription: node.description,
                code: node.code,
                // acs: node.acs,
                // code_first_instruction: node.code_first_instruction,
                // use_additional_instruction: node.use_additional_instruction,
                // codealsowhenperf_instruction: node.codealsowhenperf_instruction,
                isFromTabular: true,
                isDirectCode: true,
                children: [],
              });
            }
          }

          //  ALWAYS TRAVERSE CHILDREN
          if (node.children && node.children.length > 0) {
            traverse(node.children);
          }
        });
      };

      traverse(data);

      // ✅ PRIORITY: exact > broad
      return exactMatches.length > 0 ? exactMatches : broadMatches;
    };
    const findTabularChildrenWithHyphen = useCallback(
      (items, normalizedCode, level = 0, results = []) => {
        items.forEach((item) => {
          if (item.chapter && item.codes) {
            findTabularChildrenWithHyphen(
              item.codes,
              normalizedCode,
              level + 1,
              results,
            );
            return;
          }

          const rawItemCode = item.name || "";
          if (rawItemCode.includes("/") && !normalizedCode.includes("/")) {
            return;
          }
          const itemCodeNormalized = rawItemCode
            .replace(/\./g, "")
            .toLowerCase();
          const matchesCode =
            itemCodeNormalized === normalizedCode ||
            itemCodeNormalized.startsWith(normalizedCode);

          if (matchesCode) {
            const children = item.subcodes || item.children || [];
            const validTargets = (
              children.length > 0 ? children : [item]
            ).filter((target) => {
              const targetName = target.name || item.name || "";
              if (targetName.includes("/") && !normalizedCode.includes("/")) {
                return false;
              }
              return true;
            });

            validTargets.forEach((target) =>
              results.push({
                rawName: target.name || item.name,
                rawDescription: target.description || item.description,
                name: target.name || item.name,
                description: target.description || item.description,
                children: target.subcodes || target.children || [],
                isFromTabular: true,
                id: target.id || item.id,
                acs: target.acs || item.acs,
                nca: target.nca || item.nca,
                ...(target === item
                  ? {}
                  : {
                      code_first_instruction: target.code_first_instruction,
                      use_additional_instruction:
                        target.use_additional_instruction,
                      codealsowhenperf_instruction:
                        target.codealsowhenperf_instruction,
                      codeto_instruction: target.codeto_instruction,
                      manual_added: target.manual_added,
                    }),
              }),
            );
          }
          // Search all possible nested structures
          [item.codes, item.subcodes, item.children]
            .filter(Boolean)
            .forEach((nested) =>
              findTabularChildrenWithHyphen(
                nested,
                normalizedCode,
                level + 1,
                results,
              ),
            );
        });

        return results;
      },
      [],
    );
    const findCodesWithPrefixInBlock = useCallback(
      (codePrefix, blockNumber, category) => {
        if (category !== "Procedure Codes") return [];

        const tabularData = achiTabularData13;
        if (!tabularData || !Array.isArray(tabularData)) return [];

        const targetBlock = blockNumber.toString();
        const targetPrefix = codePrefix.toString().trim();
        const foundCodes = [];
        const seenCodes = new Set();

        const searchForCodesInBlock = (items) => {
          for (const item of items) {
            let isInCorrectBlock = false;

            // Check if this item represents the block
            if (item.name) {
              const nameStr = item.name.toString().trim();
              if (
                nameStr === targetBlock ||
                nameStr.startsWith(`${targetBlock} `) ||
                nameStr.startsWith(`${targetBlock}-`)
              ) {
                isInCorrectBlock = true;
              }
            }

            // If this is the block, search its children for codes starting with the prefix
            if (isInCorrectBlock) {
              const collectCodesWithPrefix = (blockItems) => {
                for (const child of blockItems) {
                  const childName = child.name?.toString().trim() || "";

                  // Check if the code starts with the target prefix
                  if (childName.startsWith(targetPrefix)) {
                    const codeKey = `${childName}-${child.id}`;
                    if (!seenCodes.has(codeKey)) {
                      seenCodes.add(codeKey);
                      foundCodes.push({
                        rawName: child.name,
                        rawDescription: child.description || child.name,
                        name: child.name,
                        description: child.description || child.name,
                        children: child.subcodes || child.children || [],
                        isFromTabular: true,
                        id: child.id,
                        acs: child.acs,
                        nca: child.nca,
                        code_first_instruction: child.code_first_instruction,
                        use_additional_instruction:
                          child.use_additional_instruction,
                        codealsowhenperf_instruction:
                          child.codealsowhenperf_instruction,
                        blockNumber: targetBlock,
                        parentBlockName: item.name,
                        codeto_instruction: child.codeto_instruction,
                        manual_added: child.manual_added,
                      });
                    }
                  }

                  // Search deeper children
                  const deeperChildren = child.children || child.subcodes || [];
                  if (deeperChildren.length > 0) {
                    collectCodesWithPrefix(deeperChildren);
                  }
                }
              };

              const children = item.children || item.subcodes || [];
              collectCodesWithPrefix(children);

              if (foundCodes.length > 0) break;
            }

            // Continue searching for the block
            const children = item.children || item.subcodes || [];
            if (children.length > 0) {
              searchForCodesInBlock(children);
            }

            if (item.codes && Array.isArray(item.codes)) {
              searchForCodesInBlock(item.codes);
            }
          }
        };

        searchForCodesInBlock(tabularData);

        // Sort codes alphabetically
        foundCodes.sort((a, b) =>
          (a.rawName || "").localeCompare(b.rawName || ""),
        );

        return foundCodes;
      },
      [achiTabularData13],
    );

    const compareCodes = (code, start, end) => {
      // Convert to comparable string: remove dots and uppercase
      const c = code.replace(/\./g, "").toUpperCase();
      const s = start.replace(/\./g, "").toUpperCase();
      const e = end.replace(/\./g, "").toUpperCase();
      return c >= s && c <= e;
    };

    const findTabularChildren = useCallback(
      (
        items,
        normalizedCode,
        results = [],
        found = { value: false },
        extraOptionToIdentify = null, 
      ) => {
        const isRange = normalizedCode?.includes("-");
        let rangeStart, rangeEnd;

        if (isRange) {
          const parts = normalizedCode
            .split("-")
            .map((c) => c.trim()?.toUpperCase());
          [rangeStart, rangeEnd] = [parts[0], parts[1]];
          normalizedCode = normalizedCode.toUpperCase(); // CHANGE 1 - ADD THIS LINE

        } else {
          normalizedCode = normalizedCode?.toUpperCase();
        }

        const createResultItem = (item, isChild = false) => ({
          rawName: item.name,
          rawDescription: item.description,
          name: item.name,
          description: item.description,
          children: item.subcodes || item.children || [],
          isFromTabular: true,
          id: item.id,
          acs: item.acs,
          nca: item.nca,
          use_additional_instruction: item.use_additional_instruction,
          codealsowhenperf_instruction: item.codealsowhenperf_instruction,
          code_first_instruction: item.code_first_instruction,
          codeto_instruction: item.codeto_instruction,
          ...(extraOptionToIdentify &&
            !isChild && {
            code_first_instruction: item.code_first_instruction,
            use_additional_instruction: item.use_additional_instruction,
             codeto_instruction: item.codeto_instruction,
          }),
          codealsowhenperf_instruction: item.codealsowhenperf_instruction,
          manual_added: item.manual_added,
        });

        const isInRange = (code) => {
          if (!isRange) return false;
          return rangeEnd
            ? compareCodes(code, rangeStart, rangeEnd)
            : code.startsWith(rangeStart);
        };

        for (const item of items) {
          if (found.value) break;

          // Handle chapters
          if (item.chapter && item.codes) {
            const chapterCode = item.chapter?.replace(/\./g, "")?.toUpperCase();
            if (!isRange && chapterCode === normalizedCode) {
              findTabularChildren(
                item.codes,
                normalizedCode,
                results,
                found,
                extraOptionToIdentify,
              );
              found.value = true;
              return results;
            }
           findTabularChildren(              // CHANGE 2 - ADD THESE 2 LINES
          item.codes,                     //    (before the continue below)
          normalizedCode,
          results,
          found,
          extraOptionToIdentify,
        );
        if (found.value) return results;
            continue;
          }

          const itemCode = item.name;
          if (!itemCode) continue;

          const itemCodeNormalized = itemCode.replace(/\./g, "").toUpperCase();
          const itemIsItselfARange = itemCode.includes("-"); //  CHANGE 3a - ADD THIS LINE
          // const matches =
          //   itemCodeNormalized === normalizedCode ||
          //   isInRange(itemCodeNormalized);
          const matches = itemIsItselfARange                  //  CHANGE 3b - REPLACE the matches block below
            ? itemCodeNormalized === normalizedCode
            : (itemCodeNormalized === normalizedCode || isInRange(itemCodeNormalized));

          if (matches) {
            const itemChildren = item.subcodes || item.children || [];
            const targets = itemChildren.length > 0 ? itemChildren : [item];
            targets.forEach((target) =>
              results.push(createResultItem(target, itemChildren.length > 0)),
            );
            found.value = true;
          }

          // Recurse deeper if not found
          if (!found.value) {
            [item.codes, item.subcodes, item.children]
              .filter(Boolean)
              .forEach((nested) =>
                findTabularChildren(
                  nested,
                  normalizedCode,
                  results,
                  found,
                  extraOptionToIdentify,   
                ),
              );
          }
        }

        return results;
      },
      [],
    );
    // Recursive function to get ALL children from a code (including nested children)
    const getAllNestedChildren = useCallback((code) => {
      if (!code) return [];

      let allChildren = [];

      // Add the current code itself if it's a leaf node (no children)
      const hasChildren =
        (code.children && code.children.length > 0) ||
        (code.subcodes && code.subcodes.length > 0);

      if (!hasChildren) {
        allChildren.push(code);
      }

      // Recursively get children from subcodes
      if (code.subcodes && code.subcodes.length > 0) {
        code.subcodes.forEach((subcode) => {
          allChildren = [...allChildren, ...getAllNestedChildren(subcode)];
        });
      }

      // Recursively get children from children array
      if (code.children && code.children.length > 0) {
        code.children.forEach((child) => {
          allChildren = [...allChildren, ...getAllNestedChildren(child)];
        });
      }

      return allChildren;
    }, []);
    const filterTabularCodes = useCallback(
      (baseCode, category,characterStr = null) => {
        // if (category === "Procedure Codes") return [];      
        const normalizedCode = baseCode?.replace(/\./g, "").toLowerCase();
        const tabularData =
          category === "Procedure Codes"
            ? achiTabularData13
            : icd10amTabularData13;
        const result = findTabularChildren(tabularData || [], normalizedCode );
        if (characterStr) {
          // const expanded = result.flatMap((item) => {
          //   const childCode = item.rawName?.replace(/\./g, "").toLowerCase();
          //   if (!childCode) return [item];

          //   const nestedChildren = findTabularChildren(tabularData || [], childCode );
          //   return nestedChildren.length > 0 ? nestedChildren : [item];
         const expandDeep = (items, depth = 0) => {
        if (depth > 5) return items; // safety cap against infinite loops
        return items.flatMap((item) => {
          const name = item.rawName || item.name || "";
          if (name.includes(".")) return [item]; // already a leaf, stop here

          const childCode = name.replace(/\./g, "").toLowerCase();
          if (!childCode) return [item];

          const nestedChildren = findTabularChildren(tabularData || [], childCode);
          if (nestedChildren.length === 0) return [item]; // nothing deeper, keep as-is
          return expandDeep(nestedChildren, depth + 1); // recurse instead of stopping
        });
          };
         const expanded = expandDeep(result);
          const filtered = applyCharacterFilter(expanded, characterStr, category);
          // console.log(filtered, "stage3")
          return filtered;
        }

    return result; 
      },
      [icd10amTabularData13, achiTabularData13, findTabularChildren],
    );
    const isInvalidAchiCode = (code) => {
      if (!code) return false;
      const description = code.rawDescription || code.description || "";
      const match = description.match(/^([^\[]+)\s+\[(\d+)\]\s*$/);
      return match ? !match[1].trim().includes("-") : false;
    };
    // Recursive function to get ALL codes with complete hierarchy
    const getAllCodesWithHierarchy = useCallback((code, depth = 0) => {
      if (!code) return [];

      let allCodes = [];

      // Add the current code itself
      allCodes.push({
        ...code,
        _depth: depth,
        _isRangeCode: true,
        _isParentNode:
          (code.children && code.children.length > 0) ||
          (code.subcodes && code.subcodes.length > 0),
      });

      // Recursively process subcodes (this is where intermediate parents like S00.0 are)
      if (code.subcodes && code.subcodes.length > 0) {
        code.subcodes.forEach((subcode) => {
          const subcodeResults = getAllCodesWithHierarchy(subcode, depth + 1);
          allCodes = [...allCodes, ...subcodeResults];
        });
      }

      // Recursively process children
      if (code.children && code.children.length > 0) {
        code.children.forEach((child) => {
          const childResults = getAllCodesWithHierarchy(child, depth + 1);
          allCodes = [...allCodes, ...childResults];
        });
      }

      return allCodes;
    }, []);

    // Enhanced function to get ALL codes from a range with complete hierarchy
    const getAllCodesFromRange = useCallback(
      (rangeCode, category) => {
        // First, get the main range children
        const rangeChildren = filterTabularCodes(rangeCode, category);

        if (!rangeChildren || rangeChildren.length === 0) {
          return [];
        }

        let allCodes = [];

        // For each child in the range, get complete hierarchy
        rangeChildren.forEach((child) => {
          const hierarchyCodes = getAllCodesWithHierarchy(child);
          allCodes = [...allCodes, ...hierarchyCodes];
        });

        // Remove duplicates based on ID while preserving hierarchy
        const uniqueCodes = allCodes.filter(
          (code, index, self) =>
            index === self.findIndex((c) => c.id === code.id),
        );

        return uniqueCodes;
      },
      [filterTabularCodes, getAllCodesWithHierarchy],
    );

    // Fetch range data on component mount
    // Fetch range data on component mount - enhanced to include parents
    const fetchAndStoreRangeChildren = useCallback(
      (selectedCategory = "Principal Diagnosis") => {
        const ranges = [
          {
            code: "S00-S09,S10-S29,S20-S29,S30-S39,S40-S49,S50-S59,S60-S69,S70-S79,S80-S89,S90-S99,T00-T07,T08-T14,T15-T19,T20-T31,T33-T35,T36-T50,T51-T65",
            name: "Injury Codes with Activity",
            stateKey: "injuryWithActivity",
          },
          {
            code: "V00-X59,X60-X84,X85-Y09,Y10-Y34,Y35-Y36,Y37-Y37,Y40-Y84,Y85-Y89,Y90,Y91,Y95,Y96,Y97,Y98",
            name: "External Cause Codes",
            stateKey: "externalCauseCodes",
          },
          {
            code: "Y92.0,Y92.1,Y92.2,Y92.3,Y92.4,Y92.5,Y92.6,Y92.7,Y92.8,Y92.9",
            name: "Place of Occurrence",
            stateKey: "placeOfOccurrence",
          },
          {
            code: "U50-U72,U73.0,U73.1,U73.2,U73.81,U73.8, U73.9",
            name: "Activity Codes",
            stateKey: "activityCodes",
          },
          {
            code: "T66-T78,T79-T79,T80-T88,T89-T89,T90-T98",
            name: "Injury Codes without Activity",
            stateKey: "injuryWithoutActivity",
          },
          {
            code: "S00-S09,S10-S29,S20-S29,S30-S39,S40-S49,S50-S59,S60-S69,S70-S79,S80-S89,S90-S99,T00-T07,T08-T14",
            name: "injuries",
            stateKey: "injuries",
          },
          {
            code: "T20-T31",
            name: "burns",
            stateKey: "burns",
          },
          {
            code: "T33-T35",
            name: "frostbite",
            stateKey: "frostbite",
          },
          {
            code: "T33-T35",
            name: "poisoning",
            stateKey: "poisoning",
          },
          {
            code: "T66-T78",
            name: "effectsOfExternalCauses",
            stateKey: "effectsOfExternalCauses",
          },
          {
            code: "T79-T79",
            name: "earlyComplicationsOfTrauma",
            stateKey: "earlyComplicationsOfTrauma",
          },
          {
            code: "T80-T88",
            name: "complicationOfSurgicalAndMedicalCare",
            stateKey: "complicationOfSurgicalAndMedicalCare",
          },
          {
            code: "T89-T89",
            name: "otherComplicationOfTrauma",
            stateKey: "otherComplicationOfTrauma",
          },
          {
            code: "T90-T98",
            name: "sequelaeOfInjuries",
            stateKey: "sequelaeOfInjuries",
          },
          {
            code: "U78-U88",
            name: "chronicCodes",
            stateKey: "chronicCodes",
          },
        ];

        let totalCodes = 0;
        let parentCount = 0;
        let childCount = 0;

        // Process each range
        ranges.forEach((range, rangeIndex) => {
          try {
            // console.group(
            //   ` Processing ${range.name} (${range.code}) - ${rangeIndex + 1}/${
            //     ranges.length
            //   }`
            // );

            const rangeParts = range.code.split(",");
            let allCodes = [];

            rangeParts.forEach((part, partIndex) => {
              const rangeCodes = getAllCodesFromRange(part, selectedCategory);

              if (rangeCodes && rangeCodes.length > 0) {
                allCodes = [...allCodes, ...rangeCodes];
              } else {
                // console.log(`No codes found in this part`);
              }
            });

            // Count parents and children
            const parentsInRange = allCodes.filter(
              (code) => code._isParentNode,
            ).length;
            const childrenInRange = allCodes.filter(
              (code) => !code._isParentNode,
            ).length;

            parentCount += parentsInRange;
            childCount += childrenInRange;

            // Store in Redux
            reduxDispatch(
              setRangeChildren({
                rangeType: range.stateKey,
                children: allCodes,
              }),
            );

            totalCodes += allCodes.length;
          } catch (error) {
            console.groupEnd();
          }
        });
      },
      [reduxDispatch, getAllCodesFromRange],
    );
    // Fetch all range individual codes when component mounts and tabular data is available
    useEffect(() => {
      const fetchRanges = () => {
        // Check if tabular data is available
        const hasTabularData = icd10amTabularData13 && achiTabularData13;

        if (hasTabularData) {
          fetchAndStoreRangeChildren(filterTabularCodes, "Principal Diagnosis");
        }
      };

      // Use a small delay to ensure everything is loaded
      const timer = setTimeout(fetchRanges, 1000);
      return () => clearTimeout(timer);
    }, [
      icd10amTabularData13,
      achiTabularData13,
      fetchAndStoreRangeChildren,
      filterTabularCodes,
    ]);

    // Capture snapshot when a fresh pathway starts
    useEffect(() => {
      if (searchTerm.length === 1 && !initialSearchDone && !showFinalScreen) {
        capturePathwaySnapshot();
      }
    }, [searchTerm]);
    const filterTabularCodesWithHyphen = useCallback(
      (baseCode, category) => {
        if (category === "Procedure Codes") return [];
        const normalizedCode = baseCode.replace(/\./g, "").toLowerCase();
        const tabularData =
          category === "Procedure Codes"
            ? achiTabularData13
            : icd10amTabularData13;
        return findTabularChildrenWithHyphen(tabularData || [], normalizedCode);
      },
      [icd10amTabularData13, achiTabularData13, findTabularChildrenWithHyphen],
    );

    const isHyphenCode = useCallback((code) => {
      if (!code || typeof code !== "string") return false;
      const trimmedCode = code.trim();
      // must contain at least one letter+digit combo
      if (!/[A-Za-z]\d/.test(trimmedCode)) return false;

      return (
        /-$/.test(trimmedCode) ||
        /\s+-\s+/.test(trimmedCode) ||
        /-\*$/.test(trimmedCode) ||
        /^[A-Za-z]\d+-(\.\d+|\d+)$/i.test(trimmedCode)
      );
    }, []);

    const extractCodesFromHyphen = useCallback(
      (code) => {
        if (!isHyphenCode(code)) return [code];
        const trimmedCode = code.trim();
        let codes = [];

        const patternMatch = trimmedCode.match(/^([A-Za-z]\d+)-(\.\d+|\d+)$/i);
        if (patternMatch) {
          const baseCode = patternMatch[1];
          const endPart = patternMatch[2];
          return { type: "range", start: baseCode, end: endPart };
        }

        if (trimmedCode.includes("+")) {
          const parts = trimmedCode.split("+");
          const firstCode = parts[0].replace(/[-*]$/, "").trim();
          const secondCode = parts[1].replace(/[-*]$/, "").trim();
          codes = [firstCode, secondCode];
        } else if (trimmedCode.includes(" - ")) {
          codes = [trimmedCode.split(" - ")[0].trim()];
        } else {
          codes = [trimmedCode.replace(/[-*]+$/, "").trim()];
        }
        return { type: "list", codes };
      },
      [isHyphenCode],
    );

    const getTabularExtensions = useCallback(
      (baseCode, category, extraOptionToIdentify = null) => {
        const normalizedCode = baseCode?.replace(/\./g, "").toLowerCase();
        const tabularData =
          category === "Procedure Codes"
            ? achiTabularData13
            : icd10amTabularData13;
        const extensions = findTabularChildren(
          tabularData || [],
          normalizedCode,
          [],
          { value: false },
          extraOptionToIdentify,
        );
        const uniqueExtensions = Array.from(
          new Set(extensions.map((ext) => ext.rawName)),
        ).map((name) => extensions.find((ext) => ext.rawName === name));
        return uniqueExtensions.map((ext) => {
          return {
            rawName: ext.rawName,
            rawDescription: ext.rawDescription || ext.name,
            description: ext.rawDescription || ext.name,
            children: ext.children || [],
            isFromTabular: true,
            id: ext.id,
            acs: ext.acs,
            nca: ext.nca,
            code_first_instruction: ext.code_first_instruction,
            codeto_instruction: ext.codeto_instruction,
            use_additional_instruction: ext.use_additional_instruction,
            codealsowhenperf_instruction: ext.codealsowhenperf_instruction,
            manual_added: ext.manual_added,
          };
        });
      },
      [icd10amTabularData13, achiTabularData13, findTabularChildren],
    );
    const getOwnCode = (code) => {
      if (!code) return null;
      const own =
        (code.rawDescription && code.rawDescription.trim()) ||
        (code.description && code.description.trim());
      return own || null;
    };

        const canShowUnspecified = (code) => {
  if (!code || code.isFromTabular) return false;
  const own = getOwnCode(code);
  if (!own) return false;
  if (isHyphenCode(own)) return false; // never show Unspecified for a hyphen code's own code
  return true;
};
    const processHyphenCode = useCallback(
      (selectedCode, category) => {
        if (category === "Procedure Codes") return null;

        const codeToCheck = (
          selectedCode.rawDescription ||
          selectedCode.description ||
          ""
        ).trim();
        if (!codeToCheck || !isHyphenCode(codeToCheck)) return null;

        const extracted = extractCodesFromHyphen(codeToCheck);

        let newFilteredCodes = [];
        if (extracted.type === "range") {
          const tabularResults = filterTabularCodesWithHyphen(
            extracted.start,
            category,
          ).filter((item) => item.rawName?.endsWith(extracted.end));
          newFilteredCodes = tabularResults;
        } else {
          const primaryCode = extracted.codes[0];
          const tabularResults = filterTabularCodesWithHyphen(
            primaryCode,
            category,
          );
          newFilteredCodes = tabularResults;
        }

        const indexChildren = (
          selectedCode.subcodes ||
          selectedCode.children ||
          []
        )
          .filter((child) => child.name?.trim())
          .map((child) => ({
            rawName: child.name,
            rawDescription: child.description,
            name: child.name,
            description: child.description,
            children: child.subcodes || child.children || [],
            isFromTabular: false,
            id: child.id,
            acs: child.acs,
            nca: child.nca,
            code_first_instruction: child.code_first_instruction,
            use_additional_instruction: child.use_additional_instruction,
            codealsowhenperf_instruction: child.codealsowhenperf_instruction,
            codeto_instruction: child.codeto_instruction,
            manual_added: child.manual_added,
            see: child.see,
            seealso: child.seealso,
          }));

        const filteredTabularResults = newFilteredCodes.filter(
          (item) => item.rawName?.trim() && item.rawDescription?.trim(),
        );

        const seenNames = new Set();
        const finalCodes = [...indexChildren, ...filteredTabularResults].filter(
          (item) => {
            if (seenNames.has(item.name)) return false;
            seenNames.add(item.name);
            return true;
          },
        );
        const hasSeeAlso = !!selectedCode.seealso?.trim();
        if (hasSeeAlso) {
          const seeAlsoTerm = extractSeeAlsoTerm(selectedCode.seealso);
          const unspecifiedOption = {
            rawName: `Unspecified (${seeAlsoTerm})`,
            rawDescription: `See also: ${seeAlsoTerm}`,
            name: `Unspecified (${seeAlsoTerm})`,
            description: seeAlsoTerm,
            isSeeAlsoOption: true,
            isUnspecifiedSeeAlso: true,
            option: seeAlsoTerm,
            originalCode: selectedCode,
            referenceId: selectedCode.id,
            category: category,
            acs: selectedCode.acs,
            nca: selectedCode.nca,
          };
          finalCodes.push(unspecifiedOption);
        } else if (canShowUnspecified(selectedCode)) {
          const ownCode = getOwnCode(selectedCode);
          finalCodes.push({
            rawName: selectedCode.rawName,
            rawDescription: ownCode,
            name: ownCode,
            description: ownCode,
            children: [],
            isUnspecified: true,
            isFromTabular: false,
            id: selectedCode.id,
            acs: selectedCode.acs,
            nca: selectedCode.nca,
            code_first_instruction: selectedCode.code_first_instruction,
            use_additional_instruction: selectedCode.use_additional_instruction,
            codealsowhenperf_instruction:
              selectedCode.codealsowhenperf_instruction,
            codeto_instruction: selectedCode.codeto_instruction,
            manual_added: selectedCode.manual_added,
          });
        }

        return { filteredCodes: finalCodes, parent: selectedCode };
      },
      [isHyphenCode, extractCodesFromHyphen, filterTabularCodesWithHyphen],
    );

    const buildHistoryNode = useCallback(
      (code, isParent = false) => {
        return {
          category: selectedCategory,
          code: code.rawName || code.rawDescription || "",
          description: isParent
            ? ""
            : code.rawDescription || code.rawName || "",
          isFromTabular: code.isFromTabular || false,
          timestamp: new Date().toISOString(),
          children: [],
          id: code.id,
        };
      },
      [selectedCategory],
    );

    const isCodeInHistory = (code, description, history) => {
      return history.some((entry) => {
        if (entry.code === code && entry.description === description)
          return true;
        if (entry.children) {
          return entry.children.some(
            (child) =>
              (child.code === code && child.description === description) ||
              (child.children &&
                child.children.some(
                  (grandchild) =>
                    grandchild.code === code &&
                    grandchild.description === description,
                )),
          );
        }
        return false;
      });
    };

    const prevHcpLineIdRef = useRef(null);

useEffect(() => {
  const currentHcpLineId = codingscreendata?.data?.meta?.hcp_line_id;

  // Only reset if the episode actually changed, not on every data refresh
  if (prevHcpLineIdRef.current === currentHcpLineId) return;
  prevHcpLineIdRef.current = currentHcpLineId;

  // Always clear local codesearch state (final screen list, search term, etc.)
  reduxDispatch(resetLocalState());

  // Only skip the CATEGORY override if it was just set manually via Alt+D/Alt+P
  if (skipCategoryResetRef.current) {
    skipCategoryResetRef.current = false;
    return;
  }

  const principalDiagnosisArray = diagnosisStates?.principal ?? [];
  const hasValidDiagnosis = principalDiagnosisArray.some(
    (item) =>
      (item?.code && item.code.trim()) ||
      (item?.description && item.description.trim()),
  );
  const initialCategory = hasValidDiagnosis
    ? "Additional Diagnoses"
    : "Principal Diagnosis";

  reduxDispatch(setSelectedCategory(initialCategory));

  window.history.replaceState(
    { ...localState, selectedCategory: initialCategory },
    "",
    window.location.pathname,
  );
}, [codingscreendata, diagnosisStates, reduxDispatch]);

    const [debouncedSearchTerm] = useDebounce(searchTerm, 100);
    // useEffect(() => {
    //   if (!initialSearchDone && searchTerm.length >= 2) {
    //     const filtered = filterCodes(
    //       debouncedSearchTerm,
    //       dataSource,
    //       initialSearchDone,
    //       selectedCategory,
    //     );
    //     reduxDispatch(setFilteredCodes(filtered));
    //   }
    // }, [
    //   debouncedSearchTerm,
    //   dataSource,
    //   initialSearchDone,
    //   selectedCategory,
    //   filterCodes,
    //   reduxDispatch,
    // ]);
    const filterByAbbreviations = useCallback((term, data) => {
      if (!term || term.length < 2) return [];

      const normalized = normalizeSearchText(term);
      const results = [];

      const traverse = (node) => {
        // const abbrValue = (node.abbreviations || node.abbr || "")
        //   .toLowerCase()
        //   .trim();

        // if (abbrValue && abbrValue.startsWith(normalized)) {
        //   results.push({
        //     ...node,
        //     rawName: node.rawName || node.name,
        //     rawDescription: node.rawDescription || node.description,
        //     children: node.subcodes || node.children || [],
        //     _matchedVia: "abbreviation",
        //     _abbr: node.abbreviations || node.abbr,
        //   });
        // }
        const abbrRaw = (node.abbreviations || node.abbr || "").trim();

        if (abbrRaw) {
          const abbrList = abbrRaw
            .split("),")
            .map((a) => a.trim())
            .filter(Boolean)
            .map((a) => (a.endsWith(")") ? a : a + ")"));

          if (abbrList.length === 1) {
            // single abbreviation — existing behavior unchanged
            if (normalizeSearchText(abbrList[0]).startsWith(normalized)) {
              results.push({
                ...node,
                rawName: node.rawName || node.name,
                rawDescription: node.rawDescription || node.description,
                children: node.subcodes || node.children || [],
                _matchedVia: "abbreviation",
                _abbr: abbrList[0],
              });
            }
          } else {
            // multiple abbreviations on same node
            abbrList.forEach((abbr, idx) => {
              if (abbr.toLowerCase().startsWith(normalized)) {
                results.push({
                  ...node,
                  rawName: node.rawName || node.name,
                  rawDescription: node.rawDescription || node.description,
                  children: node.subcodes || node.children || [],
                  _matchedVia: "abbreviation",
                  _abbr: abbr,
                  _abbrIndex: idx, // only set when multiple abbrs on same node
                });
              }
            });
          }
        }
        const kids = node.subcodes || node.children || [];
        kids.forEach((child) => traverse(child));
      };

      data.forEach((item) => traverse(item));
      return results;
    }, []);

    useEffect(() => {
      if (!initialSearchDone && searchTerm.length >= 2) {
        const searchType = detectSearchType(debouncedSearchTerm);
        let filtered = [];
        if (searchType === "ICD_TABULAR") {
          filtered = filterDirectCodes(
            debouncedSearchTerm,
            icd10amTabularData13,
          );
          reduxDispatch(setFilteredCodes(filtered));
          return;
        } else if (searchType === "ACHI_TABULAR") {
          filtered = filterDirectCodes(debouncedSearchTerm, achiTabularData13);
          reduxDispatch(setFilteredCodes(filtered));
          return;
        }

        const currentSearch = ++searchCancelRef.current;

        const isShortTerm = debouncedSearchTerm.length < 3;

        const nameSearchPromise = isShortTerm
          ? Promise.resolve([])
          : new Promise((resolve) => {
            setTimeout(() => {
              resolve(
                filterCodes(
                  debouncedSearchTerm,
                  dataSource,
                  false,
                  selectedCategory,
                ),
              );
            }, 0);
          });

        const abbrSearchPromise = new Promise((resolve) => {
          setTimeout(() => {
            resolve(filterByAbbreviations(debouncedSearchTerm, dataSource));
          }, 0);
        });

        Promise.all([nameSearchPromise, abbrSearchPromise]).then(
          ([nameResults, abbrResults]) => {
            if (searchCancelRef.current !== currentSearch) return;

            const seenIds = new Set(
              abbrResults.map((r) => r.id).filter(Boolean),
            );
            const dedupedNameResults = nameResults.filter(
              (r) => !seenIds.has(r.id),
            );
            const merged = [...abbrResults, ...dedupedNameResults];

            reduxDispatch(setFilteredCodes(merged));
          },
        );
      }
    }, [
      debouncedSearchTerm,
      dataSource,
      initialSearchDone,
      selectedCategory,
      filterCodes,
      filterByAbbreviations,
      reduxDispatch,
      icd10amTabularData13,
      achiTabularData13,
    ]);
useEffect(() => {
  const handlePopState = (event) => {
    const t0 = performance.now();
    if (window.__suppressPop) {
      return;
    }
    if (event.state) {
      let stateToRestore = { ...event.state, selectedIndex: null };
      
      // If this is a keyboard back navigation, force showFinalScreen to false
      if (window.__keyboardBack || isBackProcessing) {
        stateToRestore.showFinalScreen = false;
        stateToRestore.arrivedViaBackToFinal = false;
      }
      
      // Ensure filteredCodes is preserved from the history state
      if (!stateToRestore.filteredCodes || stateToRestore.filteredCodes.length === 0) {
        // Try to get filteredCodes from the previous state or use a fallback
        const previousState = window.history.state?._previousState;
        if (previousState?.filteredCodes) {
          stateToRestore.filteredCodes = previousState.filteredCodes;
        }
      }
      
      reduxDispatch(updateLocalState(stateToRestore));
    }
    const t1 = performance.now();
    perfLogRef.current.lastPop = t0;
  };
  window.addEventListener("popstate", handlePopState);
  return () => window.removeEventListener("popstate", handlePopState);
}, [reduxDispatch, isBackProcessing]);
    useEffect(() => {
      if (isKeyboardNavigatingRef.current) return;
      setHighlightedIndex(-1);
    }, [initialSearchDone, filteredCodes]);

const pushHistoryState = (newState) => {
  const prevDepth = window.history.state?._historyDepth || 0;
  const stateToPush = {
    ...newState,
    _historyDepth: prevDepth + 1,
    searchTerm: newState.searchTerm,
    selectedIndex: newState.selectedIndex,
    // CRITICAL FIX: Ensure filteredCodes is always preserved in history
    filteredCodes: newState.filteredCodes && newState.filteredCodes.length > 0 
      ? newState.filteredCodes 
      : (window.history.state?.filteredCodes || []),
    selectedCategory: newState.selectedCategory,
    currentCodeSearchData: newState.currentCodeSearchData,
    showFinalScreen: newState.showFinalScreen || false,
    selectedFinalCode: newState.selectedFinalCode,
    initialSearchDone: newState.initialSearchDone,
    selectedCodes: newState.selectedCodes,
    selectedParent: newState.selectedParent,
    topLevelParent: newState.topLevelParent,
    lastAddedCode: newState.lastAddedCode,
    originalCode: newState.originalCode,
    currentPath: newState.currentPath,
    pendingInstructions: newState.pendingInstructions || [],
    currentInstructionIndex: newState.currentInstructionIndex || 0,
    currentWarningMessage: newState.currentWarningMessage || null,
    specialCodesToProcess: newState.specialCodesToProcess,
    currentSpecialCode: newState.currentSpecialCode,
    nextRangeToProcess: newState.nextRangeToProcess || null,
  };
  window.history.pushState(stateToPush, "", window.location.pathname);
  const lastNode = newState.currentPath?.[newState.currentPath.length - 1];
  if (lastNode?.timestamp) {
    reduxDispatch(savePathwaySnapshot({ timestamp: lastNode.timestamp, snapshot: stateToPush }));
  }
};

    const parseSeeTerm = (term) => {
      if (!term || typeof term !== "string" || term.trim() === "") {
        return [];
      }

      const cleanedTerm = term
        .replace(/^[—–\-→]+/g, "") // remove any leading dash, arrow, etc.
        .replace(/[\uFFFD]?\s*(?:see\s+also|see)\s*/gi, "")
        .replace(/^\(|\)$/g, "")
        .replace(/\s*[/→]+\s*/g, "/") // normalize all separators (comma, arrow, dash) to '/'
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase()
        .split("/")
        .map((part) => part.trim())
        .filter((part) => part.length > 0);

      return cleanedTerm;
    };

    const resolveSeePath = (pathParts, category) => {
      if (!pathParts.length) {
        return null;
      }

      const indexData =
        category === "Procedure Codes" ? achiIndexData13 : icd10amIndexData13;
      if (!indexData || !Array.isArray(indexData)) {
        return null;
      }

      const normalize = normalizeSearchText;

      let currentLevel = indexData;
      let foundItem = null;

      for (let i = 0; i < pathParts.length; i++) {
        const targetPart = normalize(pathParts[i]);

        foundItem = currentLevel.find(
          (item) =>
            normalize(item.name) === targetPart ||
            normalize(item.name).startsWith(targetPart),
        );

        if (!foundItem) {
          return null;
        }

        if (i === pathParts.length - 1) {
          const itemChildren = foundItem.children || foundItem.subcodes || [];
          let results = [];

          if (itemChildren.length > 0) {
            results = itemChildren.map((child) => ({
              rawName: child.name,
              rawDescription:
                child.description ||
                (child.description === null ? null : child.name),
              name: child.name,
              description:
                child.description ||
                (child.description === null ? null : child.name),
              children: child.subcodes || child.children || [],
              isFromTabular: false,
              Next_parent_code: child.Next_parent_code,
              Warning_message: child.Warning_message,
              see: child.see,
              seealso: child.seealso,
              isDescriptionNull: child.description === null,
              acs: child.acs,
              nca: child.nca,
              code_first_instruction: child.code_first_instruction,
              use_additional_instruction: child.use_additional_instruction,
              codealsowhenperf_instruction: child.codealsowhenperf_instruction,
              codeto_instruction: child.codeto_instruction,
              manual_added: child.manual_added,
              id: child.id,
              seeparentId: foundItem.id,
              seeparentName: foundItem.name,
            }));
            const parentHasCode =
              foundItem.id &&
              foundItem.name?.trim() &&
              foundItem.description?.trim() &&
              foundItem.description !== null;

            if (parentHasCode) {
              // if the parent's own code is a hyphen code, expand it
              // into its tabular children instead of pushing a flat leaf.
              if (isHyphenCode(foundItem.description)) {
                const extracted = extractCodesFromHyphen(foundItem.description);
                let tabularHyphenChildren = [];

                if (extracted.type === "range") {
                  tabularHyphenChildren = filterTabularCodesWithHyphen(
                    extracted.start,
                    category,
                  ).filter((item) => item.rawName?.endsWith(extracted.end));
                } else {
                  const primaryCode = extracted.codes[0];
                  tabularHyphenChildren = filterTabularCodesWithHyphen(
                    primaryCode,
                    category,
                  );
                }

                const filteredTabularHyphenChildren =
                  tabularHyphenChildren.filter(
                    (item) =>
                      item.rawName?.trim() && item.rawDescription?.trim(),
                  );

                // Merge in, deduping by name against what's already there
                const seenNames = new Set(results.map((r) => r.name));
                filteredTabularHyphenChildren.forEach((item) => {
                  if (!seenNames.has(item.name)) {
                    seenNames.add(item.name);
                    results.push(item);
                  }
                });
              } else {
                // Original behavior — plain Unspecified leaf
                results.push({
                  rawName: foundItem.name,
                  rawDescription: foundItem.description,
                  name: foundItem.description || foundItem.name,
                  description: foundItem.description,
                  children: [],
                  isFromTabular: false,
                  isUnspecified: true,
                  Next_parent_code: foundItem.Next_parent_code || null,
                  Warning_message: foundItem.Warning_message || null,
                  see: foundItem.see || null,
                  seealso: foundItem.seealso || null,
                  isDescriptionNull: false,
                  acs: foundItem.acs,
                  nca: foundItem.nca,
                  code_first_instruction: foundItem.code_first_instruction,
                  use_additional_instruction:
                    foundItem.use_additional_instruction,
                  codealsowhenperf_instruction:
                    foundItem.codealsowhenperf_instruction,
                  id: foundItem.id,
                  codeto_instruction: foundItem.codeto_instruction,
                  manual_added: foundItem.manual_added,
                });
              }
            }
          }

          return results.length > 0 ? results : null;
        } else {
          currentLevel = foundItem.children || foundItem.subcodes || [];
          if (!currentLevel || currentLevel.length === 0) {
            return null;
          }
        }
      }

      return null;
    };
    // Resolves a see-term path to the item itself (not its children).
    // Used so manually_added instructions can present the parent as one
    // selectable option instead of jumping straight into its children.
    const resolveSeeTermToItem = useCallback(
      (pathParts, category) => {
        if (!pathParts.length) return null;

        const indexData =
          category === "Procedure Codes" ? achiIndexData13 : icd10amIndexData13;
        if (!indexData || !Array.isArray(indexData)) return null;

        const normalize = normalizeSearchText;
        let currentLevel = indexData;
        let foundItem = null;

        for (let i = 0; i < pathParts.length; i++) {
          const targetPart = normalize(pathParts[i]);
          foundItem = currentLevel.find(
            (item) =>
              normalize(item.name) === targetPart ||
              normalize(item.name).startsWith(targetPart),
          );
          if (!foundItem) return null;

          if (i === pathParts.length - 1) {
            return {
              rawName: foundItem.name,
              rawDescription: foundItem.description,
              name: foundItem.name,
              description: foundItem.description,
              children: foundItem.subcodes || foundItem.children || [],
              isFromTabular: false,
              Next_parent_code: foundItem.Next_parent_code,
              Warning_message: foundItem.Warning_message,
              see: foundItem.see,
              seealso: foundItem.seealso,
              isDescriptionNull: foundItem.description === null,
              acs: foundItem.acs,
              nca: foundItem.nca,
              code_first_instruction: foundItem.code_first_instruction,
              use_additional_instruction: foundItem.use_additional_instruction,
              codealsowhenperf_instruction:
                foundItem.codealsowhenperf_instruction,
              codeto_instruction: foundItem.codeto_instruction,
              manual_added: foundItem.manual_added,
              id: foundItem.id,
            };
          } else {
            currentLevel = foundItem.children || foundItem.subcodes || [];
            if (!currentLevel || currentLevel.length === 0) return null;
          }
        }
        return null;
      },
      [achiIndexData13, icd10amIndexData13],
    );
    // Resolves a manually_added term into selectable "Yes (term)" options.
const resolveManualAddedTerm = (term, category) => {
  const tag = (item, extra = {}) => ({
    ...item,
    ...extra,
    _displayLabelOverride: `Yes (${term})`,
    isManualAddedOption: true,
    _originalTerm: term,
  });
  const tagList = (list) => (Array.isArray(list) ? list.map((i) => tag(i)) : []);

  const pathParts = parseSeeTerm(term);
  const resolvedItem = resolveSeeTermToItem(pathParts, category);

  // Term couldn't be resolved as an item at all
  if (!resolvedItem) return tagList(resolveSeePath(pathParts, category));

  // Item has its own children → present it as one option
  if (resolvedItem.children?.length > 0) return [tag(resolvedItem)];

  // No children: follow "see", else "seealso" (they were identical branches)
  const ref = resolvedItem.see?.trim()
    ? resolvedItem.see
    : resolvedItem.seealso?.trim()
      ? resolvedItem.seealso
      : null;

  if (ref) {
    const refParts = parseSeeTerm(ref);
    const refItem = resolveSeeTermToItem(refParts, category);
    if (refItem?.children?.length > 0) {
      return [
        tag(refItem, {
          rawName: resolvedItem.rawName,
          rawDescription: resolvedItem.rawDescription,
          originalResolvedItem: resolvedItem,
        }),
      ];
    }
    return tagList(resolveSeePath(refParts, category));
  }

  // Plain leaf
  return [tag(resolvedItem)];
};
const resolveInstructionExtensions = async (instr, category) => {
  const code = instr.Next_Parent_code?.toString().trim();
  if (!code) return [];
  if (instr._instructionType === "manually_added") {
    return resolveManualAddedTerm(code, category);
  }
  const result = await filterTabularCodes(
    code,
    category,
    instr._instructionType === "codeto" ? instr.Character : null,
  );
  return Array.isArray(result) ? result : [];
};
    const isCodeInInjuryTracker = (code) => {
      // if(code.children && code.children.length>0){
      //   return
      // }
      if (!injuryCodeFlowTracker || !Array.isArray(injuryCodeFlowTracker))
        return false;

      const isTracked = injuryCodeFlowTracker.some((trackedCode) => {
        const idMatch = trackedCode.id === code.id;
        const nameMatch = trackedCode.rawName === code.rawName;
        const descMatch = trackedCode.rawDescription === code.rawDescription;
        const tabularMatch = trackedCode.rawDescription === code.rawName;

        if (idMatch || (nameMatch && descMatch) || tabularMatch) {
          return true;
        }
        return false;
      });

      return isTracked;
    };
    const findTabularChildrenForMultipleRanges = (
      items,
      rangeCodes,
      category,
      results = [],
    ) => {
      if (!rangeCodes || !Array.isArray(rangeCodes)) return results;

      const normalizeCode = (code) =>
        code?.replace(/\./g, "").toUpperCase().trim();

      // Process each range code
      rangeCodes.forEach((rangeCode) => {
        const isRange = rangeCode.includes("-");
        let rangeStart, rangeEnd;

        if (isRange) {
          const parts = rangeCode.split("-").map((part) => normalizeCode(part));
          [rangeStart, rangeEnd] = parts;
        } else {
          // Single code
          rangeStart = normalizeCode(rangeCode);
          rangeEnd = null;
        }

        const findRangeCodes = (items) => {
          for (const item of items) {
            // Handle chapters
            if (item.chapter && item.codes) {
              findRangeCodes(item.codes);
              continue;
            }

            const itemCode = item.name;
            if (!itemCode) continue;

            const itemCodeNormalized = normalizeCode(itemCode);

            // Check if this item exactly matches our range or single code
            const matchesRange = isRange
              ? rangeEnd
                ? compareCodes(itemCodeNormalized, rangeStart, rangeEnd)
                : itemCodeNormalized.startsWith(rangeStart)
              : itemCodeNormalized === rangeStart;

            if (matchesRange) {
              // Add the range code itself
              results.push({
                rawName: item.name, // This will be the range code like "V00-X59"
                rawDescription: item.description || item.name,
                name: item.name,
                description: item.description || item.name,
                children: item.subcodes || item.children || [],
                isFromTabular: true,
                id: item.id,
                acs: item.acs,
                nca: item.nca,
                code_first_instruction: item.code_first_instruction,
                use_additional_instruction: item.use_additional_instruction,
                codealsowhenperf_instruction: item.codealsowhenperf_instruction,
                codeto_instruction: item.codeto_instruction,
                manual_added: item.manual_added,
                _isRangeCode: true,
                _originalRange: rangeCode,
              });

              // Don't search deeper - we found our exact range
              continue;
            }

            // Continue searching in nested structures
            [item.codes, item.subcodes, item.children]
              .filter(Boolean)
              .forEach((nested) => findRangeCodes(nested));
          }
        };

        findRangeCodes(items);
      });

      // Remove duplicates based on rawName
      const uniqueResults = results.filter(
        (result, index, self) =>
          index === self.findIndex((r) => r.rawName === result.rawName),
      );

      return uniqueResults;
    };
    // Helper function to check if a code is already in injuryCodeFlowTracker

    const filterTabularCodesForMultipleRanges = (
      rangeCodesString,
      category,
    ) => {
      if (category === "Procedure Codes") return [];

      // Split the range string by commas and trim each part
      const rangeCodes = rangeCodesString.split(",").map((code) => code.trim());

      const tabularData =
        category === "Procedure Codes"
          ? achiTabularData13
          : icd10amTabularData13;

      const allResults = findTabularChildrenForMultipleRanges(
        tabularData || [],
        rangeCodes,
        category,
      );

      // Filter out codes that are already in injuryCodeFlowTracker
      const filteredResults = allResults.filter((code) => code);
      return filteredResults;
    };
    // remove chnapter logic Table of drugs and chemicals
    const removeChapter19AndStore = (list) => {
      return list.map((item) => {
        const ch19 = item.children?.find((c) =>
          c.name?.toLowerCase().includes("chapter 19"),
        );

        return {
          ...item,
          children: item.children
            ? item.children.filter(
                (c) => !c.name?.toLowerCase().includes("chapter 19"),
              )
            : [],
          chapter19Info: ch19
            ? {
                rawName: ch19.rawName || ch19.name,
                rawDescription: ch19.description || ch19.name,
                id: ch19.id,
              }
            : null,
        };
      });
    };
    //  Morpology  spliting logic final screen

    const isCancerRange = (icdCode) => {
      if (!icdCode) return false;

      const clean = icdCode.toUpperCase().trim();
      const letter = clean[0];
      const num = parseInt(clean.slice(1)); // category-based

      if (letter === "C") {
        return (
          (num >= 0 && num <= 76) ||
          (num >= 77 && num <= 79) ||
          (num >= 80 && num <= 96)
        );
      }

      if (letter === "D") {
        return (
          (num >= 0 && num <= 9) ||
          (num >= 10 && num <= 36) ||
          (num >= 37 && num <= 48)
        );
      }

      return false;
    };
    const splitMorphologyIfNeeded = (codeObj) => {
      // Apply ONLY for index (NOT tabular)
      if (codeObj.isFromTabular) return [codeObj];

      const raw = (codeObj.rawDescription || "").trim();
      const match = raw.match(/^(\(?M\d+\/\d+\)?)\s+([A-Z]\d+[\d.]*)$/i);
      if (!match) return [codeObj];

      const morphCode = match[1]; // M8000/3
      const icdCode = match[2]; // C80.9

      const name = codeObj.rawName || ""; //  SAME description
      // const isCancer = isCancerRange(icdCode);
      return [
        { ...codeObj, rawName: name, rawDescription: icdCode }, // ICD first
        { ...codeObj, rawName: name, rawDescription: morphCode }, // Morph next
      ];
    };
    // Extracts character values based on category (tab)
    // ICD (Principal/Additional Diagnoses) → extracts .digit values, e.g. ".2" or ".0,.1,.2,.3,.4"
    // ACHI (Procedure Codes) → extracts extension digits, e.g. "-08 Ending with 08" → "08"
    const parseCharacterInstruction = (characterStr, category) => {
      if (!characterStr) return null;
      const str = characterStr.toString().trim();
      const isProcedureCategory = category === "Procedure Codes";

      if (isProcedureCategory) {
        // ACHI — extract number sequences (handles "-08 Ending with 08" → ["08"])
        const matches = str.match(/\d+/g);
        if (matches && matches.length > 0) {
          // dedupe values
          const values = Array.from(new Set(matches));
          return { type: "achi_extension", values };
        }
        return null;
      }

      // ICD — extract decimal (fourth character) values, e.g. ".2" or ".0,.1,.2,.3,.4"
      const dotMatches = str.match(/\.\d+/g);
      if (dotMatches && dotMatches.length > 0) {
        const values = dotMatches.map((d) => d.replace(".", ""));
        return { type: "icd_character", values };
      }

      return null;
    };

    const applyCharacterFilter = (extensions, characterStr, category) => {
      const parsed = parseCharacterInstruction(characterStr, category);
      if (!parsed || !Array.isArray(extensions)) return extensions;

      if (parsed.type === "icd_character") {
        // Match F10.2, F11.2 etc → checks the digit right after the decimal point
        return extensions.filter((ext) => {
          const name = (ext.rawName || ext.name || "").trim();
          const match = name.match(/\.(\d)/); // e.g. F10.2 → "2"
          if (!match) return false;
          return parsed.values.includes(match[1]);
        });
      }

      if (parsed.type === "achi_extension") {
        // Match codes ending with the extension digits, e.g. ...08
        return extensions.filter((ext) => {
          const name = (ext.rawName || ext.name || "").trim();
          const codeOnly = name.replace(/\s*\[\d+\]\s*$/, "");
          return parsed.values.some((v) => codeOnly.endsWith(v));
        });
      }

      return extensions;
    };
    // check for achi codes for duplication
    const isCodeInAchiList = (code) => {
      const value = code.isFromTabular ? code.rawName : code.rawDescription;
      const clean = (v) =>
        (v || "")
          .replace(/\[.*\]/, "")
          .trim()
          .toLowerCase();
      return achiCodesData.achiCodes.some((c) => clean(c) === clean(value));
    };
    const handleContinue = useCallback(() => {
      forwardStackRef.current = [];
      let newState = {
        searchTerm,
        selectedIndex,
        filteredCodes,
        currentCodeSearchData,
        showFinalScreen,
        selectedFinalCode,
        selectedCategory,
        initialSearchDone,
        selectedCodes: { ...localSelectedCodes },
        selectedParent,
        topLevelParent,
        lastAddedCode,
        showError: false,
        originalCode,
        currentPath,
        pendingInstructions: localState.pendingInstructions || [],
        currentInstructionIndex: localState.currentInstructionIndex || 0,
        currentWarningMessage: localState.currentWarningMessage || null,
        specialCodesToProcess: [...localState.specialCodesToProcess],
        currentSpecialCode: localState.currentSpecialCode,
        nextRangeToProcess: localState.nextRangeToProcess || null,
        chapter19Info: localState.chapter19Info || null,
        pendingDirectCode: localState.pendingDirectCode || null,
        allHyphenCodesWithPendingMorphology:
          localState.allHyphenCodesWithPendingMorphology || [],
        pendingMorphologyFromHyphen:
          localState.pendingMorphologyFromHyphen || null,
        pendingManifestationAfterInstructions:
          localState.pendingManifestationAfterInstructions || null,
        manifestationWarningMessage:
          localState.manifestationWarningMessage || null,
        processingSpecialCodes: localState.processingSpecialCodes || false,
      };
      if (selectedIndex !== null && filteredCodes?.[selectedIndex]) {
        let selectedCode = filteredCodes[selectedIndex];
        // console.log(
        //   "Selected code:",
        //   newState,
        //   selectedCode,
        //   localState,
        //   // icd10amTabularData13,
        //   icd10amIndexData13,
        // );
        const isSelectedCodeInHyphenCodes =
          newState.allHyphenCodesWithPendingMorphology?.some(
            (code) => code.id === selectedCode.id,
          );
        //Manifestation - Yes or No
        if (
          selectedCode._isManifestationYes ||
          selectedCode._isManifestationNo
        ) {
          if (selectedCode._isManifestationYes) {
            // User wants to code
            //  - go to search
            reduxDispatch(clearManifestationState());
            const searchState = {
              ...newState,
              searchTerm: "",
              initialSearchDone: false,
              showFinalScreen: false,
              selectedIndex: null,
              filteredCodes: [],
              currentCodeSearchData: null,
              currentWarningMessage: null,
              pendingManifestationAfterInstructions: false,
              manifestationWarningMessage: null,
              selectedParent: null,
              topLevelParent: null,
              lastAddedCode: null,
              originalCode: null,
              currentPath: [],
              pendingInstructions: [],
              currentInstructionIndex: 0,
              selectedCodes: { ...localSelectedCodes }, // Preserve existing codes
            };

            reduxDispatch(updateLocalState(searchState));
            pushHistoryState(searchState);

            setTimeout(() => {
  if (!suppressAutoFocusRef.current) {
    focusSearchInput();
  }
  reduxDispatch(setRawSearchQuery(""));
  reduxDispatch(setSearchByCodeId(null));
}, 100);
            return;
          } else if (selectedCode._isManifestationNo) {
            // User clicked "No" - go directly to final screen
            reduxDispatch(clearManifestationState());
            const finalState = {
              ...newState,
              showFinalScreen: true,
              currentWarningMessage: null,
              // selectedCodes remains as is - no manifestation option added
              selectedCodes: { ...localSelectedCodes },
              // Clear manifestation flags
              pendingManifestationAfterInstructions: false,
              manifestationWarningMessage: null,
              // Remove manifestation options from filtered codes
              filteredCodes: [],
              currentCodeSearchData: null,
              initialSearchDone: false,
            };
            setTimeout(() => {
              reduxDispatch(setRawSearchQuery(""));
              reduxDispatch(setSearchByCodeId(null));
            }, 0);
            reduxDispatch(updateLocalState(finalState));
            pushHistoryState(finalState);
            return;
          }
        }
        const addMorphologyCodeToState = (state, category, morphologyCode) => {
          if (!morphologyCode) return false;

          const existingArr = state.selectedCodes[category] || [];
          const alreadyExists = existingArr.some(
            (code) =>
              code.rawName === morphologyCode ||
              code.rawDescription === morphologyCode,
          );
          if (!alreadyExists) {
            const morphologyCodeObj = {
              rawName: morphologyCode,
              rawDescription: morphologyCode,
              name: morphologyCode,
              description: morphologyCode,
              isFromTabular: false,
              isMorphologyCode: true,
              acs: null,
              nca: null,
              code_first_instruction: null,
              use_additional_instruction: null,
              codealsowhenperf_instruction: null,
              codeto_instruction: null,
              manual_added: null,
              id: `morph-${Date.now()}`,
            };
            state.selectedCodes = {
              ...state.selectedCodes,
              [category]: [...existingArr, morphologyCodeObj],
            };
          }
          return true;
        };
        //Reusable function to finalize or show manifestation options
        const finalizeOrManifestation = (stateToFinalize) => {
          const hasManifestation =
            localState.pendingManifestationAfterInstructions ||
            stateToFinalize.pendingManifestationAfterInstructions;

          if (hasManifestation) {
            const warningMessage =
              localState.manifestationWarningMessage ||
              stateToFinalize.manifestationWarningMessage ||
              "Code the manifestation?";

            // NEW: was this triggered by a codeto instruction?
            const isFromCodeTo =
              localState.manifestationIsFromCodeTo ||
              stateToFinalize.manifestationIsFromCodeTo ||
              false;

            const yesOption = {
              id: "manifestation-yes-option",
              rawName: isFromCodeTo ? "Yes" :"Yes",
              rawDescription: "Search for manifestation codes",
              name: "Yes",
              description: "Search for manifestation codes",
              children: [],
              isFromTabular: false,
              isFromManifestation: true,
              _isCustomOption: true,
              _isManifestationYes: true,
              _warningMessage: warningMessage,
            };

            const noOption = {
              id: "manifestation-no-option",
              rawName: "No",
              rawDescription: "Skip manifestation codes and finalize",
              name: "No",
              description: "Skip manifestation codes",
              children: [],
              isFromTabular: false,
              isFromManifestation: true,
              _isCustomOption: true,
              _isManifestationNo: true,
            };
            // NEW: codeto -> only Yes option; everything else -> Yes/No as before
            const manifestationOptions = isFromCodeTo
              ? [yesOption]
              : [yesOption, noOption];
              
            const manifestationState = {
              ...stateToFinalize,
              showFinalScreen: false,
              initialSearchDone: true,
              selectedIndex: null,
              currentCodeSearchData: manifestationOptions,
              filteredCodes: manifestationOptions,
              currentWarningMessage: warningMessage,
              selectedCodes: { ...stateToFinalize.selectedCodes },
            };

            reduxDispatch(updateLocalState(manifestationState));
            pushHistoryState(manifestationState);
            return true;
          }

          // No manifestation - show final screen
          const finalState = { ...stateToFinalize, showFinalScreen: true };
          reduxDispatch(updateLocalState(finalState));
          pushHistoryState(finalState);
          return false;
        };
        const extractSpecialCodesFromDescription = (value) => {
          if (!value) return [];
          return Array.from(
            new Set(
              value
                .replace(/[^\x20-\x7E\u2020]/g, " ")
                .replace(/[^\w.\-*†, ]/g, " ")
                .split(/[\s,]+/)
                .map((p) => p.trim())
                .filter(Boolean)
                .filter((part) => /^[A-Z]+\d+(\.\d*)?[-*†]+$/i.test(part))
                .flatMap((part) => {
                  const base = part.replace(/[-*†]+$/, "");
                  const specialsMatch = part.match(/[-*†]+$/);
                  const specials = specialsMatch ? specialsMatch[0] : "";
                  const out = new Set();
                  if (specials.includes("-")) out.add(base + "-");
                  if (specials.includes("*")) out.add(base + "*");
                  if (specials.includes("†")) out.add(base + "†");
                  return Array.from(out);
                }),
            ),
          );
        };
        const processSpecialCodesForSelection = (targetCode, baseState) => {
          if (
            !targetCode ||
            !targetCode.description ||
            targetCode.isFromTabular ||
            (targetCode.children && targetCode.children.length > 0)
          ) {
            return false;
          }

          let workingState = { ...baseState };

          const morphologyMatch = targetCode.description.match(
            /^\(?(M\d+\/\d+)\)?\s+([A-Z]\d+[\d.\-*†]*)/i,
          );
          const morphologyCode = morphologyMatch ? morphologyMatch[1] : null;

          const safeSelectedCodes =
            typeof localSelectedCodes === "object" &&
            !Array.isArray(localSelectedCodes)
              ? localSelectedCodes
              : {};

          if (morphologyCode) {
            const existingArr = Array.isArray(
              safeSelectedCodes[selectedCategory],
            )
              ? safeSelectedCodes[selectedCategory]
              : [];
            const alreadyExists = existingArr.some(
              (item) => item?.rawName === morphologyCode,
            );
            if (!alreadyExists) {
              workingState.selectedCodes = {
                ...safeSelectedCodes,
                [selectedCategory]: [
                  ...existingArr,
                  {
                    rawName: morphologyCode,
                    rawDescription: `(${morphologyCode})`,
                    name: morphologyCode,
                    description: `(${morphologyCode})`,
                    isFromTabular: false,
                    isMorphologyCode: true,
                    acs: targetCode.acs || null,
                    nca: targetCode.nca || null,
                    code_first_instruction: null,
                    use_additional_instruction: null,
                    codealsowhenperf_instruction: null,
                    codeto_instruction: null,
                    manual_added: null,
                    id: `morph-${Date.now()}`,
                  },
                ],
              };
            }
          }

          const newParts = extractSpecialCodesFromDescription(
            targetCode.description,
          );
          if (newParts.length === 0) return false;

          const orderedNewParts = prioritizeParts(newParts);
          const trimNewCodes = Array.from(
            new Set(
              orderedNewParts.map((code) => code?.replace(/[-†*]+$/, "")),
            ),
          );

          workingState.originalSpecialCodesWithSymbols = [
            ...(localState.originalSpecialCodesWithSymbols || []),
            ...orderedNewParts,
          ].filter((value, index, self) => self.indexOf(value) === index);

          workingState = {
            ...workingState,
            lastAddedCode: targetCode,
            specialCodesToProcess: trimNewCodes,
            processingSpecialCodes: true,
          };

          const currentSpecialCode = trimNewCodes[0];
          const remainingSpecialCodes = trimNewCodes.slice(1);
          const extensions = getTabularExtensions(
            currentSpecialCode,
            selectedCategory,
            true,
          );

          if (extensions.length > 0) {
            const baseCode = (targetCode.children || []).map((item) => ({
              ...item,
              rawDescription: item.description,
              rawName: item.name,
            }));
            workingState = {
              ...workingState,
              specialCodesToProcess: remainingSpecialCodes,
              currentCodeSearchData: [...baseCode, ...extensions],
              filteredCodes: [...baseCode, ...extensions],
              selectedIndex: null,
              initialSearchDone: true,
              showFinalScreen: false,
              currentSpecialCode,
              selectedParent: targetCode,
            };
          } else {
            workingState = {
              ...workingState,
              specialCodesToProcess: remainingSpecialCodes,
              currentCodeSearchData: null,
              filteredCodes: [],
              selectedIndex: null,
              initialSearchDone: false,
              showFinalScreen: false,
              selectedParent: targetCode,
            };
          }

          reduxDispatch(updateLocalState(workingState));
          pushHistoryState(workingState);
          return true;
        };
        if (
          isSelectedCodeInHyphenCodes &&
          newState.allHyphenCodesWithPendingMorphology.length > 0 &&
          newState.pendingMorphologyFromHyphen !== null
        ) {
          // After adding the main code
          addMorphologyCodeToState(
            newState,
            selectedCategory,
            newState.pendingMorphologyFromHyphen,
          );
          newState.pendingMorphologyFromHyphen = null;
        }
        const hasInstructions =
          (Array.isArray(selectedCode.code_first_instruction) &&
            selectedCode.code_first_instruction.length > 0) ||
          (Array.isArray(selectedCode.use_additional_instruction) &&
            selectedCode.use_additional_instruction.length > 0) ||
          (Array.isArray(selectedCode.codeto_instruction) &&
            selectedCode.codeto_instruction.length > 0) ||
          (Array.isArray(selectedCode.manual_added) &&
            selectedCode.manual_added.length > 0) ||
          (Array.isArray(selectedCode.codealsowhenperf_instruction) &&
            selectedCode.codealsowhenperf_instruction.length > 0 &&
            !selectedCode.description !== null &&
            !selectedCode.children.length > 0);
        // if (selectedCode?.chapter19Info) {
        //   newState = {
        //     ...newState,
        //     chapter19Info: selectedCode.chapter19Info,
        //   };
        //   setTimeout(() => {
        //     reduxDispatch(updateLocalState(newState));
        //   }, 0);
        //   pushHistoryState(newState);
        // }
        // Table of drug and chemicals
        // if (selectedCode?.chapter19Info) {
        //   newState = {
        //     ...newState,
        //     chapter19Info: selectedCode.chapter19Info,
        //     selectedCodes: {
        //       ...newState.selectedCodes,
        //       [selectedCategory]: [
        //         ...(newState.selectedCodes[selectedCategory] || []),
        //         {
        //           rawName: selectedCode.chapter19Info.rawName,
        //           rawDescription: selectedCode.chapter19Info.rawDescription,
        //           id: selectedCode.chapter19Info.id,
        //           isFromTabular: false,
        //         },
        //       ],
        //     },
        //   };
        // }
        const isTableDrugsCase =
          selectedCode.id === "348284" ||
          (selectedCode.parentId === "348284" &&
            selectedCode._type === "child") ||
          selectedCode.seeparentId === "348284";
        //  (selectedCode.parentId === "223743" && selectedCode._type === "child" && selectedCode.id === "223754") ;

        const isInjuryWithActivity =
          rangeChildren.injuryWithActivity?.some(
            (code) =>
              code.id === selectedCode.id ||
              code.name === selectedCode.rawDescription ||
              code.rawName === selectedCode.rawName,
          ) || isTableDrugsCase; // FORCE activity for Table of drugs
        const isAllInjuries = rangeChildren.injuries?.some(
          (code) =>
            code.id === selectedCode.id ||
            code.name === selectedCode.rawDescription ||
            code.rawName === selectedCode.rawName,
        );
        const isAllBurns = rangeChildren.burns?.some(
          (code) =>
            code.id === selectedCode.id ||
            code.name === selectedCode.rawDescription ||
            code.rawName === selectedCode.rawName,
        );
        const isAllFrostbites = rangeChildren.frostbite?.some(
          (code) =>
            code.id === selectedCode.id ||
            code.name === selectedCode.rawDescription ||
            code.rawName === selectedCode.rawName,
        );
        const isAllPoisoning = rangeChildren.poisoning?.some(
          (code) =>
            code.id === selectedCode.id ||
            code.name === selectedCode.rawDescription ||
            code.rawName === selectedCode.rawName,
        );
        const isAllEffectsOfExtCause =
          rangeChildren.effectsOfExternalCauses?.some(
            (code) =>
              code.id === selectedCode.id ||
              code.name === selectedCode.rawDescription ||
              code.rawName === selectedCode.rawName,
          );
        const isAllEarlyComplicationsOfTrauma =
          rangeChildren.earlyComplicationsOfTrauma?.some(
            (code) =>
              code.id === selectedCode.id ||
              code.name === selectedCode.rawDescription ||
              code.rawName === selectedCode.rawName,
          );
        const isAllCompOfSrgAndMedcare =
          rangeChildren.complicationOfSurgicalAndMedicalCare?.some(
            (code) =>
              code.id === selectedCode.id ||
              code.name === selectedCode.rawDescription ||
              code.rawName === selectedCode.rawName,
          );
        const isAllOtherComplicationsOfTrauma =
          rangeChildren.otherComplicationOfTrauma?.some(
            (code) =>
              code.id === selectedCode.id ||
              code.name === selectedCode.rawDescription ||
              code.rawName === selectedCode.rawName,
          );
        const isAllsequelaeOfInjuries = rangeChildren.sequelaeOfInjuries?.some(
          (code) =>
            code.id === selectedCode.id ||
            code.name === selectedCode.rawDescription ||
            code.rawName === selectedCode.rawName,
        );

        const isInjuryWithoutActivity =
          rangeChildren.injuryWithoutActivity?.some(
            (code) =>
              code.id === selectedCode.id ||
              code.name === selectedCode.rawDescription ||
              code.rawName === selectedCode.rawName,
          );
        if (
          isInjuryWithActivity ||
          isInjuryWithoutActivity ||
          (isTableDrugsCase && !selectedCode.isDirectCode)
        ) {
          // console.log("🧠 Injury code detected:", {
          //   isInjuryWithActivity,
          //   isInjuryWithoutActivity,
          //   isTableDrugsCase,
          //   currentInitialInjuryState: initialInjuryState,
          //   nextRangeToProcess: localState.nextRangeToProcess,
          // });
          if (!initialInjuryState) {
            setTimeout(() => {
              reduxDispatch(setInitialInjuryState(true));
            }, 0);
            // newState.showFinalScreen = false;
          }

          const newTracker = [...(localState.injuryFlowTracker || [])];

          // Add to tracker if it's an injury code
          if (isInjuryWithActivity || isTableDrugsCase) {
            newTracker.push("withActivity");
            newTracker.push("withInjuries");
            if (isAllBurns) {
              newTracker.push("withBurns");
            }
          } else if (isInjuryWithoutActivity) {
            newTracker.push("withoutActivity");
            if (isAllEffectsOfExtCause) {
              newTracker.push("witheffectsOfExternalCause");
            }
            if (isAllEarlyComplicationsOfTrauma) {
              newTracker.push("withEarlyComplicationsOfTrauma");
            }
            if (isAllCompOfSrgAndMedcare) {
              newTracker.push("withComplicationsOfSurgicalAndMedicalCare");
            }
            if (isAllOtherComplicationsOfTrauma) {
              newTracker.push("withOtherComplicationsOfTrauma");
            }
            if (isAllsequelaeOfInjuries) {
              newTracker.push("withSequelaeOfInjuries");
            }
          } else if (isAllInjuries) {
            newTracker.push("withInjuries");
          } else if (isAllBurns) {
            newTracker.push("withBurns");
          } else if (isAllFrostbites) {
            newTracker.push("withForstBites");
          } else if (isAllPoisoning) {
            newTracker.push("withPoisoning");
          } else if (isAllEffectsOfExtCause) {
            newTracker.push("witheffectsOfExternalCause");
          } else if (isAllEarlyComplicationsOfTrauma) {
            newTracker.push("withEarlyComplicationsOfTrauma");
          } else if (isAllCompOfSrgAndMedcare) {
            newTracker.push("withComplicationsOfSurgicalAndMedicalCare");
          } else if (isAllOtherComplicationsOfTrauma) {
            newTracker.push("withOtherComplicationsOfTrauma");
          } else if (isAllsequelaeOfInjuries) {
            newTracker.push("withSequelaeOfInjuries");
          }
          reduxDispatch(setInjuryFlowTracker(newTracker));
        }

        if (selectedCode._isCustomOption === true) {
          const checkIsFromTabular = localState.lastAddedCode?.isFromTabular;

          if (checkIsFromTabular) {
            setTimeout(() => {
              reduxDispatch(
                setRawSearchQuery(localState.lastAddedCode?.rawDescription),
              );
            }, 0);
            if (selectedCategory === "Procedure Codes") {
              reduxDispatch(setActiveTab("ACHI Tabular"));
            } else {
              reduxDispatch(setActiveTab("ICD 10 AM Tabular"));
            }
            reduxDispatch(setActiveIcon("Tabular"));
          } else {
            if (selectedCategory === "Procedure Codes") {
              setTimeout(() => {
                reduxDispatch(
                  setRawSearchQuery(localState.lastAddedCode?.rawName),
                );
              }, 0);
            } else {
              setTimeout(() => {
                reduxDispatch(
                  setRawSearchQuery(localState.lastAddedCode?.name),
                );
              });
            }
          }
          reduxDispatch(setSearchByCodeId(localState.lastAddedCode?.id));
        }
        // ============ HANDLE MANUALLY Instructions ============

        if (selectedCode.isManualAddedOption === true) {
          const term = selectedCode._originalTerm || selectedCode.rawName;
          const resolvedChildren =
            selectedCode.children || selectedCode.subcodes || [];
          const hyphenResult = processHyphenCode(
            selectedCode,
            selectedCategory,
          );

          if (hyphenResult) {
            newState = {
              ...newState,
              selectedParent: hyphenResult.parent,
              currentCodeSearchData: hyphenResult.filteredCodes,
              filteredCodes: hyphenResult.filteredCodes,
              selectedIndex: null,
              initialSearchDone: true,
              showFinalScreen: false,
              lastAddedCode: selectedCode,
              originalCode: normalizeCodeObject(selectedCode),
            };

            reduxDispatch(updateLocalState(newState));
            reduxDispatch(clearWarningMessage());
            pushHistoryState(newState);
            return;
          }
          // ─────────────────────────────────────────────────────────────
          // CASE A: Resolved item HAS children → do NOT finalize.
          //         Fall through to the normal children-navigation flow,
          //         but strip the manual-added flag so we don't re-enter
          //         this block on subsequent selection.
          // ─────────────────────────────────────────────────────────────
          if (resolvedChildren.length > 0) {
            const childOptions = resolvedChildren
              .filter((child) => child && (child.name || child.rawName))
              .map((child) => ({
                rawName: child.name || child.rawName,
                rawDescription:
                  child.description ??
                  (child.description === null
                    ? null
                    : child.name || child.rawName),
                name: child.name || child.rawName,
                description:
                  child.description ??
                  (child.description === null
                    ? null
                    : child.name || child.rawName),
                children: child.subcodes || child.children || [],
                isFromTabular:
                  child.isFromTabular || selectedCode.isFromTabular || false,
                Next_parent_code:
                  child.Next_parent_code || child.Next_Parent_code || null,
                Warning_message: child.Warning_message,
                see: child.see,
                seealso: child.seealso,
                isDescriptionNull: child.description === null,
                id: child.id,
                acs: child.acs,
                nca: child.nca,
                code_first_instruction: null,
                use_additional_instruction: null,
                codealsowhenperf_instruction: null,
                codeto_instruction: null,
                manual_added: null,
                isManualAddedOption: false,
                _fromManualAddedParent: true,
              }));

           const seeAlsoTerm = extractSeeAlsoTerm(selectedCode.seealso);
const cleanedSelectedCode = { ...selectedCode, isManualAddedOption: false };

let combinedOptions = [...childOptions];

if (seeAlsoTerm) {
  // Same behaviour as the direct flow (CASE 1): the see-also
  // "Unspecified" option replaces the plain unspecified option.
  combinedOptions.push({
    rawName: `Unspecified (${seeAlsoTerm})`,
    rawDescription: `See also: ${seeAlsoTerm}`,
    name: `Unspecified (${seeAlsoTerm})`,
    description: seeAlsoTerm,
    isSeeAlsoOption: true,
    isUnspecifiedSeeAlso: true,
    option: seeAlsoTerm,
    originalCode: cleanedSelectedCode,
    referenceId: selectedCode.id,
    category: selectedCategory,
    acs: selectedCode.acs,
    nca: selectedCode.nca,
  });
} else if (canShowUnspecified(selectedCode) || getOwnCode(selectedCode)) {
  // Original behaviour: plain parent option when the item has its own code
  combinedOptions.push({
    ...selectedCode,
    isManualAddedOption: false,
    isDirectCode: true,
    isUnspecified: true,
    _displayLabelOverride: `${term}`,
    children: [],
  });
}


            const remainingInstructionsAfterThis =
              newState.pendingInstructions || [];

            const childNavigationState = {
              ...newState,
              selectedParent: {
                ...selectedCode,
                isManualAddedOption: false,
              },
              currentCodeSearchData: combinedOptions,
              filteredCodes: combinedOptions,
              selectedIndex: null,
              initialSearchDone: true,
              showFinalScreen: false,
              originalCode: normalizeCodeObject(selectedCode),
              lastAddedCode: selectedCode,
              pendingInstructions: remainingInstructionsAfterThis,
              currentWarningMessage: newState.currentWarningMessage || null,
              pendingInjuryAfterInstructions:
                newState.pendingInjuryAfterInstructions ||
                localState.pendingInjuryAfterInstructions ||
                false,
              pendingInjuryCode:
                newState.pendingInjuryCode ||
                localState.pendingInjuryCode ||
                null,
              nextRangeToProcess:
                newState.nextRangeToProcess ||
                localState.nextRangeToProcess ||
                null,
            };

            reduxDispatch(updateLocalState(childNavigationState));
            pushHistoryState(childNavigationState);
            return;
          }

          // ─────────────────────────────────────────────────────────────
          // CASE B: Resolved item has NO children → safe to finalize.
          //         (This is your existing behavior for leaf codes.)
          // ─────────────────────────────────────────────────────────────
          if (processSpecialCodesForSelection(selectedCode, newState)) {
            return;
          }
          const existingArr = localSelectedCodes[selectedCategory] || [];
          const codeKey = `${selectedCode.rawName}-${selectedCode.rawDescription}`;
          const isAlreadyPresent = existingArr.some(
            (c) => `${c.rawName}-${c.rawDescription}` === codeKey,
          );

          if (!isAlreadyPresent) {
            newState.selectedCodes = {
              ...localSelectedCodes,
              [selectedCategory]: [
                ...existingArr,
                {
                  rawName: selectedCode.rawName,
                  rawDescription: selectedCode.rawDescription,
                  isFromTabular: selectedCode.isFromTabular || false,
                  acs: selectedCode.acs || null,
                  nca: selectedCode.nca || null,
                  code_first_instruction: null,
                  use_additional_instruction: null,
                  codealsowhenperf_instruction: null,
                  codeto_instruction: null,
                  manual_added: null,
                  id: selectedCode.id,
                },
              ],
            };
            newState.lastAddedCode = selectedCode;
          }

          // Drop the current instruction from pending (it's been resolved)
          const remainingInstructions = newState.pendingInstructions || [];

          if (remainingInstructions.length > 0) {
            newState.pendingInstructions = remainingInstructions;
            newState.currentWarningMessage = null;
            newState.showFinalScreen = false;
            newState.initialSearchDone = true;

            reduxDispatch(updateLocalState(newState));
            // pushHistoryState(newState);

            // Process the next instruction
            (async () => {
              const [nextInstruction, ...rest] = remainingInstructions;

              if (nextInstruction._instructionType === "manually_added") {
                const nextTerm =
                  nextInstruction.Next_Parent_code?.toString().trim();
                if (nextTerm) {
                  const extensions = resolveManualAddedTerm(nextTerm, selectedCategory);
                  if (nextInstruction.Warning_message) {
                    const displayWarning =
                      nextInstruction._instructionType === "codealsowhenperf"
                        ? `Code also when performed: ${nextInstruction.Warning_message}`
                        : nextInstruction.Warning_message;
                    reduxDispatch(setCurrentWarningMessage(displayWarning));
                    newState.currentWarningMessage = displayWarning;
                  } else {
                    reduxDispatch(clearWarningMessage());
                    newState.currentWarningMessage = null;
                  }

                  if (extensions.length > 0) {
                    const noOption = createNoOption();
                    const nextState = {
                      ...newState,
                      currentCodeSearchData: extensions,
                      filteredCodes: [noOption, ...extensions],
                      selectedIndex: null,
                      initialSearchDone: true,
                      showFinalScreen: false,
                      pendingInstructions: rest,
                      currentWarningMessage:
                        newState.currentWarningMessage || null,
                    };
                    reduxDispatch(updateLocalState(nextState));
                    pushHistoryState(nextState);
                  } else {
                    finalizeOrManifestation({
                      ...newState,
                      pendingInstructions: rest,
                      currentWarningMessage: null,
                    });
                  }
                } else {
                  // No term on this manually_added instruction at all — skip it cleanly.
                  finalizeOrManifestation({
                    ...newState,
                    pendingInstructions: rest,
                    currentWarningMessage: null,
                  });
                }
              } else {
                // non-manual instruction path — unchanged
                const extensions = await filterTabularCodes(
                  nextInstruction.Next_Parent_code,
                  selectedCategory,
                  nextInstruction._instructionType === "codeto"
                    ? nextInstruction.Character
                    : null,
                );

                if (nextInstruction.Warning_message) {
                  const displayWarning =
                    nextInstruction._instructionType === "codealsowhenperf"
                      ? `Code also when performed: ${nextInstruction.Warning_message}`
                      : nextInstruction.Warning_message;
                  reduxDispatch(setCurrentWarningMessage(displayWarning));
                  newState.currentWarningMessage = displayWarning;
                } else {
                  reduxDispatch(clearWarningMessage());
                  newState.currentWarningMessage = null;
                }

                if (extensions.length > 0) {
                  const noOption = createNoOption();
                  const nextState = {
                    ...newState,
                    currentCodeSearchData: extensions,
                    filteredCodes:
                      nextInstruction._instructionType === "codeto"
                        ? [...extensions]
                        : [noOption, ...extensions],
                    selectedIndex: null,
                    initialSearchDone: true,
                    showFinalScreen: false,
                    pendingInstructions: rest,
                    currentWarningMessage:
                      newState.currentWarningMessage || null,
                  };
                  reduxDispatch(updateLocalState(nextState));
                  pushHistoryState(nextState);
                } else {
                  finalizeOrManifestation({
                    ...newState,
                    pendingInstructions: rest,
                    currentWarningMessage: null,
                  });
                }
              }
            })();
            return;
          }

          // No remaining instructions - finalize
          newState.pendingInstructions = [];
          newState.currentWarningMessage = null;
          newState.showFinalScreen = false;

          finalizeOrManifestation(newState);
          return;
        }

        if (
          selectedCode._fromManualAddedParent === true &&
          (!selectedCode.children || selectedCode.children.length === 0) &&
          (!selectedCode.subcodes || selectedCode.subcodes.length === 0)
        ) {
          // Pre-add the child to selectedCodes so injury flow doesn't drop it.
          const existingArr = localSelectedCodes[selectedCategory] || [];
          const codeKey = `${selectedCode.rawName}-${selectedCode.rawDescription}`;
          const isAlreadyPresent = existingArr.some(
            (c) => `${c.rawName}-${c.rawDescription}` === codeKey,
          );

          if (!isAlreadyPresent) {
            newState.selectedCodes = {
              ...localSelectedCodes,
              [selectedCategory]: [
                ...existingArr,
                {
                  rawName: selectedCode.rawName,
                  rawDescription: selectedCode.rawDescription,
                  isFromTabular: selectedCode.isFromTabular || false,
                  acs: selectedCode.acs || null,
                  nca: selectedCode.nca || null,
                  code_first_instruction: null,
                  use_additional_instruction: null,
                  codealsowhenperf_instruction: null,
                  codeto_instruction: null,
                  manual_added: null,
                  id: selectedCode.id,
                },
              ],
            };
            newState.lastAddedCode = selectedCode;
            // Sync Redux immediately so downstream injury logic sees it
            reduxDispatch(updateLocalState(newState));
          }

          // Clear manual-added parent flag so downstream logic doesn't misread it
          selectedCode = { ...selectedCode, _fromManualAddedParent: false };
        }

        if (selectedCode.children && selectedCode.children.length === 0) {
          newState.currentWarningMessage = null;
        }

        reduxDispatch(handleStoreParentAndChilds(selectedCode));

        const isAdditionalAndFirstEmpty =
          Array.isArray(selectedCode.use_additional_instruction) &&
          selectedCode.use_additional_instruction === " " &&
          Array.isArray(selectedCode.code_first_instruction) &&
          selectedCode.code_first_instruction === "" &&
            Array.isArray(selectedCode.codeto_instruction) &&
          selectedCode.codeto_instruction === "";

        if (isAdditionalAndFirstEmpty) {
          const finalState = {
            ...localState,
            lastAddedCode: selectedCode,
            // showFinalScreen: true,
            showError: false,
          };
          reduxDispatch(clearWarningMessage());
          finalizeOrManifestation(finalState);
          return;
        }
        // 🟣 FIX: use referenceId as id only for See Also options
        const effectiveSelectedCode =
          selectedCode?.isSeeAlsoOption && selectedCode?.referenceId
            ? { ...selectedCode, id: selectedCode.referenceId }
            : selectedCode;
        const newNode = {
          category:
            selectedCode?.children && selectedCode.children.length === 0
              ? selectedCategory
              : null,
          code: selectedCode.isDirectCode
            ? selectedCode.rawName
            : selectedCode._type === "child"
              ? selectedCode.parentName
              : selectedCode.rawName || selectedCode.rawDescription || "",
          description:
            selectedCode.rawDescription || selectedCode.rawName || "",
          isFromTabular: selectedCode.isFromTabular || false,
          timestamp: new Date().toISOString(),
          id: effectiveSelectedCode.id,
        };

        if (!initialSearchDone && !topLevelParent) {
          newState.topLevelParent = selectedCode;
          newState.originalCode = normalizeCodeObject(selectedCode);
          newState.currentPath = [buildHistoryNode(selectedCode, true)];
        } else {
          newState.currentPath = [...currentPath, newNode];
        }
        reduxDispatch(updateCodePathHistory(newNode));

        // ============ INJURY FLOW PROCESSING - FIXED PLACEMENT ============

        const rangeTypes = [
          "injuryWithActivity",
          "externalCauseCodes",
          "placeOfOccurrence",
          "activityCodes",
          "injuryWithoutActivity",
          "injuries",
          "burns",
          "frostbite",
          "poisoning",
          "effectsOfExternalCauses",
          "earlyComplicationsOfTrauma",
          "complicationOfSurgicalAndMedicalCare",
          "otherComplicationOfTrauma",
          "sequelaeOfInjuries",
          "tableofdrugsandchemicals",
        ];
        const resetInjuryCycle = (dispatch) => {
          reduxDispatch(setInitialInjuryState(false));
        };

        let isInAnyRange =
          rangeTypes.some((rangeType) =>
            rangeChildren[rangeType]?.some((rangeCode) => {
              // Add parentheses and proper return
              return (
                rangeCode.id === selectedCode.id ||
                rangeCode.name === selectedCode.rawDescription
              );
            }),
          ) || isTableDrugsCase;

        //  reduxDispatch(setInitialInjuryState(true))

        // ✅ SIMPLER FIX - Use local variable instead of Redux dispatch timing
        let shouldProcessInjuryFlow = false;
        let isStartingNewInjuryFlow = false;

        // Check if this is the FIRST injury code in a sequence
        if (isInAnyRange && !localState.initialInjuryState) {
          isStartingNewInjuryFlow = true;
        }
        shouldProcessInjuryFlow =
          isInAnyRange &&
          initialInjuryState &&
          selectedCode.children.length === 0 &&
          newState.pendingInstructions.length === 0 &&
          !localState.showFinalScreen &&
          selectedIndex !== null;
        //           console.log("🔍 Injury flow check:", {
        //   isInAnyRange,
        //   isStartingNewInjuryFlow,
        //   initialInjuryState: localState.initialInjuryState,
        //   selectedIndex,
        //   shouldProcessInjuryFlow,
        //   childrenLength: selectedCode.children.length,
        //   pendingInstructions: newState.pendingInstructions.length,
        //   showFinalScreen: localState.showFinalScreen
        // });
        if (
          shouldProcessInjuryFlow ||
          newState.pendingInjuryAfterInstructions
        ) {
          // CRITICAL: Update newState immediately
          newState.initialInjuryState = true;

          // Initialize injury flow
          setTimeout(() => {
            reduxDispatch(setInitialInjuryState(true));
          }, 0);

          const currentTracker = localState.injuryCodeFlowTracker || [];

          // Create a unique identifier for the selected code to avoid duplicates
          const codeIdentifier = {
            id: selectedCode.id,
            rawName: selectedCode.rawName,
            rawDescription: selectedCode.rawDescription,
            name: selectedCode.name,
            description: selectedCode.description,
            isFromTabular: selectedCode.isFromTabular || false,
          };

          // Check if this code is already in the tracker to avoid duplicates
          const isAlreadyTracked = currentTracker.some(
            (existingCode) =>
              existingCode.id === codeIdentifier.id &&
              existingCode.rawName === codeIdentifier.rawName,
          );

          // Only add if it's not already in the tracker
          if (!isAlreadyTracked) {
            const updatedTracker = [...currentTracker, codeIdentifier];
            reduxDispatch(setInjuryCodeFlowTracker(updatedTracker));
          }
        }
        // ================= SEE ALSO OPTION CREATION =================

        // if (
        //   !selectedCode.isDirectCode && //  KEY FIX
        //   !selectedCode.isSeeAlsoOption && // KEY FIX
        //   (!selectedCode.children || selectedCode.children.length === 0) &&
        //   (!selectedCode.subcodes || selectedCode.subcodes.length === 0) &&
        //   selectedCode.seealso &&
        //   selectedCode.seealso.trim()
        // ) {
        //   const seeAlsoTerm = selectedCode.seealso.trim();

        //   const seeAlsoOption = {
        //     rawName: seeAlsoTerm,
        //     rawDescription: "Navigate to referenced codes",
        //     isSeeAlsoOption: true,
        //     option: seeAlsoTerm,
        //     originalCode: selectedCode,
        //     referenceId: selectedCode.id,
        //     category: selectedCategory.category,
        //   };

        //   // ✅ DIRECT / UNSPECIFIED OPTION
        //   const directOption = {
        //     ...selectedCode,
        //     isDirectCode: true, // ⭐ IMPORTANT
        //     isSeeAlsoUnspecified: true,
        //   };

        //   const combined = [directOption, seeAlsoOption];

        //   newState = {
        //     ...newState,
        //     selectedParent: selectedCode,
        //     currentCodeSearchData: combined,
        //     filteredCodes: combined,
        //     selectedIndex: null,
        //     initialSearchDone: true,
        //     showFinalScreen: false,
        //     originalCode: normalizeCodeObject(selectedCode),
        //   };

        //   reduxDispatch(updateLocalState(newState));
        //   pushHistoryState(newState);

        //   return; //  STOP here ONLY for initial option creation
        // }

        const hasActivityCheck = injuryFlowTracker.includes("withActivity");
        if (
          isInAnyRange &&
          initialInjuryState &&
          selectedCode.children.length === 0 &&
          newState.pendingInstructions.length === 0 &&
          !hasInstructions &&
          !selectedCode.isDirectCode &&
          !selectedCode.pendingDirectCode
        ) {
          const isInjuryWithoutActivity =
            rangeChildren.injuryWithoutActivity?.some(
              (code) =>
                code.id === selectedCode.id ||
                code.name === selectedCode.rawDescription ||
                code.rawName === selectedCode.rawName,
            );

          // Set the flow state
          if (isInjuryWithoutActivity) {
            setTimeout(() => {
              reduxDispatch(setIsInjuryWithoutActivityFlow(true));
            }, 0);
          }

          // Ensure selected code is added to selectedCodes FIRST
          const existingArr = localSelectedCodes[selectedCategory] || [];
          const codeKey = `${selectedCode.rawName}-${selectedCode.rawDescription}`;
          const isOriginalPresent = existingArr.some(
            (c) => `${c.rawName}-${c.rawDescription}` === codeKey,
          );

          if (!isOriginalPresent) {
            newState.selectedCodes = {
              ...localSelectedCodes,
              [selectedCategory]: [
                ...existingArr,
                {
                  rawName: selectedCode.rawName,
                  rawDescription: selectedCode.rawDescription,
                  isFromTabular: selectedCode.isFromTabular || false,
                  acs: selectedCode.acs,
                  nca: selectedCode.nca,
                  code_first_instruction: selectedCode.code_first_instruction,
                  use_additional_instruction:
                    selectedCode.use_additional_instruction,
                  codealsowhenperf_instruction:
                    selectedCode.codealsowhenperf_instruction,
                  id: selectedCode.id,
                  codeto_instruction: selectedCode.codeto_instruction,
                  manual_added: selectedCode.manual_added,
                },
              ],
            };
            newState.lastAddedCode = selectedCode;
          }

          let rangeToExecute = "";
          let nextRangeType = null;
          let warningMessage = null;

          if (
            rangeChildren.injuryWithActivity?.some(
              (code) =>
                code.id === selectedCode.id ||
                code.name === selectedCode.rawDescription ||
                code.rawName === selectedCode.rawName,
            ) ||
            rangeChildren.injuryWithoutActivity?.some(
              (code) =>
                code.id === selectedCode.id ||
                code.name === selectedCode.rawDescription ||
                code.rawName === selectedCode.rawName,
            ) ||
            isTableDrugsCase // ADD THIS - Force Table of drugs to trigger external cause
          ) {
            rangeToExecute =
              "V00-X59,X60-X84,X85-Y09,Y10-Y34,Y35-Y36,Y37-Y37,Y40-Y84,Y85-Y89,Y90,Y91,Y95,Y96,Y97,Y98";
            nextRangeType = "externalCauseCodes";
            warningMessage = "Select the External Cause";
          } else if (
            rangeChildren.externalCauseCodes?.some(
              (code) =>
                code.id === selectedCode.id ||
                code.name === selectedCode.rawDescription ||
                code.rawName === selectedCode.rawName,
            )
          ) {
            rangeToExecute =
              "Y92.0,Y92.1,Y92.2,Y92.3,Y92.4,Y92.5,Y92.6,Y92.7,Y92.8,Y92.9";
            nextRangeType = "placeOfOccurrence";
            warningMessage = "Select the Place of Occurrence";
          } else if (
            rangeChildren.placeOfOccurrence?.some(
              (code) =>
                code.id === selectedCode.id ||
                code.name === selectedCode.rawDescription ||
                code.rawName === selectedCode.rawName,
            )
          ) {
            if (!hasActivityCheck && !isTableDrugsCase) {
              rangeToExecute = "";
              nextRangeType = "done";
              warningMessage = null;
            } else {
              rangeToExecute = "U50-U72,U73.0,U73.1,U73.2,U73.81,U73.8";
              nextRangeType = "activityCodes";
              warningMessage = "Select Activity";
            }
          } else if (
            rangeChildren.activityCodes?.some(
              (code) =>
                code.id === selectedCode.id ||
                code.name === selectedCode.rawDescription ||
                code.rawName === selectedCode.rawName,
            )
          ) {
            if (hasActivityCheck || isTableDrugsCase) {
              // Reprocess the external cause flow
              rangeToExecute =
                "V00-X59,X60-X84,X85-Y09,Y10-Y34,Y35-Y36,Y37-Y37,Y40-Y84,Y85-Y89,Y90,Y91,Y95,Y96,Y97,Y98";
              nextRangeType = "externalCauseCodes";
              warningMessage = "Select the External Cause";
            } else {
              rangeToExecute = restartInjuryCycle
                ? "U50-U72,U73.0,U73.1,U73.2,U73.81,U73.8"
                : "done";
              nextRangeType = "done";
              warningMessage = warningMessage;
            }

            newState.nextRangeToProcess = nextRangeType;

            // If it's done, show final screen
            if (nextRangeType === "done") {
              finalizeOrManifestation(newState);
              // Reset for next time
              // setTimeout(() => {
              //   reduxDispatch(setInitialInjuryState(false));
              // }, 10);

              // reduxDispatch(updateLocalState(newState));
              // pushHistoryState(newState);
              return;
            }
          }
          // Set the warning message before processing
          // if (warningMessage) {
          //   reduxDispatch(setCurrentWarningMessage(warningMessage));
          // }
          (async () => {
            const extensions = await filterTabularCodesForMultipleRanges(
              rangeToExecute,
              selectedCategory,
            );

            if (extensions.length > 0) {
              // pause for user selection
              const noOption = createNoOption();
              // Show children, wait for user selection
              newState.currentCodeSearchData = extensions;
              newState.filteredCodes = [noOption, ...extensions];
              newState.selectedIndex = null;
              newState.initialSearchDone = true;
              newState.showFinalScreen = false;
              newState.currentWarningMessage = warningMessage;
              newState.originalCode = normalizeCodeObject(selectedCode);
              // newState.topLevelParent=selectedCode
              if (nextRangeType) {
                // Instead of just setting it in newState, dispatch the action
                newState.nextRangeToProcess = nextRangeType;
              } else {
                // If no next range type, set to null and show final screen
                newState.nextRangeToProcess = null;
                finalizeOrManifestation(newState);
                reduxDispatch(setCurrentWarningMessage(null)); // Clear when done
              }
              reduxDispatch(updateLocalState(newState));
              pushHistoryState(newState);
            } else {
              // If no extensions found, check if we should show final screen
              if (!nextRangeType || nextRangeType === "done") {
                newState.nextRangeToProcess = null;
                finalizeOrManifestation(newState);
                reduxDispatch(setCurrentWarningMessage(null)); // Clear when done
              } else {
                // Instead of just setting it in newState, dispatch the action
                newState.nextRangeToProcess = nextRangeType;
                newState.showFinalScreen = false;
              }
              reduxDispatch(updateLocalState(newState));
              pushHistoryState(newState);
            }
            (async () => {
              await new Promise((resolve) => setTimeout(resolve, 0)); // allow Redux updates to flush
              resetInjuryCycle(reduxDispatch);
              //  reduxDispatch(setInitialInjuryState(false));
            })();
          })();
          return;
        } else {
          // Continue with normal non-injury flow processing
        }

        if (
          localState.nextRangeToProcess &&
          selectedCode.children.length === 0 &&
          localState.pendingInstructions.length === 0 &&
          !localState.isInjuryFlow
        ) {
          // Ensure selected code is added to selectedCodes FIRST (skip "No or already coded")
          if (
            selectedCode.rawName !== "No or already coded" &&
            selectedCode.rawDescription !== "No or already coded"
          ) {
            // Ensure selected code is added to selectedCodes FIRST
            const existingArr = localSelectedCodes[selectedCategory] || [];
            const codeKey = `${selectedCode.rawName}-${selectedCode.rawDescription}`;
            const isOriginalPresent = existingArr.some(
              (c) => `${c.rawName}-${c.rawDescription}` === codeKey,
            );

            if (!isOriginalPresent) {
              newState.selectedCodes = {
                ...localSelectedCodes,
                [selectedCategory]: [
                  ...existingArr,
                  {
                    rawName: selectedCode.rawName,
                    rawDescription: selectedCode.rawDescription,
                    isFromTabular: selectedCode.isFromTabular || false,
                    acs: selectedCode.acs,
                    nca: selectedCode.nca,
                    code_first_instruction: selectedCode.code_first_instruction,
                    use_additional_instruction:
                      selectedCode.use_additional_instruction,
                    codealsowhenperf_instruction:
                      selectedCode.codealsowhenperf_instruction,
                    id: selectedCode.id,
                    codeto_instruction: selectedCode.codeto_instruction,
                    manual_added: selectedCode.manual_added,
                  },
                ],
              };
              newState.lastAddedCode = selectedCode;
            }
          } else {
            // For "No or already coded", just update lastAddedCode but don't add to selectedCodes
            newState.lastAddedCode = selectedCode;
          }

          // User selected "No, already coded" - trigger next range automatically
          const nextRange = localState.nextRangeToProcess;
          let nextRangeToExecute = "";
          let nextNextRange = null;
          let warningMessage = null;
          const hasNoActivityCheck =
            injuryFlowTracker.includes("withoutActivity");
          switch (nextRange) {
            case "externalCauseCodes":
              nextRangeToExecute =
                restartInjuryCycle && isInjuryWithoutActivityFlow
                  ? "V00-X59,X60-X84,X85-Y09,Y10-Y34,Y35-Y36,Y37-Y37,Y40-Y84,Y85-Y89,Y90,Y91,Y95,Y96,Y97,Y98"
                  : "Y92.0,Y92.1,Y92.2,Y92.3,Y92.4,Y92.5,Y92.6,Y92.7,Y92.8,Y92.9";
              warningMessage =
                restartInjuryCycle && isInjuryWithoutActivityFlow
                  ? "Select the External Cause"
                  : "Select the Place Of Occurrence";
              break;
            case "placeOfOccurrence":
              nextRangeToExecute = !hasActivityCheck
                ? restartInjuryCycle
                  ? "Y92.0,Y92.1,Y92.2,Y92.3,Y92.4,Y92.5,Y92.6,Y92.7,Y92.8,Y92.9"
                  : ""
                : "U50-U72,U73.0,U73.1,U73.2,U73.81,U73.8";

              warningMessage = !hasActivityCheck
                ? restartInjuryCycle && hasNoActivityCheck
                  ? "Select the Place Of Occurrence"
                  : warningMessage
                : "Select the Activity";
              break;

            case "activityCodes":
              if (hasActivityCheck) {
                // After activity codes, restart the external cause flow for the next injury
                nextRangeToExecute = "";
                nextNextRange = "";
                warningMessage = "Select the External Cause";

                // Reset flags to allow the cycle to continue
                reduxDispatch(setInitialInjuryState(true));
                setStartNewCycle(true);
                sessionStorage.setItem("newcycle", "true");
              } else {
                // For injury without activity, go to final range
                nextRangeToExecute = "";
                nextNextRange = null;
                warningMessage = warningMessage;
              }
              break;
          }
          // Set the warning message
          if (warningMessage) {
            reduxDispatch(setCurrentWarningMessage(warningMessage));
          }

          if (nextRangeToExecute) {
            (async () => {
              const extensions = await filterTabularCodesForMultipleRanges(
                nextRangeToExecute,
                selectedCategory,
              );

              if (extensions.length > 0) {
                const noOption = createNoOption();

                // Determine what comes after this range
                let nextNextRange = null;
                if (nextRange === "externalCauseCodes") {
                  nextNextRange = "placeOfOccurrence";
                } else if (nextRange === "placeOfOccurrence") {
                  nextNextRange = isInjuryWithoutActivityFlow
                    ? restartInjuryCycle
                      ? "U50-U72,U73.0,U73.1,U73.2,U73.81,U73.8"
                      : ""
                    : "done";
                  reduxDispatch(setFinalRangeExecuted(true));
                } else if (nextRange === "activityCodes") {
                  nextNextRange = "U50-U72,U73.0,U73.1,U73.2,U73.81,U73.8"; // This is the final range
                }

                newState.currentCodeSearchData = extensions;
                newState.filteredCodes = [noOption, ...extensions];
                newState.selectedIndex = null;
                newState.initialSearchDone = true;
                newState.showFinalScreen = false;
                newState.currentWarningMessage = warningMessage;
                newState.nextRangeToProcess = nextNextRange; // Set the next range
                // newState.topLevelParent=selectedCode

                reduxDispatch(updateLocalState(newState));
                pushHistoryState(newState);
              } else {
                // If no extensions found, move to next range or show final screen
                if (nextNextRange) {
                  newState.nextRangeToProcess = nextNextRange;
                  newState.showFinalScreen = false;
                } else {
                  newState.nextRangeToProcess = nextNextRange;
                  // newState.showFinalScreen = true;
                  reduxDispatch(setCurrentWarningMessage("")); // Clear when done
                }
                finalizeOrManifestation(newState);
                // reduxDispatch(updateLocalState(newState));
                // pushHistoryState(newState);
              }
              (async () => {
                await new Promise((resolve) => setTimeout(resolve, 0)); // allow Redux updates to flush
                resetInjuryCycle(reduxDispatch);
                //  reduxDispatch(setInitialInjuryState(false));
              })();
            })();

            return;
          }
        }

        const processNextInstruction = async (instructions) => {
          if (!instructions || instructions.length === 0) {
            // All instructions processed - finalize
            const shouldStartInjuryFlow =
              localState.pendingInjuryAfterInstructions;
            const pendingInjuryCode = localState.pendingInjuryCode;

            if (shouldStartInjuryFlow && pendingInjuryCode) {
              // Clear the flags
              reduxDispatch(setPendingInjuryAfterInstructions(false));
              reduxDispatch(setPendingInjuryCode(null));

              // Get the next range type (should have been set earlier)
              const nextRangeType =
                localState.nextRangeToProcess || "externalCauseCodes";
              const warningMessage =
                localState.currentWarningMessage || "Select the External Cause";

              let rangeToExecute = "";
              if (nextRangeType === "externalCauseCodes") {
                rangeToExecute =
                  "V00-X59,X60-X84,X85-Y09,Y10-Y34,Y35-Y36,Y37-Y37,Y40-Y84,Y85-Y89,Y90,Y91,Y95,Y96,Y97,Y98";
              } else if (nextRangeType === "placeOfOccurrence") {
                rangeToExecute =
                  "Y92.0,Y92.1,Y92.2,Y92.3,Y92.4,Y92.5,Y92.6,Y92.7,Y92.8,Y92.9";
              } else if (nextRangeType === "activityCodes") {
                rangeToExecute = "U50-U72,U73.0,U73.1,U73.2,U73.81,U73.8";
              } else {
                // No valid range - show final screen
                finalizeOrManifestation(newState);
                return;
              }

              const extensions = await filterTabularCodesForMultipleRanges(
                rangeToExecute,
                selectedCategory,
              );

              if (extensions.length > 0) {
                const noOption = createNoOption();

                const injuryFlowState = {
                  ...newState,
                  currentCodeSearchData: extensions,
                  filteredCodes: [noOption, ...extensions],
                  selectedIndex: null,
                  initialSearchDone: true,
                  showFinalScreen: false,
                  currentWarningMessage: warningMessage,
                  nextRangeToProcess: nextRangeType,
                  originalCode: normalizeCodeObject(pendingInjuryCode),
                  pendingInstructions: [], // Clear instructions
                  currentInstructionIndex: 0,
                };

                reduxDispatch(updateLocalState(injuryFlowState));
                pushHistoryState(injuryFlowState);
              } else {
                // No extensions found - show final screen or move to next range
                const finalState = {
                  ...newState,
                  // showFinalScreen: true,
                  nextRangeToProcess: null,
                  currentWarningMessage: null,
                };
                finalizeOrManifestation(finalState);
              }
              return;
            }

            // ========== NORMAL COMPLETION (NO INJURY FLOW) ==========
            // Your existing code to finalize...
            const currArr =
              (newState.selectedCodes &&
                newState.selectedCodes[selectedCategory]) ||
              [];
            const codeKey = `${selectedCode.rawName}-${selectedCode.rawDescription}`;
            const isOriginalPresent = currArr.some(
              (c) => `${c.rawName}-${c.rawDescription}` === codeKey,
            );

            if (!isOriginalPresent) {
              newState.selectedCodes = {
                ...newState.selectedCodes,
                [selectedCategory]: [
                  ...currArr,
                  {
                    rawName: selectedCode.rawName,
                    rawDescription: selectedCode.rawDescription,
                    isFromTabular: selectedCode?.isFromTabular || false,
                    acs: selectedCode.acs,
                    nca: selectedCode.nca,
                    code_first_instruction: selectedCode.code_first_instruction,
                    use_additional_instruction:
                      selectedCode.use_additional_instruction,
                    codealsowhenperf_instruction:
                      selectedCode.codealsowhenperf_instruction,
                    id: selectedCode.id,
                    codeto_instruction: selectedCode.codeto_instruction,
                    manual_added: selectedCode.manual_added,
                  },
                ],
              };
              newState.lastAddedCode = selectedCode;
            }

            // newState.showFinalScreen = true;
            newState.pendingInstructions = [];
            newState.currentInstructionIndex = 0;
            newState.currentWarningMessage = null;
            finalizeOrManifestation(newState);
            return;
          }

          const [current, ...remaining] = instructions;
          let displayWarning = null;
          newState.pendingInstructions = remaining;
          newState.currentWarningMessage = current.Warning_message || null;
          if (current._instructionType === "codealsowhenperf") {
            displayWarning = `Code also when performed: ${current.Warning_message}`;
            newState.currentWarningMessage = displayWarning || null;
          }

          if (current.Warning_message) {
            if (current._instructionType === "codealsowhenperf") {
              displayWarning = `Code also when performed: ${current.Warning_message}`;
              reduxDispatch(setCurrentWarningMessage(displayWarning));
            } else {
              reduxDispatch(setCurrentWarningMessage(current.Warning_message));
            }
          } else {
            reduxDispatch(clearWarningMessage());
          }

          // Process Next_Parent_code for ALL instruction types including codealsowhenperf
          let extensions = [];
          if (
            current.Next_Parent_code &&
            current.Next_Parent_code.toString().trim() !== ""
          ) {
            extensions = await resolveInstructionExtensions(current, selectedCategory);
          } else if (
            Array.isArray(current.children) &&
            current.children.length > 0
          ) {
            // Handle children for all instruction types
            const noOption = createNoOption();

            const mapChildrenToOptions = (childrenArr = []) =>
              (childrenArr || [])
                .filter((child) => child && (child.name || child.rawName))
                .map((child) => ({
                  rawName: child.name || child.rawName,
                  rawDescription:
                    child.description ??
                    (child.description === null
                      ? null
                      : child.name || child.rawName),
                  name: child.name || child.rawName,
                  description:
                    child.description ??
                    (child.description === null
                      ? null
                      : child.name || child.rawName),
                  children: child.subcodes || child.children || [],
                  isFromTabular:
                    child.isFromTabular || selectedCode.isFromTabular || false,
                  Next_parent_code:
                    child.Next_parent_code || child.Next_Parent_code || null,
                  Warning_message: child.Warning_message,
                  see: child.see,
                  seealso: child.seealso,
                  isDescriptionNull: child.description === null,
                  id: child.id,
                  acs: child.acs,
                  nca: child.nca,
                  code_first_instruction: child.code_first_instruction,
                  use_additional_instruction: child.use_additional_instruction,
                  codealsowhenperf_instruction:
                    child.codealsowhenperf_instruction, // Include this
                  codeto_instruction: child.codeto_instruction,
                  manual_added: child.manual_added,
                }));

            const childrenOpts = mapChildrenToOptions(current.children);
            newState.currentCodeSearchData = childrenOpts;
            newState.filteredCodes = [noOption, ...childrenOpts];
            newState.selectedIndex = null;
            newState.initialSearchDone = true;
            newState.showFinalScreen = false;
            newState.originalCode = normalizeCodeObject(selectedCode);

            reduxDispatch(updateLocalState(newState));
            pushHistoryState(newState);
            return;
          }

          // console.log(
          //   "Extensions for",
          //   current._instructionType,
          //   ":",
          //   extensions,
          // );

          if (extensions.length > 0) {
            // Pause for user selection (extensions) - this should work for codealsowhenperf too
            const noOption = createNoOption();

            newState.currentCodeSearchData = extensions;
            newState.filteredCodes =
              current._instructionType === "codeto"
                ? [...extensions]
                : [noOption, ...extensions];
            newState.selectedIndex = null;
            newState.initialSearchDone = true;
            newState.showFinalScreen = false;
            newState.originalCode = normalizeCodeObject(selectedCode);

            reduxDispatch(updateLocalState(newState));
            pushHistoryState(newState);
            return;
          } else {
            // No Next_Parent_code / no extensions -> add current instruction's code (if any), then continue

            const isSentinel =
              current.Next_Parent_code === "No or already coded" ||
              !current.Next_Parent_code ||
              current.Next_Parent_code.toString().trim() === "";

            if (!isSentinel) {
              const leafKey = `${current._parent?.rawName || current.Next_Parent_code}-${current.Next_Parent_code}`;
              const existingArr =
                newState.selectedCodes[selectedCategory] || [];
              const alreadyPresent = existingArr.some(
                (c) => `${c.rawName}-${c.rawDescription}` === leafKey,
              );

              if (!alreadyPresent) {
                const leafCode = {
                  rawName: current._parent?.rawName || current.Next_Parent_code,
                  rawDescription: current.Next_Parent_code,
                  isFromTabular: false,
                  id: current._parent?.id || null,
                  acs: null,
                  nca: null,
                  code_first_instruction: null,
                  use_additional_instruction: null,
                  codealsowhenperf_instruction: null,
                  codeto_instruction: null,
                  manual_added: null,
                };
                newState.selectedCodes = {
                  ...newState.selectedCodes,
                  [selectedCategory]: [...existingArr, leafCode],
                };
                newState.lastAddedCode = leafCode;
                reduxDispatch(updateLocalState(newState)); // keep redux in sync before recursing
              }
            }

            const shouldProcessSpecialCodesAfterInstructions = () => {
              const hasPendingSpecialCodes =
                localState.specialCodesToProcess &&
                localState.specialCodesToProcess.length > 0;
              const hasMoreInstructions = remaining && remaining.length > 0;
              return hasPendingSpecialCodes && !hasMoreInstructions;
            };

            if (shouldProcessSpecialCodesAfterInstructions()) {
              processSpecialCodesAfterInstructions(remaining);
            } else {
              processNextInstruction(remaining);
            }
          }
        };
        const processSpecialCodesAfterInstructions = async (
          remainingInstructions = [],
        ) => {
          // If there are still instructions left, process them first
          if (remainingInstructions && remainingInstructions.length > 0) {
            processNextInstruction(remainingInstructions);
            return;
          }

          // Check if we have special codes to process
          const hasPendingSpecialCodes =
            localState.specialCodesToProcess &&
            localState.specialCodesToProcess.length > 0;

          if (!hasPendingSpecialCodes) {
            // Ensure original code is present
            const currArr =
              (newState.selectedCodes &&
                newState.selectedCodes[selectedCategory]) ||
              [];
            const codeKey = `${selectedCode.rawName}-${selectedCode.rawDescription}`;
            const isOriginalPresent = currArr.some(
              (c) => `${c.rawName}-${c.rawDescription}` === codeKey,
            );

            if (!isOriginalPresent) {
              newState.selectedCodes = {
                ...newState.selectedCodes,
                [selectedCategory]: [
                  ...currArr,
                  {
                    rawName: selectedCode.rawName,
                    rawDescription: selectedCode.rawDescription,
                    isFromTabular: selectedCode.isFromTabular || false,
                    acs: selectedCode.acs,
                    nca: selectedCode.nca,
                    code_first_instruction: selectedCode.code_first_instruction,
                    use_additional_instruction:
                      selectedCode.use_additional_instruction,
                    codealsowhenperf_instruction:
                      selectedCode.codealsowhenperf_instruction,
                    codeto_instruction: selectedCode.codeto_instruction,
                    manual_added: selectedCode.manual_added,
                    id: selectedCode.id,
                  },
                ],
              };
              newState.lastAddedCode = selectedCode;
            }

            // newState.showFinalScreen = true;
            newState.pendingInstructions = [];
            newState.currentInstructionIndex = 0;
            newState.currentWarningMessage = null;
            newState.processingSpecialCodes = false;
            newState.specialCodesToProcess = [];
            // PRESERVE the original special codes with symbols
            newState.originalSpecialCodesWithSymbols =
              localState.originalSpecialCodesWithSymbols || []; //
            finalizeOrManifestation(newState);
            return;
          }

          // Process the next special code
          const currentSpecialCode = localState.specialCodesToProcess[0];
          const remainingSpecialCodes =
            localState.specialCodesToProcess.slice(1);

          // Get tabular extensions for the current special code
          const extensions = getTabularExtensions(
            currentSpecialCode,
            selectedCategory,
            true,
          );

          if (extensions && extensions.length > 0) {
            // Show the extension children for user selection
            newState = {
              ...newState,
              processingSpecialCodes: true,
              specialCodesToProcess: remainingSpecialCodes,
              currentCodeSearchData: extensions,
              filteredCodes: extensions,
              selectedIndex: null,
              initialSearchDone: true,
              showFinalScreen: false,
              originalCode: normalizeCodeObject(selectedCode),
              currentSpecialCode: currentSpecialCode,
              pendingInstructions: [], // Clear any remaining instructions
              currentInstructionIndex: 0,
              currentWarningMessage: null,
              selectedParent: selectedCode,
              lastAddedCode: selectedCode,
              // PRESERVE existing symbols and add new ones
              originalSpecialCodesWithSymbols: [
                ...(localState.originalSpecialCodesWithSymbols || []),
                ...(newState.originalSpecialCodesWithSymbols || []),
              ].filter((value, index, self) => self.indexOf(value) === index), // Remove duplicates
            };

            reduxDispatch(updateLocalState(newState));
            pushHistoryState(newState);
          } else {
            // No extensions found for this special code - treat as leaf node
            // console.log(
            //   `No extensions found for ${currentSpecialCode}, treating as leaf node`
            // );

            const finalCodeToAdd = {
              rawName: currentSpecialCode,
              rawDescription: currentSpecialCode,
              isFromTabular: true,
              nca: null,
              code_first_instruction: null,
              use_additional_instruction: null,
              codealsowhenperf_instruction: null,
              codeto_instruction: null,
              manual_added: null,
            };

            const updatedSelectedCodes = {
              ...newState.selectedCodes,
              [selectedCategory]: [
                ...(newState.selectedCodes[selectedCategory] || []),
                finalCodeToAdd,
              ],
            };

            if (remainingSpecialCodes.length > 0) {
              // Continue with remaining special codes
              newState = {
                ...newState,
                selectedCodes: updatedSelectedCodes,
                lastAddedCode: finalCodeToAdd,
                processingSpecialCodes: true,
                specialCodesToProcess: remainingSpecialCodes,
                // PRESERVE symbols from localState
                originalSpecialCodesWithSymbols:
                  localState.originalSpecialCodesWithSymbols || [],
                currentCodeSearchData: null,
                filteredCodes: [],
                selectedIndex: null,
                initialSearchDone: false,
                showFinalScreen: false,
                pendingInstructions: [],
                currentInstructionIndex: 0,
                selectedParent: selectedCode,
              };

              reduxDispatch(updateLocalState(newState));
              pushHistoryState(newState);
            } else {
              newState = {
                ...newState,
                selectedCodes: updatedSelectedCodes,
                lastAddedCode: selectedCode,
                // showFinalScreen: true,
                processingSpecialCodes: false,
                // PRESERVE symbols for final display
                originalSpecialCodesWithSymbols:
                  localState.originalSpecialCodesWithSymbols || [],
                specialCodesToProcess: [],
                currentSpecialCodeIndex: 0,
                pendingInstructions: [],
                currentInstructionIndex: 0,
                selectedParent: selectedCode,
              };
              finalizeOrManifestation(newState);
            }
          }
        };

        if (selectedCode._isCustomOption === true) {
          // Check if we have pending injury flow (highest priority)
          const shouldStartInjuryFlow =
            localState.pendingInjuryAfterInstructions;
          const pendingInjuryCode = localState.pendingInjuryCode;

          if (
            shouldStartInjuryFlow &&
            pendingInjuryCode &&
            localState.pendingInstructions.length === 0
          ) {
            // Wrap async operations in an IIFE
            (async () => {
              reduxDispatch(setPendingInjuryAfterInstructions(false));
              reduxDispatch(setPendingInjuryCode(null));
              reduxDispatch(clearWarningMessage());
              reduxDispatch(clearPendingInstructions());

              const nextRangeType =
                localState.nextRangeToProcess || "externalCauseCodes";
              let warningMessage = "Select the External Cause";
              let rangeToExecute = "";

              if (nextRangeType === "externalCauseCodes") {
                rangeToExecute =
                  "V00-X59,X60-X84,X85-Y09,Y10-Y34,Y35-Y36,Y37-Y37,Y40-Y84,Y85-Y89,Y90,Y91,Y95,Y96,Y97,Y98";
                warningMessage = "Select the External Cause";
              } else if (nextRangeType === "placeOfOccurrence") {
                rangeToExecute =
                  "Y92.0,Y92.1,Y92.2,Y92.3,Y92.4,Y92.5,Y92.6,Y92.7,Y92.8,Y92.9";
                warningMessage = "Select the Place of Occurrence";
              } else if (nextRangeType === "activityCodes") {
                rangeToExecute = "U50-U72,U73.0,U73.1,U73.2,U73.81,U73.8";
                warningMessage = "Select Activity";
              }

              const extensions = await filterTabularCodesForMultipleRanges(
                rangeToExecute,
                selectedCategory,
              );

              if (extensions.length > 0) {
                const noOption = createNoOption();

                const injuryFlowState = {
                  ...newState,
                  currentCodeSearchData: extensions,
                  filteredCodes: [noOption, ...extensions],
                  selectedIndex: null,
                  initialSearchDone: true,
                  showFinalScreen: false,
                  currentWarningMessage: warningMessage,
                  nextRangeToProcess: nextRangeType,
                  originalCode: normalizeCodeObject(pendingInjuryCode),
                  pendingInstructions: [],
                  currentInstructionIndex: 0,
                  processingSpecialCodes: false,
                  specialCodesToProcess: [],
                  selectedParent: pendingInjuryCode,
                  lastAddedCode: pendingInjuryCode,
                  pendingInjuryAfterInstructions: false,
                  pendingInjuryCode: null,
                };

                reduxDispatch(updateLocalState(injuryFlowState));
                pushHistoryState(injuryFlowState);
              } else {
                const finalState = {
                  ...newState,
                  pendingInjuryAfterInstructions: false,
                  pendingInjuryCode: null,
                  currentWarningMessage: null,
                };
                finalizeOrManifestation(finalState);
              }
            })();
            return;
          }

          const hasMoreInstructions =
            newState.pendingInstructions &&
            newState.pendingInstructions.length > 0;

          if (hasMoreInstructions) {
            // console.log(
            //   "📋 Custom option selected - processing next instruction",
            // );
            // console.log(
            //   "Current pending instructions count:",
            //   newState.pendingInstructions.length,
            // );

            // Don't add "No or already coded" to selectedCodes
            // Get the current instruction (first in the array)
            const [currentInstruction, ...remainingInstructions] =
              newState.pendingInstructions;

            newState = {
              ...newState,
              pendingInstructions: [
                currentInstruction,
                ...remainingInstructions,
              ],
              selectedIndex: null,
              initialSearchDone: true,
              showFinalScreen: false,
              currentInstructionIndex: 0,
            };

            reduxDispatch(
              setPendingInstructions([
                currentInstruction,
                ...remainingInstructions,
              ]),
            );

            // Wrap async operations in an IIFE
            (async () => {
              // Check if the current instruction has Next_Parent_code or children
              let extensions = [];

              if (
                currentInstruction.Next_Parent_code &&
                currentInstruction.Next_Parent_code.toString().trim() !== ""
              ) {
                extensions = await resolveInstructionExtensions(currentInstruction, selectedCategory);
              } else if (
                Array.isArray(currentInstruction.children) &&
                currentInstruction.children.length > 0
              ) {
                const mapChildrenToOptions = (childrenArr = []) =>
                  (childrenArr || [])
                    .filter((child) => child && (child.name || child.rawName))
                    .map((child) => ({
                      rawName: child.name || child.rawName,
                      rawDescription:
                        child.description ??
                        (child.description === null
                          ? null
                          : child.name || child.rawName),
                      name: child.name || child.rawName,
                      description:
                        child.description ??
                        (child.description === null
                          ? null
                          : child.name || child.rawName),
                      children: child.subcodes || child.children || [],
                      isFromTabular:
                        child.isFromTabular ||
                        selectedCode.isFromTabular ||
                        false,
                      Next_parent_code:
                        child.Next_parent_code ||
                        child.Next_Parent_code ||
                        null,
                      Warning_message: child.Warning_message,
                      see: child.see,
                      seealso: child.seealso,
                      isDescriptionNull: child.description === null,
                      id: child.id,
                      acs: child.acs,
                      nca: child.nca,
                      code_first_instruction: child.code_first_instruction,
                      use_additional_instruction:
                        child.use_additional_instruction,
                      codealsowhenperf_instruction:
                        child.codealsowhenperf_instruction,
                      codeto_instruction: child.codeto_instruction,
                      manual_added: child.manual_added,
                    }));

                extensions = mapChildrenToOptions(currentInstruction.children);
              }
              if (currentInstruction.Warning_message) {
                const displayWarning =
                  currentInstruction._instructionType === "codealsowhenperf"
                    ? `Code also when performed: ${currentInstruction.Warning_message}`
                    : currentInstruction.Warning_message;
                reduxDispatch(setCurrentWarningMessage(displayWarning));
                newState.currentWarningMessage = displayWarning;
              } else {
                reduxDispatch(clearWarningMessage());
                newState.currentWarningMessage = null;
              }
              // If we have extensions (children to show for this instruction)
              if (extensions.length > 0) {
                const noOption = createNoOption();
                newState.pendingInstructions = remainingInstructions;
                newState.currentCodeSearchData = extensions;
                newState.filteredCodes =
                  currentInstruction._instructionType === "codeto"
                    ? [...extensions]
                    : [noOption, ...extensions];
                newState.initialSearchDone = true;
                newState.showFinalScreen = false;
                newState.selectedIndex = null;
                newState.originalCode = normalizeCodeObject(selectedCode);
                newState.currentWarningMessage =
                  newState.currentWarningMessage || null;

                reduxDispatch(setPendingInstructions(remainingInstructions));
                reduxDispatch(updateLocalState(newState));
                pushHistoryState(newState);
              }
              // No extensions - this instruction is done, move to next instruction
              else {
                if (remainingInstructions.length > 0) {
                  // Advance queue and let the next instruction be processed
                  // when the user clicks again — but keep the warning from
                  // the current instruction so the user isn't confused.
                  newState.pendingInstructions = remainingInstructions;
                  newState.currentWarningMessage =
                    newState.currentWarningMessage || null;

                  reduxDispatch(setPendingInstructions(remainingInstructions));
                  reduxDispatch(updateLocalState(newState));
                  pushHistoryState(newState);
                  return;
                }
                // No more instructions - check for pending injury flow
                else {
                  const finalShouldStartInjuryFlow =
                    localState.pendingInjuryAfterInstructions;
                  const finalPendingInjuryCode = localState.pendingInjuryCode;

                  if (finalShouldStartInjuryFlow && finalPendingInjuryCode) {
                    reduxDispatch(setPendingInjuryAfterInstructions(false));
                    reduxDispatch(setPendingInjuryCode(null));
                    reduxDispatch(clearWarningMessage());

                    const nextRangeType =
                      localState.nextRangeToProcess || "externalCauseCodes";
                    let warningMessage = "Select the External Cause";
                    let rangeToExecute = "";

                    if (nextRangeType === "externalCauseCodes") {
                      rangeToExecute =
                        "V00-X59,X60-X84,X85-Y09,Y10-Y34,Y35-Y36,Y37-Y37,Y40-Y84,Y85-Y89,Y90,Y91,Y95,Y96,Y97,Y98";
                      warningMessage = "Select the External Cause";
                    } else if (nextRangeType === "placeOfOccurrence") {
                      rangeToExecute =
                        "Y92.0,Y92.1,Y92.2,Y92.3,Y92.4,Y92.5,Y92.6,Y92.7,Y92.8,Y92.9";
                      warningMessage = "Select the Place of Occurrence";
                    } else if (nextRangeType === "activityCodes") {
                      rangeToExecute = "U50-U72,U73.0,U73.1,U73.2,U73.81,U73.8";
                      warningMessage = "Select Activity";
                    }

                    const injuryExtensions =
                      await filterTabularCodesForMultipleRanges(
                        rangeToExecute,
                        selectedCategory,
                      );

                    if (injuryExtensions.length > 0) {
                      const noOption = createNoOption();

                      const injuryFlowState = {
                        ...newState,
                        currentCodeSearchData: injuryExtensions,
                        filteredCodes: [noOption, ...injuryExtensions],
                        selectedIndex: null,
                        initialSearchDone: true,
                        showFinalScreen: false,
                        currentWarningMessage: warningMessage,
                        nextRangeToProcess: nextRangeType,
                        originalCode: normalizeCodeObject(
                          finalPendingInjuryCode,
                        ),
                        pendingInstructions: [],
                        currentInstructionIndex: 0,
                        processingSpecialCodes: false,
                        specialCodesToProcess: [],
                        selectedParent: finalPendingInjuryCode,
                        lastAddedCode: finalPendingInjuryCode,
                        pendingInjuryAfterInstructions: false,
                        pendingInjuryCode: null,
                      };

                      reduxDispatch(updateLocalState(injuryFlowState));
                      pushHistoryState(injuryFlowState);
                    } else {
                      const finalState = {
                        ...newState,
                        pendingInjuryAfterInstructions: false,
                        pendingInjuryCode: null,
                        currentWarningMessage: null,
                      };
                      finalizeOrManifestation(finalState);
                    }
                  } else {
                    // No pending injury flow - finalize, keeping the current
                    // warning visible in the interim.
                    finalizeOrManifestation({
                      ...newState,
                      pendingInstructions: remainingInstructions,
                      currentWarningMessage:
                        newState.currentWarningMessage || null,
                    });
                  }
                }
              }
            })();
            return;
          }

          // Check if we have pending special codes
          const hasPendingSpecialCodes =
            localState.specialCodesToProcess &&
            localState.specialCodesToProcess.length > 0;

          if (hasPendingSpecialCodes) {
            processSpecialCodesAfterInstructions();
            return;
          }

          // No pending instructions or special codes - finalize
          const finalState = {
            ...localState,
            pendingInstructions: [],
            currentInstructionIndex: 0,
            processingSpecialCodes: false,
            specialCodesToProcess: [],
            selectedParent: selectedCode,
            lastAddedCode: selectedCode,
            pendingInjuryAfterInstructions: false,
            pendingInjuryCode: null,
            currentWarningMessage: null,
          };
          finalizeOrManifestation(finalState);
          return;
        }

        // Check if the selected code has children to show
        const hasChildrenToShow =
          (selectedCode.children && selectedCode.children.length > 0) ||
          (selectedCode.subcodes && selectedCode.subcodes.length > 0);
        // Add this check at the beginning of handleContinue or in a useEffect
        if (hasInstructions && !hasChildrenToShow) {
          const isInjuryCode = isInAnyRange; // Use your existing isInAnyRange variable

          if (isInjuryCode) {
            // Set flag to start injury flow after instructions
            reduxDispatch(setPendingInjuryAfterInstructions(true));
            reduxDispatch(setPendingInjuryCode(selectedCode));

            // Also store the next range type
            const nextRangeType = "externalCauseCodes";
            const warningMessage = "Select the External Cause";
            reduxDispatch(setNextRangeToProcess(nextRangeType));
            reduxDispatch(setCurrentWarningMessage(warningMessage));
          }

          // gather both instruction arrays
          const firstInstr = selectedCode.code_first_instruction || [];

          const addInstr = selectedCode.use_additional_instruction || [];

          const addCodeWhen = selectedCode.codealsowhenperf_instruction || [];

          const addCodeto = selectedCode.codeto_instruction || [];
          const manualAdded = selectedCode.manual_added || [];

          const allInstrs = [
            ...manualAdded,
            ...firstInstr,
            ...addInstr,
            ...addCodeWhen,
            ...addCodeto,
          ];

          // determine if there's at least one actionable instruction:
          // - a non-empty Next_Parent_code OR instruction with children
          const hasValidNextParentCode = allInstrs.some(
            (instr) =>
              instr &&
              instr.Next_Parent_code &&
              instr.Next_Parent_code.toString().trim() !== "",
          );

          const hasChildrenInstr = allInstrs.some(
            (instr) =>
              instr &&
              Array.isArray(instr.children) &&
              instr.children.length > 0,
          );
          const shouldProcessInstructions =
            hasValidNextParentCode || hasChildrenInstr;

          // if no actionable instruction, fall through to normal child/final processing
          if (!shouldProcessInstructions) {
            /* do nothing here — allow the code below (children / final processing)
       to run as if there were no instructions */
          } else {
            // console.log(
            //   shouldProcessInstructions,
            //   "shouldProcessInstructionsgoing",
            // );

            // Ensure original code is added first (if not already present)
            const existingArr = localSelectedCodes[selectedCategory] || [];
            const codeKey = `${selectedCode.rawName}-${selectedCode.rawDescription}`;
            const alreadyAdded = existingArr.some(
              (c) => `${c.rawName}-${c.rawDescription}` === codeKey,
            );

            if (!alreadyAdded) {
              // Skip adding "No or already coded" pseudo-option
              if (
                selectedCode.rawName === "No or already coded" ||
                selectedCode.rawDescription === "No or already coded"
              ) {
                return; // simply exit early — don’t add it
              }

              const newArr = [
                ...existingArr,
                {
                  rawName: selectedCode.rawName,
                  rawDescription: selectedCode.rawDescription,
                  isFromTabular: selectedCode.isFromTabular || false,
                  acs: selectedCode.acs,
                  nca: selectedCode.nca,
                  code_first_instruction: selectedCode.code_first_instruction,
                  use_additional_instruction:
                    selectedCode.use_additional_instruction,
                  codealsowhenperf_instruction:
                    selectedCode.codealsowhenperf_instruction,
                  codeto_instruction: selectedCode.codeto_instruction,
                  manual_added: selectedCode.manual_added,
                  lastAddedCode: selectedCode,
                  id: selectedCode.id,
                },
              ];
              const existingCategoryCodes =
                newState.selectedCodes[selectedCategory] || [];
              newState = {
                ...newState,
                selectedCodes: {
                  ...localSelectedCodes,
                  [selectedCategory]: [...existingCategoryCodes, ...newArr],
                },
                lastAddedCode: selectedCode,
                selectedParent: selectedCode,
              };

              reduxDispatch(updateLocalState(newState));
            }

            (async () => {
              // Flatten instructions: produce items with Next_Parent_code normalized.
              const flattenInstructions = (
                instructions,
                isAdditional = false,
                instructionType = "code_first",
              ) =>
                (instructions || []).flatMap((instr) => {
                  const next = instr?.Next_Parent_code;
                  let isManifestation = false;
                  if (
                    next &&
                    next.toString().trim().toLowerCase() === "manifestation"
                  ) {
                    isManifestation = true;
                    const warningMsg =
                      instr.Warning_message ||
                      "Do you want to code manifestation codes?";

                    // Update Redux
                    reduxDispatch(
                      setPendingManifestationAfterInstructions(true),
                    );
                    reduxDispatch(setManifestationWarningMessage(warningMsg));

                    // We need to use a reference to newState from the outer scope
                    if (typeof newState !== "undefined") {
                      newState.pendingManifestationAfterInstructions = true;
                      newState.manifestationWarningMessage = warningMsg;
                      // NEW: remember if this manifestation came specifically from codeto_instruction
                      newState.manifestationIsFromCodeTo =
                        instructionType === "codeto";
                    }
                  }
                  const codes =
                    next && next.toString().trim()
                      ? instructionType === "manually_added"
                        ? // Do NOT split on comma for manually_added
                          [next.toString().trim()]
                        : next
                            .toString()
                            .split(",")
                            .map((c) => c.trim())
                            .filter(Boolean)
                      : [];

                  if (isManifestation) {
                    // Return an empty array or a special marker
                    return [];
                  }
                  // If no Next_Parent_code but children exist, keep the instr (with Next_Parent_code: null)
                  if (codes.length === 0) {
                    return [
                      {
                        ...instr,
                        Next_Parent_code: null,
                        _parent: {
                          rawName: selectedCode.rawName,
                          rawDescription: selectedCode.rawDescription,
                          id: selectedCode.id,
                          isFromTabular: selectedCode.isFromTabular || false,
                        },
                        _isAdditional: isAdditional,
                        _instructionType: instructionType,
                      },
                    ];
                  }

                  // For comma-separated codes, create individual instructions for EACH code
                  return codes.map((code) => ({
                    ...instr,
                    Next_Parent_code: code, // Single code, not comma-separated
                    _parent: {
                      rawName: selectedCode.rawName,
                      rawDescription: selectedCode.rawDescription,
                      id: selectedCode.id,
                      isFromTabular: selectedCode.isFromTabular || false,
                    },
                    _isAdditional: isAdditional,
                    _instructionType: instructionType,
                  }));
                });

              // Update the pendingInstructions creation:
              // Flatten this code's own instructions
              const newInstructions = [
                ...flattenInstructions(
                  selectedCode.code_first_instruction || [],
                  false,
                  "code_first",
                ),
                ...flattenInstructions(
                  selectedCode.use_additional_instruction || [],
                  true,
                  "use_additional",
                ),
                ...flattenInstructions(
                  selectedCode.codealsowhenperf_instruction || [],
                  false,
                  "codealsowhenperf",
                ),
                ...flattenInstructions(
                  selectedCode.codeto_instruction || [],
                  false,
                  "codeto",
                ),
                ...flattenInstructions(
                  selectedCode.manual_added || [],
                  false,
                  "manually_added",
                ),
              ];

              const carriedOverInstructions = (
                localState.pendingInstructions || []
              ).map((instr) => ({ ...instr, _carriedOver: true }));

              // Dedup by the target code itself, not by parent id (see earlier note on why)
              const normalizeCode = (c) =>
                (c || "").toString().replace(/\./g, "").toUpperCase().trim();
              const seenInstructionKeys = new Set();
              const pendingInstructions = [
                ...newInstructions,
                ...carriedOverInstructions,
              ].filter((instr) => {
                const key = `${normalizeCode(instr.Next_Parent_code)}|${instr._instructionType}`;
                if (seenInstructionKeys.has(key)) return false;
                seenInstructionKeys.add(key);
                return true;
              });

              // Early exit if nothing to process
              // if (pendingInstructions.length === 0) return;
              if (pendingInstructions.length === 0) {
                if (newState.pendingManifestationAfterInstructions) {
                  finalizeOrManifestation(newState);
                }
                return;
              }
              const updatedTopLevelParent = {
                ...selectedParent, // Keep all existing properties
                rawDescription: selectedCode.isFromTabular
                  ? selectedCode.rawDescription
                  : selectedCode.rawName, // Update with selected code's description
                // You might also want to update other relevant fields
                description: selectedCode.isFromTabular
                  ? selectedCode.rawDescription
                  : selectedCode.rawName,
                rawName: selectedCode.isFromTabular
                  ? selectedCode.rawDescription
                  : selectedCode.rawName,
              };

              // UPDATE: Set the modified topLevelParent
              newState = {
                ...newState,
                selectedParent: updatedTopLevelParent,
                originalCode: normalizeCodeObject(selectedCode),
                selectedParent: selectedCode,
              };
              // Add this function inside the async instruction processing

              // helper that maps children (instruction-child objects) into UI items

              // Recursive processor

              // Update state with pending instructions before starting
              newState.pendingInstructions = pendingInstructions;
              newState.currentInstructionIndex = 0;
              newState.showFinalScreen = false;

              reduxDispatch(setPendingInstructions(pendingInstructions));
              reduxDispatch(
                updateLocalState({
                  currentInstructionIndex: 0,
                  showFinalScreen: false,
                }),
              );

              processNextInstruction(pendingInstructions);
            })();

            // Return early to prevent regular processing while instructions are running
            return;
          }
        }
        // single morphology- route to neoplasm always
        const isMorphologyEntry = (code) => {
          if (!code) return false;
          const raw = (code.rawDescription || code.description || "").trim();
          // Must be ONLY a morphology code — no ICD code following it
          return /^\(?(M\d+\/\d)\)?$/.test(raw);
        };

        if (
          selectedCode &&
          !selectedCode.seeAlso &&
          !selectedCode.see &&
          !selectedCode.isFromTabular &&
          (!selectedCode.children || selectedCode.children.length === 0) &&
          (!selectedCode.subcodes || selectedCode.subcodes.length === 0) &&
          isMorphologyEntry(selectedCode)
        ) {
          // Add morphology code itself to selectedCodes first
          const existingArr = localSelectedCodes[selectedCategory] || [];
          const morphRaw = (
            selectedCode.rawDescription ||
            selectedCode.description ||
            ""
          )
            .trim()
            .replace(/[()]/g, "");
          const morphKey = `${selectedCode.rawName}-${morphRaw}`;
          const alreadyAdded = existingArr.some(
            (c) =>
              `${c.rawName}-${(c.rawDescription || "").replace(/[()]/g, "")}` ===
              morphKey,
          );

          if (!alreadyAdded) {
            newState.selectedCodes = {
              ...localSelectedCodes,
              [selectedCategory]: [
                ...existingArr,
                {
                  rawName: selectedCode.rawName,
                  rawDescription: morphRaw,
                  isFromTabular: false,
                  isMorphologyCode: true,
                  acs: selectedCode.acs || null,
                  nca: selectedCode.nca || null,
                  code_first_instruction: null,
                  use_additional_instruction: null,
                  codealsowhenperf_instruction: null,
                  codeto_instruction: null,
                  manual_added: null,
                  id: selectedCode.id,
                },
              ],
            };
            newState.lastAddedCode = selectedCode;
          }

          // Try "Neoplasm" root first, fallback to "Neoplastic"
          let resolveResult = resolveSeePath(["Neoplasm"], selectedCategory);

          if (!resolveResult || resolveResult.length === 0) {
            resolveResult = resolveSeePath(["Neoplastic"], selectedCategory);
          }

          if (resolveResult && resolveResult.length > 0) {
            newState = {
              ...newState,
              selectedParent: selectedCode,
              currentCodeSearchData: resolveResult,
              filteredCodes: resolveResult,
              selectedIndex: null,
              initialSearchDone: true,
              showFinalScreen: false,
              originalCode: normalizeCodeObject(selectedCode),
              carryDirectCodeWithoutInstructions: true,
              pendingDirectCode: {
                rawName: selectedCode.rawName,
                rawDescription: morphRaw,
                isFromTabular: false,
                isMorphologyCode: true,
                id: selectedCode.id,
              },
            };

            reduxDispatch(
              updateCodePathHistory({
                category: selectedCategory,
                code: selectedCode.rawName || "",
                description: morphRaw,
                isFromTabular: false,
                timestamp: new Date().toISOString(),
                id: selectedCode.id,
              }),
            );

            reduxDispatch(updateLocalState(newState));
            pushHistoryState(newState);
            return;
          }

          // Index lookup failed — go to final screen with morph code only
          newState = {
            ...newState,
            lastAddedCode: selectedCode,
            // showFinalScreen: true,
            showError: true,
          };
          finalizeOrManifestation(newState);
          // pushHistoryState(newState);
          // setTimeout(() => {
          //   reduxDispatch(updateLocalState({ showError: false }));
          // }, 3000);
          return;
        }
        // end of morph routing
        const extractSpecialCodes = (value) => {
          if (!value) return [];
          return Array.from(
            new Set(
              value
                // Remove non-printable chars except ASCII and †
                .replace(/[^\x20-\x7E\u2020]/g, " ")
                // Keep letters, digits, dot, -, *, †, spaces
                .replace(/[^\w.\-*†, ]/g, " ")
                // .replace(/[^\w.\-*†,()/]/g, " ")
                .split(/[\s,]+/)
                .map((p) => p.trim())
                .filter(Boolean)
                // Only match valid codes with letters/numbers + optional dot + trailing specials (-, *, †)
                .filter((part) => /^[A-Z]+\d+(\.\d*)?[-*†]+$/i.test(part))
                .flatMap((part) => {
                  const base = part.replace(/[-*†]+$/, ""); // preserve dot in base
                  const specialsMatch = part.match(/[-*†]+$/);
                  const specials = specialsMatch ? specialsMatch[0] : "";

                  const out = new Set();

                  if (specials.includes("-")) out.add(base + "-");
                  if (specials.includes("*")) out.add(base + "*");
                  if (specials.includes("†")) out.add(base + "†");

                  return Array.from(out);
                }),
            ),
          );
        };
        // Check if this selectedCode has special codes in its description
        let newSpecialCodes = [];
        let originalSpecialCodesWithSymbols = [];
        let morphologyCode = null;
        if (
          selectedCode &&
          selectedCode.description &&
          !selectedCode.isFromTabular &&
          (!selectedCode.children || selectedCode.children.length === 0)
        ) {
          // console.log(selectedCode.description, "sdfghfds");
          const morphologyMatch = selectedCode.description.match(
            /^\(?(M\d+\/\d+)\)?\s+([A-Z]\d+[\d.\-*†]*)/i,
          );

          // console.log(morphologyMatch, "morphologyMatch");
          morphologyCode = morphologyMatch ? morphologyMatch[1] : null;
          const safeSelectedCodes =
            typeof localSelectedCodes === "object" &&
            !Array.isArray(localSelectedCodes)
              ? localSelectedCodes
              : {};
          if (morphologyCode) {
            const existingArr = Array.isArray(
              safeSelectedCodes[selectedCategory],
            )
              ? safeSelectedCodes[selectedCategory]
              : [];

            const alreadyExists = existingArr.some(
              (item) => item?.rawName === morphologyCode,
            );

            if (!alreadyExists) {
              const morphologyObj = {
                rawName: morphologyCode,
                rawDescription: `(${morphologyCode})`,
                name: morphologyCode,
                description: `(${morphologyCode})`,
                isFromTabular: false,
                isMorphologyCode: true,
                acs: selectedCode.acs || null,
                nca: selectedCode.nca || null,
                code_first_instruction: null,
                use_additional_instruction: null,
                codealsowhenperf_instruction: null,
                codeto_instruction: null,
                manual_added: null,
                id: `morph-${Date.now()}`, // generate temp id

                lastAddedCode: {
                  rawName: morphologyCode,
                  rawDescription: `(${morphologyCode})`,
                  description: `(${morphologyCode})`,
                  children: [],
                  isFromTabular: false,
                  id: `morph-${Date.now()}`,
                  acs: selectedCode.acs || null,
                  nca: selectedCode.nca || null,
                  code_first_instruction: null,
                  use_additional_instruction: null,
                  codeto_instruction: null,
                  manual_added: null,
                },
              };

              newState.selectedCodes = {
                ...safeSelectedCodes,
                [selectedCategory]: [...existingArr, morphologyObj],
              };
            }
          }

          const newParts = extractSpecialCodes(selectedCode.description);
          if (newParts.length > 0) {
            const orderedNewParts = prioritizeParts(newParts);
            originalSpecialCodesWithSymbols = [...orderedNewParts];
            const trimNewCodes = Array.from(
              new Set(
                orderedNewParts.map((code) => code?.replace(/[-†*]+$/, "")),
              ),
            );
            newSpecialCodes = trimNewCodes;
            // newState.originalSpecialCodesWithSymbols = originalSpecialCodesWithSymbols;
            // MERGE with existing symbols instead of replacing
            newState.originalSpecialCodesWithSymbols = [
              ...(localState.originalSpecialCodesWithSymbols || []),
              ...originalSpecialCodesWithSymbols,
            ].filter((value, index, self) => self.indexOf(value) === index);
          }
        }

        // If we found new special codes, RESET the processing queue with new codes
        if (newSpecialCodes.length > 0) {
  // RESET and REPLACE the specialCodesToProcess with new codes
  newState = {
    ...newState,
    lastAddedCode: selectedCode,
    specialCodesToProcess: newSpecialCodes,
    originalSpecialCodesWithSymbols: newState.originalSpecialCodesWithSymbols,
    processingSpecialCodes: true,
  };

  const currentSpecialCode = newSpecialCodes[0];
  const remainingSpecialCodes = newSpecialCodes.slice(1);

  const extensions = getTabularExtensions(
    currentSpecialCode,
    selectedCategory,
    true,
  );

  //Build the see-also option if the original code has one
  const seeAlsoTerm = extractSeeAlsoTerm(selectedCode.seealso);
  const buildSeeAlsoOption = () =>
    seeAlsoTerm
      ? {
          rawName: `Unspecified (${seeAlsoTerm})`,
          rawDescription: `See also: ${seeAlsoTerm}`,
          name: `Unspecified (${seeAlsoTerm})`,
          description: seeAlsoTerm,
          isSeeAlsoOption: true,
          isUnspecifiedSeeAlso: true,
          option: seeAlsoTerm,
          originalCode: selectedCode,
          referenceId: selectedCode.id,
          category: selectedCategory,
          acs: selectedCode.acs,
          nca: selectedCode.nca,
        }
      : null;

  if (extensions.length > 0) {
    const baseCode = selectedCode.children?.map((item) => ({
      ...item,
      rawDescription: item.description,
      rawName: item.name,
    }));
    let children = [...baseCode, ...extensions];

    //Append see-also option if present
    const seeAlsoOpt = buildSeeAlsoOption();
    if (seeAlsoOpt) {
      const alreadyHas = children.some(
        (c) => c.isSeeAlsoOption && c.option === seeAlsoTerm,
      );
      if (!alreadyHas) children.push(seeAlsoOpt);
    }

    newState = {
      ...newState,
      specialCodesToProcess: remainingSpecialCodes,
      currentCodeSearchData: children,
      filteredCodes: children,
      selectedIndex: null,
      initialSearchDone: true,
      showFinalScreen: false,
      currentSpecialCode: currentSpecialCode,
      selectedParent: selectedCode,
      lastAddedCode: selectedCode,
    };
  } else {
    // No extensions for this code — still surface the see-also option alone
    const seeAlsoOpt = buildSeeAlsoOption();
    const children = seeAlsoOpt ? [seeAlsoOpt] : [];

    newState = {
      ...newState,
      specialCodesToProcess: remainingSpecialCodes,
      currentCodeSearchData: children,
      filteredCodes: children,
      selectedIndex: null,
      initialSearchDone: children.length > 0, 
      showFinalScreen: false,
      selectedParent: selectedCode,
      lastAddedCode: selectedCode,
    };
  }

  reduxDispatch(updateLocalState(newState));
  pushHistoryState(newState);
  return; 
}

        if (
          localState.processingSpecialCodes &&
          selectedCode.isFromTabular &&
          localState.pendingInstructions.length === 0
        ) {
          newState = {
            ...newState,
            lastAddedCode: selectedCode, // This is crucial for "No or already coded"
            selectedParent: selectedCode,
          };
          const childExtensions = getTabularExtensions(
            selectedCode.rawName,
            selectedCategory,
            true,
          );
          const isLeafNode = !childExtensions || childExtensions.length === 0;

          if (isLeafNode) {
            const existing = localSelectedCodes[selectedCategory] || [];
            const codeToAdd = {
              rawName: selectedCode.rawName,
              rawDescription: selectedCode.rawDescription || selectedCode.name,
              isFromTabular: true,
              acs: selectedCode.acs,
              nca: selectedCode.nca,
              code_first_instruction: selectedCode.code_first_instruction,
              use_additional_instruction:
                selectedCode.use_additional_instruction,
              codealsowhenperf_instruction:
                selectedCode.codealsowhenperf_instruction,
              codeto_instruction: selectedCode.codeto_instruction,
              manual_added: selectedCode.manual_added,
              lastAddedCode: selectedCode,
              id: selectedCode.id,
            };

            // Check if this code is already added to avoid duplicates
            // const isAlreadyAdded = existing.some(
            //   (item) =>
            //     item.rawName === codeToAdd.rawName &&
            //     item.rawDescription === codeToAdd.rawDescription,
            // );
            const isAlreadyAdded =
              selectedCategory === "Procedure Codes"
                ? isCodeInAchiList(codeToAdd) &&
                  existing.some((item) => item.rawName === codeToAdd.rawName)
                : existing.some(
                    (item) =>
                      item.rawName === codeToAdd.rawName &&
                      item.rawDescription === codeToAdd.rawDescription,
                  );
            const updatedSelectedCodes = {
              ...localSelectedCodes,
              [selectedCategory]: isAlreadyAdded
                ? existing
                : [...existing, codeToAdd],
            };

            // Check if there are more special codes to process
            const remainingCodes = localState.specialCodesToProcess || [];

            if (remainingCodes.length > 0) {
              // Process next special code

              const currentSpecialCode = remainingCodes[0];
              const remainingSpecialCodes = remainingCodes.slice(1);

              // Get tabular extensions for the next special code using existing system
              const extensions = getTabularExtensions(
                currentSpecialCode,
                selectedCategory,
                true,
              );

              if (extensions && extensions.length > 0) {
                // Show the extension children for the next special code - let existing system handle display
                newState = {
                  ...newState,
                  selectedCodes: updatedSelectedCodes,
                  lastAddedCode: selectedCode,
                  processingSpecialCodes: true,
                  specialCodesToProcess: remainingSpecialCodes,
                  // CRITICAL: Preserve symbols from localState
                  originalSpecialCodesWithSymbols:
                    localState.originalSpecialCodesWithSymbols || [],
                  currentCodeSearchData: extensions,
                  filteredCodes: extensions,
                  selectedIndex: null,
                  initialSearchDone: true,
                  showFinalScreen: false,
                  originalCode: normalizeCodeObject(selectedCode),
                  currentSpecialCode: currentSpecialCode,
                  selectedParent: selectedCode,
                };

                reduxDispatch(updateLocalState(newState));
                pushHistoryState(newState);
                return;
              } else {
                // No extensions found for next code - it might be a leaf node itself
                // Add the current special code itself as it has no children
                const finalCodeToAdd = {
                  rawName: currentSpecialCode,
                  rawDescription: currentSpecialCode, // Use code as description if no description available
                  isFromTabular: true,
                  acs: null,
                  nca: null,
                  code_first_instruction: null,
                  use_additional_instruction: null,
                  codealsowhenperf_instruction: null,
                  codeto_instruction: null,
                  manual_added: null,
                };

                const finalSelectedCodes = {
                  ...updatedSelectedCodes,
                  [selectedCategory]: [
                    ...updatedSelectedCodes[selectedCategory],
                    finalCodeToAdd,
                  ],
                };

                if (remainingSpecialCodes.length > 0) {
                  // Continue with remaining special codes
                  newState = {
                    ...newState,
                    selectedCodes: finalSelectedCodes,
                    lastAddedCode: finalCodeToAdd,
                    processingSpecialCodes: true,
                    specialCodesToProcess: remainingSpecialCodes,
                    //  originalSpecialCodesWithSymbols: remainingSpecialCodes, // Preserve symbols
                    currentCodeSearchData: null,
                    filteredCodes: [],
                    selectedIndex: null,
                    initialSearchDone: false,
                    showFinalScreen: false,
                    selectedParent: selectedCode,
                  };

                  reduxDispatch(updateLocalState(newState));
                  pushHistoryState(newState);
                  return;
                } else {
                  // No more special codes
                  newState = {
                    ...newState,
                    selectedCodes: finalSelectedCodes,
                    lastAddedCode: selectedCode,
                    // showFinalScreen: true,
                    processingSpecialCodes: false,
                    specialCodesToProcess: [],
                    // originalSpecialCodesWithSymbols: [], // Clear symbols
                    currentSpecialCodeIndex: 0,
                    selectedParent: selectedCode,
                  };
                  finalizeOrManifestation(newState);
                  return;
                }
              }
            } else {
              // No more special codes to process
              newState = {
                ...newState,
                selectedCodes: updatedSelectedCodes,
                lastAddedCode: selectedCode,
                // showFinalScreen: true,
                processingSpecialCodes: false,
                // originalSpecialCodesWithSymbols: [], // Clear symbols
                specialCodesToProcess: [],
                currentSpecialCodeIndex: 0,
                selectedParent: selectedCode,
              };

              finalizeOrManifestation(newState);
              return;
            }
          } else {
            const existing = localSelectedCodes[selectedCategory] || [];
            const codeToAdd = {
              rawName: selectedCode.rawName,
              rawDescription: selectedCode.rawDescription || selectedCode.name,
              isFromTabular: true,
              acs: selectedCode.acs,
              nca: selectedCode.nca,
              code_first_instruction: selectedCode.code_first_instruction,
              use_additional_instruction:
                selectedCode.use_additional_instruction,
              codealsowhenperf_instruction:
                selectedCode.codealsowhenperf_instruction,
              codeto_instruction: selectedCode.codeto_instruction,
              manual_added: selectedCode.manual_added,
              lastAddedCode: selectedCode,
              id: selectedCode.id,
            };

            // Check if this code is already added to avoid duplicates
            // const isAlreadyAdded = existing.some(
            //   (item) =>
            //     item.rawName === codeToAdd.rawName &&
            //     item.rawDescription === codeToAdd.rawDescription,
            // );
            const isAlreadyAdded =
              selectedCategory === "Procedure Codes"
                ? isCodeInAchiList(codeToAdd) &&
                  existing.some((item) => item.rawName === codeToAdd.rawName)
                : existing.some(
                    (item) =>
                      item.rawName === codeToAdd.rawName &&
                      item.rawDescription === codeToAdd.rawDescription,
                  );

            let updatedSelectedCodes = {
              ...localSelectedCodes,
              [selectedCategory]: isAlreadyAdded
                ? existing
                : [...existing, codeToAdd],
            };

            const remainingCodes = localState.specialCodesToProcess || [];

            // Add morphology code AFTER creating updatedSelectedCodes
            // But BEFORE creating finalSelectedCodes
            const morphologyCode =
              newState.pendingMorphologyFromHyphen &&
              isSelectedCodeInHyphenCodes;
            if (morphologyCode) {
              const existingMorph =
                updatedSelectedCodes[selectedCategory] || [];
              const morphAlreadyExists = existingMorph.some(
                (code) =>
                  code.rawName === morphologyCode ||
                  code.rawDescription === morphologyCode,
              );

              if (!morphAlreadyExists) {
                const morphologyCodeObj = {
                  rawName: morphologyCode,
                  rawDescription: morphologyCode,
                  name: morphologyCode,
                  description: morphologyCode,
                  isFromTabular: false,
                  isMorphologyCode: true,
                  acs: null,
                  nca: null,
                  code_first_instruction: null,
                  use_additional_instruction: null,
                  codealsowhenperf_instruction: null,
                  codeto_instruction: null,
                  manual_added: null,
                  id: `morph-${Date.now()}`,
                };

                updatedSelectedCodes = {
                  ...updatedSelectedCodes,
                  [selectedCategory]: [
                    ...updatedSelectedCodes[selectedCategory],
                    morphologyCodeObj,
                  ],
                };
              }

              // Clear the pending morphology
              newState.pendingMorphologyFromHyphen = null;
            }

            if (selectedCode?.children?.length === 0) {
              const existing = localSelectedCodes[selectedCategory] || [];
              const codeToAdd = {
                rawName: selectedCode.rawName,
                rawDescription:
                  selectedCode.rawDescription || selectedCode.name,
                isFromTabular: true,
                acs: selectedCode.acs,
                nca: selectedCode.nca,
                code_first_instruction: selectedCode.code_first_instruction,
                use_additional_instruction:
                  selectedCode.use_additional_instruction,
                codealsowhenperf_instruction:
                  selectedCode.codealsowhenperf_instruction,
                codeto_instruction: selectedCode.codeto_instruction,
                manual_added: selectedCode.manual_added,
                lastAddedCode: selectedCode,
                id: selectedCode.id,
              };

              // const isAlreadyAdded = existing.some(
              //   (item) =>
              //     item.rawName === codeToAdd.rawName &&
              //     item.rawDescription === codeToAdd.rawDescription,
              // );
              const isAlreadyAdded =
                selectedCategory === "Procedure Codes"
                  ? false
                  : existing.some(
                      (item) =>
                        item.rawName === codeToAdd.rawName &&
                        item.rawDescription === codeToAdd.rawDescription,
                    );
              let updatedSelectedCodesInner = {
                ...localSelectedCodes,
                [selectedCategory]: isAlreadyAdded
                  ? existing
                  : [...existing, codeToAdd],
              };

              // Also add morphology to inner updatedSelectedCodesInner
              if (morphologyCode) {
                const existingMorphInner =
                  updatedSelectedCodesInner[selectedCategory] || [];
                const morphAlreadyExistsInner = existingMorphInner.some(
                  (code) =>
                    code.rawName === morphologyCode ||
                    code.rawDescription === morphologyCode,
                );

                if (!morphAlreadyExistsInner) {
                  const morphologyCodeObj = {
                    rawName: morphologyCode,
                    rawDescription: morphologyCode,
                    name: morphologyCode,
                    description: morphologyCode,
                    isFromTabular: false,
                    isMorphologyCode: true,
                    acs: null,
                    nca: null,
                    code_first_instruction: null,
                    use_additional_instruction: null,
                    codealsowhenperf_instruction: null,
                    codeto_instruction: null,
                    manual_added: null,
                    id: `morph-${Date.now()}`,
                  };

                  updatedSelectedCodesInner = {
                    ...updatedSelectedCodesInner,
                    [selectedCategory]: [
                      ...updatedSelectedCodesInner[selectedCategory],
                      morphologyCodeObj,
                    ],
                  };
                }
              }

              const remainingCodes = localState.specialCodesToProcess || [];

              if (
                remainingCodes.length > 0 &&
                localState.pendingInstructions.length == 0
              ) {
                const currentSpecialCode = remainingCodes[0];
                const remainingSpecialCodes = remainingCodes.slice(1);
                const extensions = getTabularExtensions(
                  currentSpecialCode,
                  selectedCategory,
                  true,
                );
                if (extensions && extensions.length > 0) {
                  newState = {
                    ...newState,
                    selectedCodes: updatedSelectedCodesInner, // Use the one with morphology
                    lastAddedCode: selectedCode,
                    processingSpecialCodes: true,
                    specialCodesToProcess: remainingSpecialCodes,
                    currentCodeSearchData: extensions,
                    filteredCodes: extensions,
                    selectedIndex: null,
                    initialSearchDone: true,
                    showFinalScreen: false,
                    originalCode: normalizeCodeObject(selectedCode),
                    currentSpecialCode: currentSpecialCode,
                    selectedParent: selectedCode,
                  };
                  reduxDispatch(updateLocalState(newState));
                  pushHistoryState(newState);
                  return;
                } else {
                  const finalCodeToAdd = {
                    rawName: currentSpecialCode,
                    rawDescription: currentSpecialCode,
                    isFromTabular: true,
                    acs: null,
                    nca: null,
                    code_first_instruction: null,
                    use_additional_instruction: null,
                    codealsowhenperf_instruction: null,
                    codeto_instruction: null,
                    manual_added: null,
                  };

                  const finalSelectedCodes = {
                    ...updatedSelectedCodesInner, // Use the one with morphology
                    [selectedCategory]: [
                      ...updatedSelectedCodesInner[selectedCategory],
                      finalCodeToAdd,
                    ],
                  };

                  if (remainingSpecialCodes.length > 0) {
                    newState = {
                      ...newState,
                      selectedCodes: finalSelectedCodes,
                      lastAddedCode: finalCodeToAdd,
                      processingSpecialCodes: true,
                      specialCodesToProcess: remainingSpecialCodes,
                      currentCodeSearchData: null,
                      filteredCodes: [],
                      selectedIndex: null,
                      initialSearchDone: false,
                      showFinalScreen: false,
                      selectedParent: selectedCode,
                    };
                    reduxDispatch(updateLocalState(newState));
                    pushHistoryState(newState);
                    return;
                  } else {
                    // make sure morphology is included
                    newState = {
                      ...newState,
                      selectedCodes: finalSelectedCodes, // Already includes morphology
                      lastAddedCode: selectedCode,
                      // showFinalScreen: true,
                      processingSpecialCodes: false,
                      specialCodesToProcess: [],
                      currentSpecialCodeIndex: 0,
                      selectedParent: selectedCode,
                    };
                    finalizeOrManifestation(newState);
                    return;
                  }
                }
              } else if (localState.pendingInstructions.length > 0) {
                processNextInstruction(newState.pendingInstructions);
              } else {
                newState = {
                  ...newState,
                  selectedCodes: updatedSelectedCodesInner,
                  lastAddedCode: selectedCode,
                  // showFinalScreen: true,
                  processingSpecialCodes: false,
                  specialCodesToProcess: [],
                  currentSpecialCodeIndex: 0,
                  selectedParent: selectedCode,
                };
                finalizeOrManifestation(newState);
                return;
              }
            } else {
            }
          }
        }

        //----------------------------------------------------------------------------------------------------------------------
        // All special cases handled (-, ?, *)

        let parts = [];
        let extractedMorphologyCode = null;
        if (
          selectedCode &&
          selectedCode.description &&
          !selectedCode.isFromTabular &&
          (!selectedCode.children || selectedCode.children.length === 0)
        ) {
          parts = extractSpecialCodes(selectedCode.description);
          // Extract morphology code (e.g., M8000/3, M8220/0, etc.)
          const morphologyMatch =
            selectedCode.description.match(/\(?(M\d+\/\d+)\)?/i);
          if (morphologyMatch) {
            extractedMorphologyCode = morphologyMatch[1];
            newState.pendingMorphologyFromHyphen = extractedMorphologyCode;
          }
        }

        // To order the special codes----
        const orderedParts = prioritizeParts(parts);

        let trimSpecialCodes = Array.from(
          new Set(orderedParts.map((code) => code?.replace(/[-†*]+$/, ""))),
        );

        if (trimSpecialCodes.length > 0 && !localState.processingSpecialCodes) {
          // Start with the first special code
          const currentSpecialCode = trimSpecialCodes[0];
          const remainingSpecialCodes = trimSpecialCodes.slice(1);

          // Get tabular extensions for the current special code
          const extensions = getTabularExtensions(
            currentSpecialCode,
            selectedCategory,
            true,
          );

          if (extensions.length > 0) {
            const baseCode = selectedCode.children?.map((item) => ({
              ...item,
              rawDescription: item.description,
              rawName: item.name,
            }));
            newState.allHyphenCodesWithPendingMorphology = extensions; // Store
            const children = [...baseCode, ...extensions];

            // Update state to show extension children
            newState = {
              ...newState,
              selectedParent: selectedCode,
              lastAddedCode: selectedCode,
              processingSpecialCodes: true,
              specialCodesToProcess: remainingSpecialCodes,
              currentSpecialCodeIndex: 0,
              currentCodeSearchData: children,
              filteredCodes: children,
              selectedIndex: null,
              initialSearchDone: true,
              showFinalScreen: false,
              originalCode: normalizeCodeObject(selectedCode),
              // Store the current special code being processed
              currentSpecialCode: currentSpecialCode,
            };

            reduxDispatch(updateLocalState(newState));
            pushHistoryState(newState);

            // RETURN EARLY - wait for user to select an extension
            return;
          } else {
            // No extensions found for this code, move to next special code

            if (remainingSpecialCodes.length > 0) {
              // Process next special code

              newState = {
                ...newState,
                processingSpecialCodes: true,
                specialCodesToProcess: remainingSpecialCodes,
                currentSpecialCodeIndex: 0,
                showFinalScreen: false,
                selectedParent: selectedCode,
              };

              reduxDispatch(updateLocalState(newState));
              pushHistoryState(newState);

              // This will trigger handleContinue again with the updated state
              return;
            } else {
              // No more special codes to process
              newState = {
                ...newState,
                // showFinalScreen: true,
                processingSpecialCodes: false,
                specialCodesToProcess: [],
                currentSpecialCodeIndex: 0,
                selectedParent: selectedCode,
                lastAddedCode: selectedCode,
              };
              finalizeOrManifestation(newState);
              return;
            }
          }
        }
        const hyphenResult = processHyphenCode(selectedCode, selectedCategory);
        if (hyphenResult) {
          newState = {
            ...newState,
            selectedParent: hyphenResult.parent,
            currentCodeSearchData: hyphenResult.filteredCodes,
            filteredCodes: hyphenResult.filteredCodes,
            selectedIndex: null,
            initialSearchDone: true,
            showFinalScreen: false,
            lastAddedCode: selectedCode,
            originalCode: normalizeCodeObject(selectedCode),
          };
          reduxDispatch(updateLocalState(newState));
          reduxDispatch(clearWarningMessage());
          pushHistoryState(newState);
          return;
        }
        // Table of durgs and chemicals static for cluster Code - Nov 17 2025

        // 🔥 Handle both special codes here
        if (
          selectedCode.id === "257910" ||
          selectedCode.id === "348284" ||
          (selectedCode.parentId === "348284" && selectedCode._type === "child")
        ) {
          let children = resolveSeePath(
            ["Table of drugs and chemicals"],
            selectedCategory,
          );

          if (children?.length > 0) {
            // Remove chapter 19 + store info
            const updatedChildren = children;

            newState = {
              ...newState,
              selectedParent: selectedCode,
              currentCodeSearchData: updatedChildren,
              filteredCodes: updatedChildren,
              selectedIndex: null,
              initialSearchDone: true,
              showFinalScreen: false,
              originalCode: normalizeCodeObject(selectedCode),
            };

            reduxDispatch(updateLocalState(newState));
            pushHistoryState(newState);
            return;
          }
        }

        //  INSERT SEE LOGIC HERE — before final screen check
        // -----------------------------------------------------------------
        // PRIORITY: SEE always takes precedence over description
        if (
          selectedCode &&
          selectedCode.see &&
          selectedCode.see.trim() &&
          selectedCode._type !== "child"
        ) {
          const term = selectedCode.see.trim();
          const originalCode = selectedCode || {};
          const parsedPathParts = parseSeeTerm(term);
          // keep final key name as pathParts
          const pathParts =
            parsedPathParts?.length > 1 &&
            parsedPathParts[parsedPathParts.length - 1]?.toLowerCase() ===
              "malignant"
              ? parsedPathParts.slice(0, -1)
              : parsedPathParts;
          if (
            term.toLowerCase() === "table of drugs and chemicals" ||
            pathParts.join(" ").toLowerCase() === "table of drugs and chemicals"
          ) {
            let children = resolveSeePath(
              ["Table of drugs and chemicals"],
              selectedCategory,
            );

            if (children?.length > 0) {
              const updatedChildren = children;
              //  ALSO CHECK AFTER removeChapter19AndStore

              newState = {
                ...newState,
                selectedParent: originalCode,
                currentCodeSearchData: updatedChildren,
                filteredCodes: updatedChildren,
                selectedIndex: null,
                initialSearchDone: true,
                showFinalScreen: false,
                originalCode: normalizeCodeObject(originalCode),
              };

              reduxDispatch(updateLocalState(newState));
              pushHistoryState(newState);
              return;
            }
          }
          const resolveResult = resolveSeePath(
            pathParts,
            newState.selectedCategory,
          );

          //  If SEE path resolves successfully → proceed with that
          // if (resolveResult && resolveResult.length > 0) {
          //   newState = {
          //     ...newState,
          //     selectedParent: originalCode,
          //     currentCodeSearchData: resolveResult,
          //     filteredCodes: resolveResult,
          //     selectedIndex: null,
          //     initialSearchDone: true,
          //     showFinalScreen: false,
          //     originalCode: normalizeCodeObject(originalCode),
          //   };
          //   reduxDispatch(updateLocalState(newState));
          //   pushHistoryState(newState);
          //   return; //  stop here, don’t check description or go final
          // }
          if (resolveResult && resolveResult.length > 0) {
            // ── ABBREVIATION MERGE LOGIC ──
            let mergedFilteredCodes = resolveResult; // default: normal flow

            const isAbbreviationMatch =
              selectedCode?._matchedVia === "abbreviation" &&
              Array.isArray(selectedCode?.children) &&
              selectedCode.children.length > 0;

            if (isAbbreviationMatch) {
              const originalChildren = selectedCode.children
                .filter((c) => c && c.name?.trim())
                .map((c) => ({
                  rawName: c.name,
                  rawDescription: c.description ?? c.name,
                  name: c.name,
                  description: c.description ?? c.name,
                  children: c.children || [],
                  isFromTabular: false,
                  see: c.see,
                  seealso: c.seealso,
                  isDescriptionNull: c.description === null,
                  id: c.id,
                  acs: c.acs,
                  code_first_instruction: c.code_first_instruction,
                  use_additional_instruction: c.use_additional_instruction,
                  codealsowhenperf_instruction: c.codealsowhenperf_instruction,
                  codeto_instruction: c.codeto_instruction,
                  manual_added: c.manual_added,
                }));

              // console.log("[SeeAlso] originalChildren:", originalChildren);
              // console.log(
              //   "[SeeAlso] resolveResult before flatten:",
              //   resolveResult,
              // );

              // ✅ Flatten SEE results — first level only
              // If item has no description + no see + no seealso + has children → inline its children
              const flattenedSeeResults = [];
              for (const item of resolveResult) {
                const hasDescription =
                  item.description && item.description.trim();
                const hasSee = item.see && item.see.trim();
                const hasSeeAlso = item.seealso && item.seealso.trim();
                const hasChildren =
                  Array.isArray(item.children) && item.children.length > 0;
                const shouldFlatten =
                  !hasDescription && !hasSee && !hasSeeAlso && hasChildren;

                if (shouldFlatten) {
                  // console.log(
                  //   "[SeeAlso] flattening item:",
                  //   item.name,
                  //   "→ inlining",
                  //   item.children.length,
                  //   "children",
                  // );
                  const mappedChildren = item.children
                    .filter((c) => c && c.name?.trim())
                    .map((c) => ({
                      rawName: c.name,
                      rawDescription: c.description ?? c.name,
                      name: c.name,
                      description: c.description ?? c.name,
                      children: c.children || [],
                      isFromTabular: item.isFromTabular ?? false,
                      see: c.see,
                      seealso: c.seealso,
                      isDescriptionNull: c.description === null,
                      id: c.id,
                      acs: c.acs,
                      code_first_instruction: c.code_first_instruction,
                      use_additional_instruction: c.use_additional_instruction,
                      codealsowhenperf_instruction:
                        c.codealsowhenperf_instruction,
                      codeto_instruction: c.codeto_instruction,
                      manual_added: c.manual_added,
                    }));
                  flattenedSeeResults.push(...mappedChildren);
                } else {
                  // console.log("[SeeAlso] keeping item as-is:", item.name);
                  flattenedSeeResults.push(item);
                }
              }

              // console.log(
              //   "[SeeAlso] flattenedSeeResults:",
              //   flattenedSeeResults,
              // );

              //  originalChildren first, flattened SEE results after
              mergedFilteredCodes = [
                ...originalChildren,
                ...flattenedSeeResults,
              ];

              // console.log("[SeeAlso] merged result:", mergedFilteredCodes);

              // AFTER
              const merged = [...originalChildren, ...flattenedSeeResults];
              const seen = new Set();
              mergedFilteredCodes = merged.filter((item) => {
                const key = `${(item.name ?? "").trim().toLowerCase()} && ${(item.description ?? "").trim().toLowerCase()}`;
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
              });
              // console.log(
              //   "[SeeAlso] merged result after dedup:",
              //   mergedFilteredCodes,
              // );
            } else {
              //  Normal flow — resolveResult as-is, no flattening
              // console.log(
              //   "[SeeAlso] NOT abbreviation — normal flow, resolveResult only",
              // );
            }
            // ── END ABBREVIATION MERGE ──

            newState = {
              ...newState,
              selectedParent: originalCode,
              currentCodeSearchData: mergedFilteredCodes,
              filteredCodes: mergedFilteredCodes,
              selectedIndex: null,
              initialSearchDone: true,
              showFinalScreen: false,
              originalCode: normalizeCodeObject(originalCode),
              carryDirectCodeWithoutInstructions: true,
            };

            reduxDispatch(updateLocalState(newState));
            pushHistoryState(newState);
            return;
          }
          //  If SEE path fails → show error but still don’t use description
          newState = {
            ...newState,
            lastAddedCode: originalCode,
            // showFinalScreen: true,
            showError: true,
          };
          finalizeOrManifestation(newState);
          // setTimeout(() => {
          //   reduxDispatch(
          //     updateLocalState({ showError: false, lastAddedCode: null }),
          //   );
          // }, 3000);
          return;
        }
        // -----------------------------------------------------------------
        // Continue with normal processing if no special codes
        // ... rest of your existing normal processing logic
        // If no instructions, process children normally
        // if (
        //   (selectedCode.children && selectedCode.children.length > 0) ||
        //   (selectedCode.subcodes && selectedCode.subcodes.length > 0)
        // ) {
        //   const children = selectedCode.subcodes || selectedCode.children || [];
        //   const validChildren = children
        //     .filter((child) => child && child.name?.trim())
        //     .map((child) => ({
        //       rawName: child.name,
        //       rawDescription:
        //         child.description ||
        //         (child.description === null ? null : child.name),
        //       name: child.name,
        //       description:
        //         child.description ||
        //         (child.description === null ? null : child.name),
        //       children: child.subcodes || child.children || [],
        //       isFromTabular: selectedCode.isFromTabular || false,
        //       Next_parent_code: child.Next_parent_code,
        //       Warning_message: child.Warning_message,
        //       see: child.see,
        //       seealso: child.seealso,
        //       isDescriptionNull: child.description === null,
        //       id: child.id,
        //       acs: child.acs,
        //       code_first_instruction:
        //         selectedCode.isFromTabular && child.children
        //           ? null
        //           : child.code_first_instruction,
        //       use_additional_instruction:
        //         selectedCode.isFromTabular && child.children
        //           ? null
        //           : child.use_additional_instruction,
        //       codealsowhenperf_instruction: child.codealsowhenperf_instruction,
        //     }));

        //   let newFilteredCodes = [...validChildren];
        //   let canAddUnspecified = false;
        //   let unspecifiedCode = null;
        //   if (selectedCode._type === "child") {
        //     // Parent info is already attached in child object
        //     const parentName = selectedCode.parentName;
        //     const parentDescription = selectedCode.parentDescription;
        //     const parentChildren = selectedCode.children || []; // <-- children of the parent
        //     // Only add unspecified if parent has valid name, description, and children
        //     if (
        //       parentName?.trim() &&
        //       parentDescription?.trim() &&
        //       parentChildren.length > 0 &&
        //       !selectedCode.isFromTabular
        //     ) {
        //       canAddUnspecified = true;
        //       unspecifiedCode = {
        //         rawName: parentName,
        //         rawDescription: parentDescription,
        //         name: parentDescription || parentName,
        //         description: parentDescription,
        //         children: [], // unspecified option has no children
        //         isUnspecified: true,
        //         isFromTabular: selectedCode.isFromTabular || false,
        //         Next_parent_code: selectedCode.Next_parent_code,
        //         Warning_message: selectedCode.Warning_message,
        //         id: selectedCode.parentId,
        //         acs: selectedCode.parentAcs,
        //         code_first_instruction: selectedCode.code_first_instruction,
        //         use_additional_instruction:
        //           selectedCode.use_additional_instruction,
        //         codealsowhenperf_instruction:
        //           selectedCode.codealsowhenperf_instruction,
        //       };
        //     }
        //   } else {
        //     // Parent selection: keep existing logic
        //     canAddUnspecified =
        //       selectedCode.rawName?.trim() &&
        //       selectedCode.description?.trim() &&
        //       selectedCode.description !== null &&
        //       !selectedCode.isFromTabular;
        //     if (canAddUnspecified) {
        //       unspecifiedCode = {
        //         rawName: selectedCode.rawName,
        //         rawDescription: selectedCode.description,
        //         name: selectedCode.description || selectedCode.rawName,
        //         description: selectedCode.description,
        //         children: [],
        //         isUnspecified: true,
        //         isFromTabular: selectedCode.isFromTabular || false,
        //         Next_parent_code: selectedCode.Next_parent_code,
        //         Warning_message: selectedCode.Warning_message,
        //         id: selectedCode.id,
        //         acs: selectedCode.acs,
        //         code_first_instruction: selectedCode.code_first_instruction,
        //         use_additional_instruction:
        //           selectedCode.use_additional_instruction,
        //         codealsowhenperf_instruction:
        //           selectedCode.codealsowhenperf_instruction,
        //       };
        //     }
        //   }
        //   //see and see also condition
        //   // Push to filtered options if valid

        //   // --- SEE ALSO: preserve unspecified & children — append appropriately ---
        //   const hasSeeAlso =
        //     selectedCode.seealso && selectedCode.seealso.trim();

        //   if (hasSeeAlso) {
        //     const seeAlsoTerm = selectedCode.seealso.trim();

        //     const seeAlsoOption = {
        //       rawName: `${seeAlsoTerm}`,
        //       rawDescription: "Navigate to referenced codes",
        //       isSeeAlsoOption: true,
        //       option: seeAlsoTerm,
        //       originalCode: selectedCode,
        //       acs: selectedCode.acs,
        //       referenceId: selectedCode.id,
        //       category: selectedCategory.category,
        //     };

        //     // If we already have children or unspecified in newFilteredCodes -> just append See Also
        //     if (newFilteredCodes && newFilteredCodes.length > 0) {
        //       // avoid duplicate seeAlso
        //       const alreadyHas = newFilteredCodes.some(
        //         (c) => c.isSeeAlsoOption && c.option === seeAlsoTerm,
        //       );
        //       if (!alreadyHas) newFilteredCodes.push(seeAlsoOption);
        //     } else {
        //       // No children and no unspecified present.
        //       // Show both: direct selectable code (so user can go final) AND See Also option.

        //       const directOption = {
        //         rawName: selectedCode.rawName,
        //         rawDescription: selectedCode.description,
        //         name: selectedCode.description || selectedCode.rawName,
        //         description: selectedCode.description,
        //         isDirectCode: true, // indicator for downstream final-screen flow
        //         acs: selectedCode.acs,
        //         id: selectedCode.id,
        //         code_first_instruction: selectedCode.code_first_instruction,
        //         use_additional_instruction:
        //           selectedCode.use_additional_instruction,
        //         codealsowhenperf_instruction:
        //           selectedCode.codealsowhenperf_instruction, // Include this
        //       };

        //       newFilteredCodes = [directOption, seeAlsoOption];
        //     }
        //   }

        //   if (
        //     canAddUnspecified &&
        //     unspecifiedCode &&
        //     !selectedCode.isFromTabular
        //   ) {
        //     newFilteredCodes.push(unspecifiedCode);
        //   }

        //   if (
        //     !validChildren.length &&
        //     !canAddUnspecified &&
        //     newState.pendingInstructions.length === 0
        //   ) {
        //     newState = {
        //       ...newState,
        //       lastAddedCode: selectedCode,
        //       showFinalScreen: true,
        //       showError: true,
        //       selectedParent: selectedCode,
        //     };
        //     reduxDispatch(updateLocalState(newState));
        //     pushHistoryState(newState);
        //     setTimeout(() => {
        //       reduxDispatch(
        //         updateLocalState({ showError: false, lastAddedCode: null }),
        //       );
        //     }, 3000);
        //     return;
        //   }

        //   newState = {
        //     ...newState,
        //     selectedParent: selectedCode,
        //     currentCodeSearchData: newFilteredCodes,
        //     filteredCodes: newFilteredCodes,
        //     selectedIndex: null,
        //     initialSearchDone: true,
        //     showFinalScreen: false,
        //     originalCode: normalizeCodeObject(selectedCode),
        //   };

        //   reduxDispatch(updateLocalState(newState));
        //   pushHistoryState(newState);
        //   return;
        // }

        // // Handle see/see also
        //  Handle SEE ALSO option clicks
        // if (selectedCode && selectedCode.isSeeAlsoOption) {
        //   const term = selectedCode.option.trim();
        //   const parsedPathParts = parseSeeTerm(term);
        //   const pathParts =
        //     parsedPathParts?.length > 1 &&
        //       parsedPathParts[parsedPathParts.length - 1]?.toLowerCase() === "malignant"
        //       ? parsedPathParts.slice(0, -1)
        //       : parsedPathParts;
        //   // ⭐ SPECIAL CASE for SEE ALSO → Table of drugs (same logic as SEE)
        //   if (
        //     term.toLowerCase() === "table of drugs and chemicals" ||
        //     pathParts.join(" ").toLowerCase() === "table of drugs and chemicals"
        //   ) {
        //     let children = resolveSeePath(
        //       ["Table of drugs and chemicals"],
        //       newState.selectedCategory,
        //     );

        //     if (children?.length > 0) {
        //       const updatedChildren = removeChapter19AndStore(children);

        //       newState = {
        //         ...newState,
        //         selectedParent: effectiveSelectedCode.originalCode,
        //         currentCodeSearchData: updatedChildren,
        //         filteredCodes: updatedChildren,
        //         selectedIndex: null,
        //         initialSearchDone: true,
        //         showFinalScreen: false,
        //         originalCode: normalizeCodeObject(
        //           effectiveSelectedCode.originalCode,
        //         ),
        //       };

        //       reduxDispatch(updateLocalState(newState));
        //       pushHistoryState(newState);
        //       return;
        //     }
        //   }

        //   const resolveResult = resolveSeePath(
        //     pathParts,
        //     newState.selectedCategory,
        //   );
        //   if (resolveResult && resolveResult.length > 0) {
        //     // 🟣 Ensure correct id is preserved for see also

        //     newState = {
        //       ...newState,
        //       selectedParent: effectiveSelectedCode.originalCode, // keep link to parent
        //       currentCodeSearchData: resolveResult,
        //       filteredCodes: resolveResult,
        //       selectedIndex: null,
        //       initialSearchDone: true,
        //       showFinalScreen: false,
        //       originalCode: normalizeCodeObject(
        //         effectiveSelectedCode.originalCode,
        //       ),
        //     };

        //     reduxDispatch(updateLocalState(newState));
        //     pushHistoryState(newState);
        //     return; // stop here — handled
        //   }

        //   newState = {
        //     ...newState,
        //     lastAddedCode: selectedCode.originalCode,
        //     showFinalScreen: true,
        //     showError: true,
        //   };
        //   reduxDispatch(updateLocalState(newState));
        //   pushHistoryState(newState);
        //   setTimeout(() => {
        //     reduxDispatch(
        //       updateLocalState({ showError: false, lastAddedCode: null }),
        //     );
        //   }, 3000);
        //   return;
        // }
        // ====================== HANDLE "Unspecified" (Renamed See Also) CLICK ======================
        if (
          selectedCategory === "Procedure Codes" &&
          isInvalidAchiCode(selectedCode) &&
          (!selectedCode.children || selectedCode.children.length === 0) &&
          (!selectedCode.subcodes || selectedCode.subcodes.length === 0)
        ) {
          const match = (
            selectedCode.rawDescription ||
            selectedCode.description ||
            ""
          ).match(/^([^\[]+)\s+\[(\d+)\]\s*$/);
          if (match) {
            const codePrefix = match[1].trim();
            const blockNumber = match[2];

            // console.log(
            //   `Invalid ACHI code finalized: "${codePrefix}" - showing tabular codes from block ${blockNumber}`,
            // );

            const matchingCodes = findCodesWithPrefixInBlock(
              codePrefix,
              blockNumber,
              selectedCategory,
            );

            if (matchingCodes && matchingCodes.length > 0) {
              newState = {
                ...newState,
                selectedParent: selectedCode,
                currentCodeSearchData: matchingCodes,
                filteredCodes: matchingCodes,
                selectedIndex: null,
                initialSearchDone: true,
                showFinalScreen: false,
                lastAddedCode: selectedCode,
                originalCode: normalizeCodeObject(selectedCode),
                // currentWarningMessage: `Select a code starting with "${codePrefix}" from block ${blockNuSmber}`,
              };

              reduxDispatch(updateLocalState(newState));
              pushHistoryState(newState);
              return;
            }
          }
        }

        const hasSeeAlso = !!selectedCode.seealso?.trim();

        // CASE 1: HAS CHILDREN
        if (
          (selectedCode.children && selectedCode.children.length > 0) ||
          (selectedCode.subcodes && selectedCode.subcodes.length > 0)
        ) {
          const children = selectedCode.subcodes || selectedCode.children || [];
          const validChildren = children
            .filter((child) => child && child.name?.trim())
            .map((child) => ({
              rawName: child.name,
              rawDescription:
                child.description ||
                (child.description === null ? null : child.name),
              name: child.name,
              description:
                child.description ||
                (child.description === null ? null : child.name),
              children: child.subcodes || child.children || [],
              isFromTabular: selectedCode.isFromTabular || false,
              Next_parent_code: child.Next_parent_code,
              Warning_message: child.Warning_message,
              see: child.see,
              seealso: child.seealso,
              isDescriptionNull: child.description === null,
              id: child.id,
              acs: child.acs,
              nca: child.nca,
              code_first_instruction:
                selectedCode.isFromTabular && child.children
                  ? null
                  : child.code_first_instruction,
              use_additional_instruction:
                selectedCode.isFromTabular && child.children
                  ? null
                  : child.use_additional_instruction,
              codealsowhenperf_instruction: child.codealsowhenperf_instruction,
              manual_added: child.manual_added,
              codeto_instruction:
                selectedCode.isFromTabular && child.children
                  ? null
                  : child.codeto_instruction,
            }));

          let newFilteredCodes = [...validChildren];

          if (hasSeeAlso) {
            // Show ONLY ONE "Unspecified" option (renamed from See Also)
            const seeAlsoTerm = selectedCode.seealso.trim();
            const unspecifiedOption = {
              rawName: `Unspecified (${seeAlsoTerm})`,
              rawDescription: `See also: ${seeAlsoTerm}`,
              name: `Unspecified (${seeAlsoTerm})`,
              description: seeAlsoTerm,
              isSeeAlsoOption: true,
              isUnspecifiedSeeAlso: true,
              option: seeAlsoTerm,
              originalCode: selectedCode,
              referenceId: selectedCode.id,
              category: selectedCategory.category,
              acs: selectedCode.acs,
              nca: selectedCode.nca,
            };
            newFilteredCodes.push(unspecifiedOption);
          } else {
            // Normal Unspecified option (when no seealso exists)
            let canAddUnspecified = false;
            let unspecifiedCode = null;

            if (selectedCode._type === "child") {
              const parentName = selectedCode.parentName;
              const parentDescription = selectedCode.parentDescription;
              const parentChildren = selectedCode.children || [];

              if (
                parentName?.trim() &&
                parentDescription?.trim() &&
                !isHyphenCode(parentDescription) && 
                parentChildren.length > 0 &&
                !selectedCode.isFromTabular
              ) {
                canAddUnspecified = true;
                unspecifiedCode = {
                  rawName: parentName,
                  rawDescription: parentDescription,
                  name: parentDescription || parentName,
                  description: parentDescription,
                  children: [],
                  isUnspecified: true,
                  isFromTabular: false,
                  Next_parent_code: selectedCode.Next_parent_code,
                  Warning_message: selectedCode.Warning_message,
                  id: selectedCode.parentId,
                  acs: selectedCode.parentAcs,
                  nca: selectedCode.parentNca,
                  code_first_instruction: selectedCode.code_first_instruction,
                  use_additional_instruction:
                    selectedCode.use_additional_instruction,
                  codealsowhenperf_instruction:
                    selectedCode.codealsowhenperf_instruction,
                  codeto_instruction: selectedCode.codeto_instruction,
                  manual_added: selectedCode.manual_added,
                };
              }
            } else {
              canAddUnspecified =
                selectedCode.rawName?.trim() &&
                selectedCode.description?.trim() &&
                selectedCode.description !== null &&
                !isHyphenCode(selectedCode.description) &&
                !selectedCode.isFromTabular;

              if (canAddUnspecified) {
                unspecifiedCode = {
                  rawName: selectedCode.rawName,
                  rawDescription: selectedCode.description,
                  name: selectedCode.description || selectedCode.rawName,
                  description: selectedCode.description,
                  children: [],
                  isUnspecified: true,
                  isFromTabular: false,
                  Next_parent_code: selectedCode.Next_parent_code,
                  Warning_message: selectedCode.Warning_message,
                  id: selectedCode.id,
                  acs: selectedCode.acs,
                  nca: selectedCode.nca,
                  code_first_instruction: selectedCode.code_first_instruction,
                  use_additional_instruction:
                    selectedCode.use_additional_instruction,
                  codealsowhenperf_instruction:
                    selectedCode.codealsowhenperf_instruction,
                  codeto_instruction: selectedCode.codeto_instruction,
                  manual_added: selectedCode.manual_added,
                };
              }
            }

            if (canAddUnspecified && unspecifiedCode) {
              newFilteredCodes.push(unspecifiedCode);
            }
          }

          newState = {
            ...newState,
            selectedParent: selectedCode,
            currentCodeSearchData: newFilteredCodes,
            filteredCodes: newFilteredCodes,
            selectedIndex: null,
            initialSearchDone: true,
            showFinalScreen: false,
            originalCode: normalizeCodeObject(selectedCode),
          };
          reduxDispatch(updateLocalState(newState));
          pushHistoryState(newState);
          return;
        }

        // CASE 2: NO CHILDREN + HAS SEE ALSO → AUTO TRIGGER (No option shown)
        if (hasSeeAlso) {
          //  ADD DIRECT CODE (no need hasChildren check)
          const existingArr = localSelectedCodes[selectedCategory] || [];

          const codeKey = `${selectedCode.rawName}-${selectedCode.rawDescription}`;
          const isAlreadyAdded = existingArr.some(
            (c) => `${c.rawName}-${c.rawDescription}` === codeKey,
          );

          if (!isAlreadyAdded) {
            newState.selectedCodes = {
              ...localSelectedCodes,
              [selectedCategory]: [
                ...existingArr,
                {
                  rawName: selectedCode.rawName,
                  rawDescription:
                    selectedCode.rawDescription || selectedCode.description,
                  isFromTabular: selectedCode.isFromTabular || false,
                  acs: selectedCode.acs,
                  nca: selectedCode.nca,
                  code_first_instruction: null,
                  use_additional_instruction: null,
                  codealsowhenperf_instruction: null,
                  codeto_instruction: null,
                  manual_added: null,
                  id: selectedCode.id,
                },
              ],
            };
          }
          const seeAlsoTerm = selectedCode.seealso.trim();
          const parsedPathParts = parseSeeTerm(seeAlsoTerm);
          const pathParts =
            parsedPathParts?.length > 1 &&
            parsedPathParts[parsedPathParts.length - 1]?.toLowerCase() ===
              "malignant"
              ? parsedPathParts.slice(0, -1)
              : parsedPathParts;

          const resolveResult = resolveSeePath(
            pathParts,
            newState.selectedCategory,
          );

          if (resolveResult && resolveResult.length > 0) {
            newState = {
              ...newState,
              selectedParent: selectedCode,
              currentCodeSearchData: resolveResult,
              filteredCodes: resolveResult,
              selectedIndex: null,
              initialSearchDone: true,
              showFinalScreen: false,
              originalCode: normalizeCodeObject(selectedCode),
              carryDirectCodeWithoutInstructions: true, // Ignore instructions
              pendingDirectCode: {
                rawName: selectedCode.rawName,
                rawDescription:
                  selectedCode.rawDescription || selectedCode.description,
                isFromTabular: selectedCode.isFromTabular || false,
                acs: selectedCode.acs,
                nca: selectedCode.nca,
                code_first_instruction: null,
                use_additional_instruction: null,
                codealsowhenperf_instruction: null,
                codeto_instruction: null,
                manual_added: null,
                id: selectedCode.id,
              },
            };
            reduxDispatch(updateLocalState(newState));
            pushHistoryState(newState);
            return;
          }
        }
        if (
          selectedCode &&
          (selectedCode.isSeeAlsoOption || selectedCode.isUnspecifiedSeeAlso)
        ) {
          const originalCode = selectedCode.originalCode || selectedCode;
          // ── MORPHOLOGY INTERCEPT ──
          // If the original code is a morphology code, route to Neoplasm instead
          if (isMorphologyEntry(originalCode)) {
            const existingArr = localSelectedCodes[selectedCategory] || [];
            const morphRaw = (
              originalCode.rawDescription ||
              originalCode.description ||
              ""
            )
              .trim()
              .replace(/[()]/g, "");
            const morphKey = `${originalCode.rawName}-${morphRaw}`;
            const alreadyAdded = existingArr.some(
              (c) =>
                `${c.rawName}-${(c.rawDescription || "").replace(/[()]/g, "")}` ===
                morphKey,
            );

            if (!alreadyAdded) {
              newState.selectedCodes = {
                ...localSelectedCodes,
                [selectedCategory]: [
                  ...existingArr,
                  {
                    rawName: originalCode.rawName,
                    rawDescription: morphRaw,
                    isFromTabular: false,
                    isMorphologyCode: true,
                    acs: originalCode.acs || null,
                    nca: originalCode.nca || null,
                    code_first_instruction: null,
                    use_additional_instruction: null,
                    codealsowhenperf_instruction: null,
                    codeto_instruction: null,
                    manual_added: null,
                    id: originalCode.id,
                  },
                ],
              };
              newState.lastAddedCode = originalCode;
            }

            let resolveResult = resolveSeePath(["Neoplasm"], selectedCategory);
            if (!resolveResult || resolveResult.length === 0) {
              resolveResult = resolveSeePath(["Neoplastic"], selectedCategory);
            }

            if (resolveResult && resolveResult.length > 0) {
              newState = {
                ...newState,
                selectedParent: originalCode,
                currentCodeSearchData: resolveResult,
                filteredCodes: resolveResult,
                selectedIndex: null,
                initialSearchDone: true,
                showFinalScreen: false,
                originalCode: normalizeCodeObject(originalCode),
                carryDirectCodeWithoutInstructions: true,
                pendingDirectCode: {
                  rawName: originalCode.rawName,
                  rawDescription: morphRaw,
                  isFromTabular: false,
                  isMorphologyCode: true,
                  id: originalCode.id,
                },
              };

              reduxDispatch(updateLocalState(newState));
              pushHistoryState(newState);
              return;
            }
          }
          // ── END MORPHOLOGY INTERCEPT ──
          const originalCodeValue =
            originalCode.rawDescription || originalCode.description || "";
          const originalIsHyphen =
            !originalCode.isFromTabular && isHyphenCode(originalCodeValue);

          const existingArr = localSelectedCodes[selectedCategory] || [];
          const codeKey = `${originalCode.rawName}-${originalCode.rawDescription}`;
          const isAlreadyAdded = existingArr.some(
            (c) => `${c.rawName}-${c.rawDescription}` === codeKey,
          );

          // Skip adding the hyphen parent itself — its extension children
          // (already expanded via resolveSeePath) are what should get selected.
          if (!isAlreadyAdded && !originalIsHyphen) {
            newState.selectedCodes = {
              ...localSelectedCodes,
              [selectedCategory]: [
                ...existingArr,
                {
                  rawName: originalCode.rawName,
                  rawDescription:
                    originalCode.rawDescription || originalCode.description,
                  isFromTabular: originalCode.isFromTabular || false,
                  acs: originalCode.acs,
                  nca: originalCode.nca,
                  code_first_instruction: null,
                  use_additional_instruction: null,
                  codealsowhenperf_instruction: null,
                  codeto_instruction: null,
                  manual_added: null,
                  id: originalCode.id,
                },
              ],
            };
            newState.lastAddedCode = originalCode;
          }
          newState.pendingDirectCode = originalIsHyphen
            ? null
            : {
                rawName: originalCode.rawName,
                rawDescription:
                  originalCode.rawDescription || originalCode.description,
                isFromTabular: originalCode.isFromTabular || false,
                acs: originalCode.acs,
                nca: originalCode.nca,
                code_first_instruction: null,
                use_additional_instruction: null,
                codealsowhenperf_instruction: null,
                codeto_instruction: null,
                manual_added: null,
                id: originalCode.id,
                seealso: originalCode.seealso || null,
              };

          const term = selectedCode.option.trim();
          const parsedPathParts = parseSeeTerm(term);
          const pathParts =
            parsedPathParts?.length > 1 &&
            parsedPathParts[parsedPathParts.length - 1]?.toLowerCase() ===
              "malignant"
              ? parsedPathParts.slice(0, -1)
              : parsedPathParts;

          // Special case for Table of drugs and chemicals
          if (
            term.toLowerCase() === "table of drugs and chemicals" ||
            pathParts.join(" ").toLowerCase() === "table of drugs and chemicals"
          ) {
            let children = resolveSeePath(
              ["Table of drugs and chemicals"],
              newState.selectedCategory,
            );

            if (children?.length > 0) {
              const updatedChildren = children;

              newState = {
                ...newState,
                selectedParent: originalCode,
                currentCodeSearchData: updatedChildren,
                filteredCodes: updatedChildren,
                selectedIndex: null,
                initialSearchDone: true,
                showFinalScreen: false,
                originalCode: normalizeCodeObject(originalCode),
                carryDirectCodeWithoutInstructions: true,
              };

              reduxDispatch(updateLocalState(newState));
              pushHistoryState(newState);
              return;
            }
          }

          // Normal See Also resolution
          const resolveResult = resolveSeePath(
            pathParts,
            newState.selectedCategory,
          );

          // if (resolveResult && resolveResult.length > 0) {
          //   newState = {
          //     ...newState,
          //     selectedParent: originalCode,
          //     currentCodeSearchData: resolveResult,
          //     filteredCodes: resolveResult,
          //     selectedIndex: null,
          //     initialSearchDone: true,
          //     showFinalScreen: false,
          //     originalCode: normalizeCodeObject(originalCode),
          //     carryDirectCodeWithoutInstructions: true,
          //   };

          //   reduxDispatch(updateLocalState(newState));
          //   pushHistoryState(newState);
          //   return;
          // }
          if (resolveResult && resolveResult.length > 0) {
            // ── ONLY NEW LOGIC ADDED HERE ──
            let mergedFilteredCodes = resolveResult; // default: normal flow

            if (
              selectedCode.originalCode?._matchedVia === "abbreviation" &&
              Array.isArray(selectedCode.originalCode?.children) &&
              selectedCode.originalCode.children.length > 0
            ) {
              // console.log(
              //   "[SeeAlso] abbreviation match — will merge originalCode.children with resolveResult",
              // );

              const originalChildren = selectedCode.originalCode.children
                .filter((c) => c && c.name?.trim())
                .map((c) => ({
                  rawName: c.name,
                  rawDescription: c.description ?? c.name,
                  name: c.name,
                  description: c.description ?? c.name,
                  children: c.children || [],
                  isFromTabular: false,
                  see: c.see,
                  seealso: c.seealso,
                  isDescriptionNull: c.description === null,
                  id: c.id,
                  acs: c.acs,
                  code_first_instruction: c.code_first_instruction,
                  use_additional_instruction: c.use_additional_instruction,
                  codealsowhenperf_instruction: c.codealsowhenperf_instruction,
                  codeto_instruction: c.codeto_instruction,
                  manual_added: c.manual_added,
                }));

              // console.log(
              //   "[SeeAlso] originalChildren mapped:",
              //   originalChildren,
              // );
              // console.log(" [SeeAlso] resolveResult to append:", resolveResult);

              mergedFilteredCodes = [...originalChildren, ...resolveResult];

              // console.log(
              //   " [SeeAlso] final mergedFilteredCodes:",
              //   mergedFilteredCodes,
              // );
            } else {
              // console.log(
              //   " [SeeAlso] NOT abbreviation — normal flow, resolveResult only",
              // );
            }
            // ── END NEW LOGIC ──

            newState = {
              ...newState,
              selectedParent: originalCode,
              currentCodeSearchData: mergedFilteredCodes, // ← was resolveResult
              filteredCodes: mergedFilteredCodes, // ← was resolveResult
              selectedIndex: null,
              initialSearchDone: true,
              showFinalScreen: false,
              originalCode: normalizeCodeObject(originalCode),
              carryDirectCodeWithoutInstructions: true,
            };

            reduxDispatch(updateLocalState(newState));
            pushHistoryState(newState);
            return;
          }
          // if (resolveResult && resolveResult.length > 0) {
          //   // ── ONLY NEW LOGIC ADDED HERE ──
          //   let mergedFilteredCodes = resolveResult; // default: normal flow

          //   if (
          //     selectedCode.originalCode?._matchedVia === "abbreviation" &&
          //     Array.isArray(selectedCode.originalCode?.children) &&
          //     selectedCode.originalCode.children.length > 0
          //   ) {
          //     console.log(
          //       "[SeeAlso] abbreviation match — will merge originalCode.children with resolveResult",
          //     );

          //     const originalChildren = selectedCode.originalCode.children
          //       .filter((c) => c && c.name?.trim())
          //       .map((c) => ({
          //         rawName: c.name,
          //         rawDescription: c.description ?? c.name,
          //         name: c.name,
          //         description: c.description ?? c.name,
          //         children: c.children || [],
          //         isFromTabular: false,
          //         see: c.see,
          //         seealso: c.seealso,
          //         isDescriptionNull: c.description === null,
          //         id: c.id,
          //         acs: c.acs,
          //         code_first_instruction: c.code_first_instruction,
          //         use_additional_instruction: c.use_additional_instruction,
          //         codealsowhenperf_instruction: c.codealsowhenperf_instruction,
          //       }));

          //     console.log(
          //       "[SeeAlso] originalChildren mapped:",
          //       originalChildren,
          //     );
          //     console.log(" [SeeAlso] resolveResult to append:", resolveResult);

          //     mergedFilteredCodes = [...originalChildren, ...resolveResult];

          //     console.log(
          //       " [SeeAlso] final mergedFilteredCodes:",
          //       mergedFilteredCodes,
          //     );
          //   } else {
          //     console.log(
          //       " [SeeAlso] NOT abbreviation — normal flow, resolveResult only",
          //     );
          //   }
          //   // ── END NEW LOGIC ──

          //   newState = {
          //     ...newState,
          //     selectedParent: originalCode,
          //     currentCodeSearchData: mergedFilteredCodes, // ← was resolveResult
          //     filteredCodes: mergedFilteredCodes, // ← was resolveResult
          //     selectedIndex: null,
          //     initialSearchDone: true,
          //     showFinalScreen: false,
          //     originalCode: normalizeCodeObject(originalCode),
          //     carryDirectCodeWithoutInstructions: true,
          //   };

          //   reduxDispatch(updateLocalState(newState));
          //   pushHistoryState(newState);
          //   return;
          // }
          // Fallback
          newState = {
            ...newState,
            lastAddedCode: originalCode,
            // showFinalScreen: true,
            showError: true,
            carryDirectCodeWithoutInstructions: true,
          };
          finalizeOrManifestation(newState);
          // setTimeout(() => {
          //   reduxDispatch(
          //     updateLocalState({ showError: false, lastAddedCode: null }),
          //   );
          // }, 3000);
          return;
        }

        const hasChildren =
          (selectedCode.children && selectedCode.children.length > 0) ||
          (selectedCode.subcodes && selectedCode.subcodes.length > 0);
        if (
          !hasChildren &&
          !selectedCode.seealso &&
          (!newState.pendingInstructions ||
            (newState.pendingInstructions.length === 0 &&
              !localState.pendingInjuryAfterInstructions))
        ) {
          const existing = localSelectedCodes[selectedCategory] || [];

          // const exists = existing.some(
          //   (c) =>
          //     c.rawName === selectedCode.rawName &&
          //     (c.rawDescription || c.description) ===
          //     (selectedCode.rawDescription || selectedCode.description),
          // );
          const exists =
            selectedCategory === "Procedure Codes"
              ? false
              : existing.some(
                  (c) =>
                    c.rawName === selectedCode.rawName &&
                    (c.rawDescription || c.description) ===
                      (selectedCode.rawDescription || selectedCode.description),
                );

          if (!exists) {
            newState.selectedCodes = {
              ...localSelectedCodes,
              [selectedCategory]: [
                ...existing,
                {
                  rawName: selectedCode.rawName,
                  rawDescription:
                    selectedCode.rawDescription || selectedCode.description,
                  isFromTabular: selectedCode.isFromTabular || false,
                  acs: selectedCode.acs,
                  nca: selectedCode.nca,
                  id: selectedCode.id,
                },
              ],
            };
          }

          newState = {
            ...newState,
            lastAddedCode: selectedCode,
            // showFinalScreen: true,
            currentCodeSearchData: null,
            filteredCodes: [],
          };

          finalizeOrManifestation(newState);
          return;
        }
        const isSeePresent =
          selectedCode &&
          ((selectedCode.see && selectedCode.see.trim() !== "") ||
            (selectedCode.seeAlso && selectedCode.seeAlso.trim() !== ""));

        // If no children, continue with final processing
        const hasNullValueForFinalSelection =
          !isSeePresent &&
          (!selectedCode.rawName?.trim() ||
            selectedCode.description === null ||
            !selectedCode.description?.trim());
        if (
          // !isSeePresent && // ← skip final-screen if SEE exists
          (hasNullValueForFinalSelection ||
            selectedCode.rawDescription === null) &&
          newState.pendingInstructions?.length === 0
        ) {
          newState = {
            ...newState,
            lastAddedCode: selectedCode,
            // showFinalScreen: true,
            showError: true,
            selectedParent: selectedCode,
          };
          finalizeOrManifestation(newState);
          return;
        }
        let codesToAdd = [];
        // If we have chapter19Info in the state, add it and then clear it
        // -----------------------------------------------------------------
        // if (localState.chapter19Info) {
        //   codesToAdd.push({
        //     rawName: localState.chapter19Info.rawName,
        //     rawDescription: localState.chapter19Info.rawDescription,
        //     id: localState.chapter19Info.id,
        //     isFromTabular: false, // or adjust as neede
        //   });
        //   // Then we clear the chapter19Info from the state so it doesn't get added again
        //   newState.chapter19Info = null;
        // }
        // Same pattern as chapter19Info — merge pendingDirectCode into final codes
        // if (localState.pendingDirectCode) {
        //   codesToAdd.push({
        //     rawName: localState.pendingDirectCode.rawName,
        //     rawDescription: localState.pendingDirectCode.rawDescription,
        //     isFromTabular: false,
        //     id: localState.pendingDirectCode.id,
        //   });

        //   newState.pendingDirectCode = null;
        // }
        if (localState.pendingDirectCode) {
          const pendingCodes = splitMorphologyIfNeeded({
            rawName: localState.pendingDirectCode.rawName,
            rawDescription: localState.pendingDirectCode.rawDescription,
            isFromTabular: false,
            id: localState.pendingDirectCode.id,
          });
          codesToAdd = [...codesToAdd, ...pendingCodes]; // appended LAST
          newState.pendingDirectCode = null;
        }
        if (selectedCode.isFromTabular) {
          codesToAdd.push({
            rawName: selectedCode.rawName,
            rawDescription:
              selectedCode.description ||
              selectedCode.name ||
              "Tabular Code Description",
            description:
              selectedCode.description ||
              selectedCode.name ||
              "Tabular Code Description",
            isFromTabular: true,
            acs: selectedCode.acs,
            nca: selectedCode.nca,
            code_first_instruction: selectedCode.code_first_instruction,
            use_additional_instruction: selectedCode.use_additional_instruction,
            codealsowhenperf_instruction:
              selectedCode.codealsowhenperf_instruction,
            codeto_instruction: selectedCode.codeto_instruction,
            manual_added: selectedCode.manual_added,
          });
        }

        if (selectedCode.isUnspecified && selectedParent) {
          codesToAdd = [
            {
              rawName: selectedCode.rawName,
              rawDescription: selectedCode.description,
              isUnspecified: true,
              isFromTabular: selectedCode.isFromTabular || false,
              acs: selectedCode.acs,
              nca: selectedCode.nca,
              code_first_instruction: selectedCode.code_first_instruction,
              use_additional_instruction:
                selectedCode.use_additional_instruction,
              codealsowhenperf_instruction:
                selectedCode.codealsowhenperf_instruction,
              codeto_instruction: selectedCode.codeto_instruction,
              manual_added: selectedCode.manual_added,
            },
          ];
        }

        if (
          selectedParent &&
          (selectedCode.children?.length ||
            selectedCode.subcodes?.length ||
            0) === 0 &&
          !selectedCode.isUnspecified
        ) {
          codesToAdd.push({
            rawName: selectedCode.rawName,
            rawDescription: selectedCode.description,
            isFromTabular: selectedCode.isFromTabular || false,
            acs: selectedCode.acs,
            nca: selectedCode.nca,
            code_first_instruction: selectedCode.code_first_instruction,
            use_additional_instruction: selectedCode.use_additional_instruction,
            codealsowhenperf_instruction:
              selectedCode.codealsowhenperf_instruction,
            codeto_instruction: selectedCode.codeto_instruction,
            manual_added: selectedCode.manual_added,
          });
        }

        if (
          !selectedParent &&
          (selectedCode.children?.length ||
            selectedCode.subcodes?.length ||
            0) === 0
        ) {
          codesToAdd.push({
            rawName: selectedCode.rawName,
            rawDescription: selectedCode.description,
            isUnspecified: selectedCode.isUnspecified || false,
            isFromTabular: selectedCode.isFromTabular || false,
            acs: selectedCode.acs,
            nca: selectedCode.nca,
            code_first_instruction: selectedCode.code_first_instruction,
            use_additional_instruction: selectedCode.use_additional_instruction,
            codealsowhenperf_instruction:
              selectedCode.codealsowhenperf_instruction,
            codeto_instruction: selectedCode.codeto_instruction,
            manual_added: selectedCode.manual_added,
          });
        }

        // Step 1: Split
        codesToAdd = codesToAdd.flatMap((code) =>
          splitMorphologyIfNeeded(code),
        );

        const seen = new Set();
        codesToAdd = codesToAdd.filter((code) => {
          if (selectedCategory === "Procedure Codes") return true; // allow dupes
          const key = `${code.rawName}-${code.rawDescription}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });

        let invalidCodes = [];
        codesToAdd = codesToAdd.filter((code) => {
          const hasNullValue =
            !code.rawName?.trim() ||
            code.rawDescription === null ||
            !code.rawDescription?.trim() ||
            code.rawDescription === "-";
          if (hasNullValue) invalidCodes.push(code);
          return !hasNullValue;
        });

        if (invalidCodes.length > 0) {
          pushHistoryState({
            ...newState,
            showFinalScreen: false,
          });
          newState = {
            ...newState,
            lastAddedCode: invalidCodes[0],
            // showFinalScreen: true,
            showError: true,
            selectedParent: selectedCode,
          };
          finalizeOrManifestation(newState);
          setTimeout(() => {
            reduxDispatch(
              updateLocalState({ showError: false, lastAddedCode: null }),
            );
          }, 3000);
        } else if (codesToAdd.length > 0) {
          codesToAdd.forEach((code) => {
            if (
              !isCodeInHistory(
                code.rawName,
                code.rawDescription,
                codePathHistory,
              )
            ) {
              let historyNode;
              if (currentPath.length === 0) {
                historyNode = buildHistoryNode(code);
              } else {
                let currentNode = buildHistoryNode(code);
                for (let i = currentPath.length - 1; i >= 0; i--) {
                  const parentNode = {
                    ...currentPath[i],
                    children: [currentNode],
                  };
                  currentNode = parentNode;
                }
                historyNode = currentNode;
              }
              try {
                if (
                  selectedCode._type !== "parent" &&
                  selectedCode.children.length !== 0
                ) {
                  reduxDispatch(updateCodePathHistory(historyNode));
                }
              } catch (error) {
                // console.error(
                //   "Error in reduxDispatch(updateCodePathHistory):",
                //   error,
                // );
              }
            }
          });

          // Filter out "No or already coded" from final codes
          const codesToAddFiltered = codesToAdd.filter(
            (code) =>
              code.rawName !== "No or already coded" &&
              code.rawDescription !== "No or already coded",
          );
          const existingQueue123 = JSON.parse(
            sessionStorage.getItem("specialCodesToProcess") || "[]",
          );

          if (
            (!newState?.pendingInstructions ||
              newState.pendingInstructions.length === 0) &&
            newState.specialCodesToProcess.length === 0
          ) {
            //Check if we have pending injury flow BEFORE finalizing
            const shouldStartInjuryFlow =
              localState.pendingInjuryAfterInstructions;
            const pendingInjuryCode = localState.pendingInjuryCode;

            if (shouldStartInjuryFlow && pendingInjuryCode) {
              // Clear instruction-related warnings and state
              reduxDispatch(setPendingInjuryAfterInstructions(false));
              reduxDispatch(setPendingInjuryCode(null));
              reduxDispatch(clearWarningMessage()); // Clear any instruction warnings
              reduxDispatch(clearPendingInstructions()); // Clear any pending instructions

              // Get the next range type and set appropriate warning message
              const nextRangeType =
                localState.nextRangeToProcess || "externalCauseCodes";
              let warningMessage = "Select the External Cause";
              let rangeToExecute = "";

              if (nextRangeType === "externalCauseCodes") {
                rangeToExecute =
                  "V00-X59,X60-X84,X85-Y09,Y10-Y34,Y35-Y36,Y37-Y37,Y40-Y84,Y85-Y89,Y90,Y91,Y95,Y96,Y97,Y98";
                warningMessage = "Select the External Cause";
              } else if (nextRangeType === "placeOfOccurrence") {
                rangeToExecute =
                  "Y92.0,Y92.1,Y92.2,Y92.3,Y92.4,Y92.5,Y92.6,Y92.7,Y92.8,Y92.9";
                warningMessage = "Select the Place of Occurrence";
              } else if (nextRangeType === "activityCodes") {
                rangeToExecute = "U50-U72,U73.0,U73.1,U73.2,U73.81,U73.8";
                warningMessage = "Select Activity";
              } else {
                // No valid range - show final screen
                const finalState = {
                  ...newState,
                  // showFinalScreen: true,
                  pendingInstructions: [],
                  currentInstructionIndex: 0,
                  processingSpecialCodes: false,
                  specialCodesToProcess: [],
                  selectedParent: selectedCode,
                  lastAddedCode: selectedCode,
                  pendingInjuryAfterInstructions: false,
                  pendingInjuryCode: null,
                  currentWarningMessage: null,
                };
                finalizeOrManifestation(finalState);
                return;
              }

              // Execute the injury flow with the range
              (async () => {
                const extensions = await filterTabularCodesForMultipleRanges(
                  rangeToExecute,
                  selectedCategory,
                );

                if (extensions.length > 0) {
                  const noOption = createNoOption();

                  const injuryFlowState = {
                    ...newState,
                    currentCodeSearchData: extensions,
                    filteredCodes: [noOption, ...extensions],
                    selectedIndex: null,
                    initialSearchDone: true,
                    showFinalScreen: false,
                    currentWarningMessage: warningMessage, // Set the injury flow warning message
                    nextRangeToProcess: nextRangeType,
                    originalCode: normalizeCodeObject(pendingInjuryCode),
                    pendingInstructions: [], // Clear any remaining instructions
                    currentInstructionIndex: 0,
                    processingSpecialCodes: false,
                    specialCodesToProcess: [],
                    selectedParent: pendingInjuryCode,
                    lastAddedCode: pendingInjuryCode,
                    pendingInjuryAfterInstructions: false,
                    pendingInjuryCode: null,
                  };

                  reduxDispatch(updateLocalState(injuryFlowState));
                  pushHistoryState(injuryFlowState);
                } else {
                  // No extensions found - move to next range or show final screen
                  // console.log("No extensions found for range:", rangeToExecute);
                  const finalState = {
                    ...newState,
                    // showFinalScreen: true,
                    nextRangeToProcess: null,
                    currentWarningMessage: null,
                    pendingInstructions: [],
                    currentInstructionIndex: 0,
                    processingSpecialCodes: false,
                    specialCodesToProcess: [],
                    selectedParent: selectedCode,
                    lastAddedCode: selectedCode,
                    pendingInjuryAfterInstructions: false,
                    pendingInjuryCode: null,
                  };
                  finalizeOrManifestation(finalState);
                }
              })();
              return; // IMPORTANT: Return here to prevent final screen
            }

            // No pending injury flow - proceed with normal finalization
            // No instructions left - finalize
            newState = {
              ...newState,
              // showFinalScreen: true,
              selectedCodes: {
                ...localSelectedCodes,
                [selectedCategory]: [
                  ...localSelectedCodes[selectedCategory],
                  ...codesToAddFiltered,
                ],
              },
              lastAddedCode: codesToAddFiltered.length
                ? codesToAddFiltered[codesToAddFiltered.length - 1]
                : null,
              // currentPath: [],
              currentWarningMessage: null,
              selectedParent: selectedCode,
            };
            if (selectedCode.isDirectCode) {
              reduxDispatch(setInjuryFlowTracker([]));
              reduxDispatch(setInitialInjuryState(false));
            }
            // see also conidtion
            if (
              localState.pendingDirectCode &&
              localState.pendingDirectCode.seealso
            ) {
              reduxDispatch(setInjuryFlowTracker([]));
              reduxDispatch(setInitialInjuryState(false));
              reduxDispatch(setIsInjuryWithoutActivityFlow(false)); // if you have this
            }
            reduxDispatch(clearPendingInstructions());
            reduxDispatch(clearWarningMessage());
            finalizeOrManifestation(newState);
            return;
          } else if (newState.pendingInstructions.length > 0) {
            // Instructions still pending - process next one
            const [nextInstruction, ...remaining] =
              newState.pendingInstructions;
            let displayWarning = null;
            if (nextInstruction._instructionType === "codealsowhenperf") {
              displayWarning = `Code also when perfomed: ${nextInstruction.Warning_message}`;
            } else {
              displayWarning = nextInstruction.Warning_message;
            }
            newState = {
              ...newState,
              showFinalScreen: false,
              pendingInstructions: remaining,
              selectedCodes: {
                ...localSelectedCodes,
                [selectedCategory]: [
                  ...localSelectedCodes[selectedCategory],
                  ...codesToAddFiltered,
                ],
              },
              lastAddedCode: codesToAddFiltered.length
                ? codesToAddFiltered[codesToAddFiltered.length - 1]
                : null,
              currentWarningMessage: displayWarning,
              currentInstructionIndex: 0,
            };

            reduxDispatch(setPendingInstructions(remaining));
            if (nextInstruction.Warning_message) {
              reduxDispatch(
                setCurrentWarningMessage(nextInstruction.Warning_message),
              );
            } else {
              reduxDispatch(clearWarningMessage());
            }

            (async () => {
              if (!nextInstruction.Next_Parent_code) {
                if (remaining.length > 0) {
                  newState.pendingInstructions = remaining;
                  reduxDispatch(setPendingInstructions(remaining));
                  reduxDispatch(updateLocalState(newState));
                  processNextInstruction(remaining);
                } else {
                  finalizeOrManifestation(newState);
                  newState.pendingInstructions = [];
                  newState.currentWarningMessage = null;
                }
                return;
              }
              const extensions = await resolveInstructionExtensions(nextInstruction, selectedCategory);

              if (extensions.length > 0) {
                const noOption = createNoOption();
                newState.currentCodeSearchData = extensions;
                newState.filteredCodes =
                  nextInstruction._instructionType === "codeto"
                    ? [...extensions]
                    : [noOption, ...extensions];
                newState.selectedIndex = null;
                newState.initialSearchDone = true;
                newState.showFinalScreen = false;
                newState.originalCode = normalizeCodeObject(selectedCode);
                reduxDispatch(updateLocalState(newState));
                pushHistoryState(newState);
              } else if (
                nextInstruction._instructionType === "manually_added"
              ) {
                //unresolved manual term — don't add it as a literal code.
                // Just finalize (or continue) with what's already selected.
                finalizeOrManifestation({
                  ...newState,
                  currentWarningMessage: null,
                });
              } else {
                // no children, treat this instruction as resolved (unchanged — for real codes only)
                const codeToAdd = {
                  rawName:
                    nextInstruction._parent?.rawName ||
                    nextInstruction.Next_Parent_code,
                  rawDescription: nextInstruction.Next_Parent_code,
                  id: nextInstruction._parent?.id || null,
                  isFromTabular:
                    nextInstruction._parent?.isFromTabular || false,
                };
                const updatedState = {
                  ...newState,
                  selectedCodes: {
                    ...localSelectedCodes,
                    [selectedCategory]: [
                      ...localSelectedCodes[selectedCategory],
                      codeToAdd,
                    ],
                  },
                  lastAddedCode: codeToAdd,
                  currentWarningMessage: null,
                };
                reduxDispatch(updateLocalState(updatedState));
                pushHistoryState(updatedState);
              }
            })();
          } else if (
            newState.specialCodesToProcess.length > 0 &&
            localState.pendingInstructions.length === 0
          ) {
            // No instructions but special codes pending - process them
            processSpecialCodesAfterInstructions();
            return;
          }
        } else {
          newState = {
            ...newState,
            // showFinalScreen: true,
            // currentPath: [],
            // currentWarningMessage: null,
            selectedParent: selectedCode,
            lastAddedCode: selectedCode,
          };
          finalizeOrManifestation(newState);
          // reduxDispatch(updateLocalState(newState));
          // pushHistoryState(newState);
        }
      } else {
        reduxDispatch(updateLocalState(newState));
        pushHistoryState(newState);
      }
      // console.log(">>> [FELL THROUGH to bottom of handleContinue]");
    }, [
      localState,
      processHyphenCode,
      filterTabularCodes,
      getTabularExtensions,
      codePathHistory,
      reduxDispatch,
      buildHistoryNode,
      parseSeeTerm,
      resolveSeePath,
      normalizeCodeObject,
      searchTerm,
      selectedIndex,
      filteredCodes,
      currentCodeSearchData,
      showFinalScreen,
      selectedFinalCode,
      selectedCategory,
      initialSearchDone,
      localSelectedCodes,
      selectedParent,
      topLevelParent,
      lastAddedCode,
      originalCode,
      currentPath,
      isHyphenCode,
      extractCodesFromHyphen,
      smartSplitWithBracket,
    ]);
    useEffect(() => {
      if (showFinalScreen) {
        reduxDispatch(updateFinalScreenState(true));
      } else {
        reduxDispatch(updateFinalScreenState(false));
      }
    }, [reduxDispatch, showFinalScreen]);

    const handleTabChange = useCallback(
      (key) => {
        capturePathwaySnapshot();
        reduxDispatch(setChildFilterTerm(""));
        reduxDispatch(setRawSearchQuery(""));
        reduxDispatch(handleResetSelectedDetails());
        reduxDispatch(resetInstructions());

        const newState = {
          searchTerm: "",
          selectedIndex: null,
          filteredCodes: [],
          currentCodeSearchData: null,
          showFinalScreen: false,
          selectedFinalCode: null,
          selectedCategory: key,
          initialSearchDone: false,
          selectedCodes: { ...localSelectedCodes },
          selectedParent: null,
          topLevelParent: null,
          lastAddedCode: null,
          originalCode: null,
          currentPath: [],
          pendingInstructions: [],
          currentInstructionIndex: 0,
          currentWarningMessage: null,
          arrivedViaBackToFinal: false,
        };
        reduxDispatch(updateLocalState(newState));
        window.history.pushState(newState, "", window.location.pathname);
      },
      [localSelectedCodes, reduxDispatch],
    );

    const handleBack = useCallback(() => {
      // console.log("[HANDLEBACK] called. currentPath.length:", currentPath?.length, "history.length:", window.history.length, "isBackProcessing:", isBackProcessing);
      // if (isBackProcessing) return;
      forwardStackRef.current.push(toNavState(localState));
if (forwardStackRef.current.length > 50) forwardStackRef.current.shift();
      if (isBackProcessing) {
        // console.log("[HANDLEBACK] BLOCKED - already processing");
        return;
      }
      setIsBackProcessing(true);

      // Clear child filter term
      reduxDispatch(setChildFilterTerm(""));

      // Reset injury flow if at the final or external stage
      const shouldResetInjuryFlow =
        (showFinalScreen &&
          localState.nextRangeToProcess === "externalCauseCodes") ||
        currentPath?.length <= 1;
      // Initialize injury flow
      // setTimeout(() => {
      //   reduxDispatch(setInitialInjuryState(true));
      // }, 0);

      if (shouldResetInjuryFlow) {
        // console.log("🔄 BACK: Completely resetting injury flow");
        reduxDispatch(setNextRangeToProcess(null));
        reduxDispatch(setInjuryFlowTracker([]));
        reduxDispatch(setInjuryCodeFlowTracker([]));
        reduxDispatch(setInitialInjuryState(false));
        reduxDispatch(setIsInjuryWithoutActivityFlow(false));
        reduxDispatch(setFinalRangeExecuted(false));
        reduxDispatch(setPendingInjuryAfterInstructions(false));
        reduxDispatch(setPendingInjuryCode(null));
        reduxDispatch(setPendingInjuryAfterInstructions(false));
        reduxDispatch(setPendingInjuryCode(null));
      }

      let searchTermToAssign = "";
      let parentToRestore = null;
      let shouldClearSelectedCodeDetails = false;
      let filteredCodesToRestore = [];
      let currentCodeSearchDataToRestore = null;
      let shouldResetInstructions = false;
      let isFromTabular = false;

      const currentItem = currentPath[currentPath.length - 1];
      const isNoAlreadyCoded =
        currentItem?._isCustomOption === true ||
        currentItem?.code === "No or already coded" ||
        currentItem?.description === "No or already coded";

      const isInInstructionFlow =
        localState.pendingInstructions &&
        localState.pendingInstructions.length > 0;
      const isInSpecialCodeFlow = localState.processingSpecialCodes;
      const isGoingBackToParentLevel =
        !showFinalScreen &&
        !isInInstructionFlow &&
        // add this check
        currentPath?.length <= 2; // allow level 2 as well

      // SIMPLIFIED SEARCH TERM ASSIGNMENT LOGIC
      const getSearchTerm = (item) => {
        if (!item) return "";

        // For tabular items, use description
        if (item.isFromTabular) {
          return item.rawDescription || item.description || item.rawName || "";
        }

        // For index items, use name/code
        return (
          item.rawName || item.name || item.code || item.rawDescription || ""
        );
      };

      // FIX 1: Don't reset instructions in instruction flow
      if (isInSpecialCodeFlow) {
        reduxDispatch(removeSecondLastFromHistory());

        if (localState.selectedParent) {
          parentToRestore = localState.selectedParent;
          isFromTabular = parentToRestore.isFromTabular || false;
          const potentialSearchTerm = getSearchTerm(parentToRestore);
          if (potentialSearchTerm !== "No or already coded") {
            searchTermToAssign = potentialSearchTerm;
          }

          // Restore children
          const children =
            parentToRestore.children || parentToRestore.subcodes || [];
          const validChildren = children
            .filter((child) => child && child.name?.trim())
            .map((child) => ({
              rawName: child.name,
              rawDescription:
                child.description ||
                (child.description === null ? null : child.name),
              name: child.name,
              description:
                child.description ||
                (child.description === null ? null : child.name),
              children: child.subcodes || child.children || [],
              isFromTabular: parentToRestore.isFromTabular || false,
              Next_parent_code: child.Next_parent_code,
              Warning_message: child.Warning_message,
              see: child.see,
              seealso: child.seealso,
              isDescriptionNull: child.description === null,
              id: child.id,
              acs: child.acs,
              nca: child.nca,
              code_first_instruction: child.code_first_instruction,
              use_additional_instruction: child.use_additional_instruction,
              codealsowhenperf_instruction: child.codealsowhenperf_instruction,
              codeto_instruction: child.codeto_instruction,
              manual_added: child.manual_added,
            }));

          filteredCodesToRestore = validChildren;
          currentCodeSearchDataToRestore = validChildren;
        }
        // Don't reset instructions in special code flow
        shouldResetInstructions = false;
      } else if (showFinalScreen || isInInstructionFlow) {
        reduxDispatch(removeSecondLastFromHistory());

        if (isInInstructionFlow) {
          // FIX 2: Preserve instruction state when going back from instruction flow
          const originalCodeForInstructions =
            localState.originalCode || selectedParent || topLevelParent;

          if (originalCodeForInstructions) {
            isFromTabular = originalCodeForInstructions.isFromTabular || false;
            const potentialSearchTerm = getSearchTerm(
              originalCodeForInstructions,
            );
            if (potentialSearchTerm !== "No or already coded") {
              searchTermToAssign = potentialSearchTerm;
            }
            parentToRestore = originalCodeForInstructions;
            const children =
              originalCodeForInstructions.children ||
              originalCodeForInstructions.subcodes ||
              [];
            const validChildren = children
              .filter((child) => child && child.name?.trim())
              .map((child) => ({
                rawName: child.name,
                rawDescription:
                  child.description ||
                  (child.description === null ? null : child.name),
                name: child.name,
                description:
                  child.description ||
                  (child.description === null ? null : child.name),
                children: child.subcodes || child.children || [],
                isFromTabular:
                  originalCodeForInstructions.isFromTabular || false,
                Next_parent_code: child.Next_parent_code,
                Warning_message: child.Warning_message,
                see: child.see,
                seealso: child.seealso,
                isDescriptionNull: child.description === null,
                id: child.id,
                acs: child.acs,
                nca: child.nca,
                code_first_instruction: child.code_first_instruction,
                use_additional_instruction: child.use_additional_instruction,
                codealsowhenperf_instruction:
                  child.codealsowhenperf_instruction,
                codeto_instruction: child.codeto_instruction,
                manual_added: child.manual_added,
              }));

            filteredCodesToRestore = validChildren;
            currentCodeSearchDataToRestore = validChildren;
          }
          // Don't reset instructions - preserve the flow
          shouldResetInstructions = false;
        } else {
          // Final screen back navigation
          if (currentPath && currentPath.length > 0) {
            const targetIndex =
              currentPath.length > 1
                ? currentPath.length - 2
                : currentPath.length - 1;
            const targetPathItem = currentPath[targetIndex];
            isFromTabular = targetPathItem.isFromTabular || false;
            const potentialSearchTerm = getSearchTerm(targetPathItem);
            // ✅ Get last added code details
            const lastCode = localState.lastAddedCode;

            // ✅ Check if last added code is missing or was "No or already coded"
            const isInvalidLastCode =
              !lastCode ||
              lastCode.description === "No or already coded" ||
              lastCode.name === "No or already coded" ||
              lastCode.code === "No or already coded";

            // ✅ Decide search term based on conditions
            if (isInvalidLastCode) {
              // Reset search if last code invalid or was "No or already coded"
              searchTermToAssign = "";
              shouldClearSelectedCodeDetails = true;
              shouldResetInstructions = true;
            } else if (potentialSearchTerm !== "No or already coded") {
              // ✅ Normal case — assign search term
              searchTermToAssign = potentialSearchTerm;
            }
            if (potentialSearchTerm !== "No or already coded") {
              searchTermToAssign = potentialSearchTerm;
            }

            if (currentPath.length === 1) {
              parentToRestore = topLevelParent;
              shouldResetInstructions = true;
            } else {
              parentToRestore = targetPathItem; //null shit buggggggggggg nontuberculouschange
            }
          } else if (selectedParent) {
            isFromTabular = selectedParent.isFromTabular || false;
            const potentialSearchTerm = getSearchTerm(selectedParent);
            if (potentialSearchTerm !== "No or already coded") {
              searchTermToAssign = selectedParent;
            }
            parentToRestore = selectedParent;
          }

          // Re-filter codes if we have a search term
          if (searchTermToAssign && searchTermToAssign.length >= 3) {
            const refilteredCodes = filterCodes(
              searchTermToAssign,
              dataSource,
              false,
              selectedCategory,
            );
            filteredCodesToRestore = refilteredCodes;
            currentCodeSearchDataToRestore = refilteredCodes;
          }
        }
      } else {
        reduxDispatch(removeSecondLastFromHistory());

        if (currentPath && currentPath.length > 0 && selectedParent !== null) {
          const targetIndex =
            currentPath.length > 1
              ? currentPath.length - 2
              : currentPath.length - 1;
          const targetPathItem = currentPath[targetIndex];
          isFromTabular = targetPathItem.isFromTabular || false;
          const potentialSearchTerm = getSearchTerm(targetPathItem);
          if (potentialSearchTerm !== "No or already coded") {
            searchTermToAssign = potentialSearchTerm;
          }
          parentToRestore = targetPathItem;

          if (targetIndex === 0 && topLevelParent) {
            parentToRestore = topLevelParent;
          } else if (slectedParntesandChilds?.parent) {
            parentToRestore = slectedParntesandChilds.parent;
          }

          if (currentPath.length === 1) {
            shouldClearSelectedCodeDetails = true;
            shouldResetInstructions = true;
          }
        } else if (selectedParent) {
          isFromTabular = selectedParent.isFromTabular || false;
          const potentialSearchTerm = getSearchTerm(selectedParent);
          if (potentialSearchTerm !== "No or already coded") {
            searchTermToAssign = potentialSearchTerm;
          }
          parentToRestore = selectedParent;

          if (slectedParntesandChilds?.parent) {
            parentToRestore = slectedParntesandChilds.parent;
            isFromTabular =
              slectedParntesandChilds.parent.isFromTabular || false;
            const potentialSearchTerm = getSearchTerm(
              slectedParntesandChilds.parent,
            );
            if (potentialSearchTerm !== "No or already coded") {
              searchTermToAssign = potentialSearchTerm;
            }
          }
        } else {
          searchTermToAssign = localState.searchTerm || "";
          shouldClearSelectedCodeDetails = true;
          shouldResetInstructions = true;
          isFromTabular = false;
        }

        if (!shouldResetInstructions) {
          filteredCodesToRestore = localState.filteredCodes || [];
          currentCodeSearchDataToRestore =
            selectedParent === null ? null : localState.currentCodeSearchData;
        }
      }

      // FIX 3: Only reset instructions when explicitly needed
      if (shouldResetInstructions) {
        reduxDispatch(resetInstructions());
      }

      // Store the parent for restoration
      if (parentToRestore) {
        reduxDispatch(handleStoreParentAndChilds(parentToRestore));
        reduxDispatch(setSearchByCodeId(parentToRestore.id));
      }

      // Set the search query and reset search state
      if (searchTermToAssign) {
        reduxDispatch(setRawSearchQuery(searchTermToAssign));

        if (parentToRestore?.id) {
          reduxDispatch(
            handleResetSearch({
              id: parentToRestore.id,
              rawSearchQuery: searchTermToAssign,
            }),
          );
        } else {
          reduxDispatch(
            handleResetSearch({
              rawSearchQuery: searchTermToAssign,
            }),
          );
        }
      } else {
        reduxDispatch(setRawSearchQuery(""));
        reduxDispatch(handleResetSelectedDetails());
        reduxDispatch(
          handleResetSearch({
            rawSearchQuery: "",
          }),
        );
      }

      const historyFilteredCodes = window.history.state?.filteredCodes || [];
      const currentFilteredCodes = localState.filteredCodes || [];

      // Use the most reliable source: history state first, then local state
      const preservedFilteredCodes =
        filteredCodesToRestore.length > 0
          ? filteredCodesToRestore
          : historyFilteredCodes.length > 0
            ? historyFilteredCodes
            : currentFilteredCodes;

      // FIX 4: Preserve pendingInstructions and lastAddedCode when in instruction/special code flow
      const newState = {
        ...localState,
        searchTerm: searchTermToAssign,
        showFinalScreen: false,
        selectedIndex: null,
        filteredCodes: preservedFilteredCodes,
        pendingInstructions: shouldResetInstructions
          ? []
          : localState.pendingInstructions,
        currentInstructionIndex: shouldResetInstructions
          ? 0
          : localState.currentInstructionIndex,
        processingSpecialCodes:
          shouldResetInstructions || isGoingBackToParentLevel
            ? false
            : localState.processingSpecialCodes,
        specialCodesToProcess:
          shouldResetInstructions || isGoingBackToParentLevel
            ? []
            : localState.specialCodesToProcess,
        currentSpecialCode:
          shouldResetInstructions || isGoingBackToParentLevel
            ? null
            : localState.currentSpecialCode,
        allHyphenCodesWithPendingMorphology:
          shouldResetInstructions || isGoingBackToParentLevel
            ? []
            : localState.allHyphenCodesWithPendingMorphology,
        pendingMorphologyFromHyphen:
          shouldResetInstructions || isGoingBackToParentLevel
            ? null
            : localState.pendingMorphologyFromHyphen,
        pendingManifestationAfterInstructions:
          shouldResetInstructions || isGoingBackToParentLevel
            ? false
            : localState.pendingManifestationAfterInstructions,
        manifestationWarningMessage:
          shouldResetInstructions || isGoingBackToParentLevel
            ? null
            : localState.manifestationWarningMessage,
        nextRangeToProcess: shouldResetInjuryFlow
          ? null
          : localState.nextRangeToProcess,
        injuryFlowTracker: shouldResetInjuryFlow
          ? []
          : localState.injuryFlowTracker,
        injuryCodeFlowTracker: shouldResetInjuryFlow
          ? []
          : localState.injuryCodeFlowTracker,
        initialInjuryState: shouldResetInjuryFlow
          ? false
          : localState.initialInjuryState,
        isInjuryWithoutActivityFlow: shouldResetInjuryFlow
          ? false
          : localState.isInjuryWithoutActivityFlow,
        finalRangeExecuted: shouldResetInjuryFlow
          ? false
          : localState.finalRangeExecuted,
        chapter19Info: shouldResetInjuryFlow ? null : localState.chapter19Info,
        // pendingDirectCode:shouldResetInjuryFlow ? null : localState.pendingDirectCode
        // Add other properties as needed
      };

      if (shouldClearSelectedCodeDetails) {
        reduxDispatch(clearSelectedCodeDetails());
      }

      // Update local state
      reduxDispatch(updateLocalState(newState));

      // Update browser history
      window.history.replaceState(toNavState(newState), "", window.location.pathname);

      // Navigate back if we have history
      if (
        currentPath?.length > 0 ||
        selectedParent !== null ||
        showFinalScreen
      ) {
        window.history.back();
      }

      // Set tabs after navigation
      setTimeout(() => {
        if (
          selectedCategory === "Additional Diagnoses" ||
          selectedCategory === "Principal Diagnosis"
        ) {
          if (isFromTabular) {
            reduxDispatch(setActiveTab("ICD 10 AM Tabular"));
            reduxDispatch(setActiveIcon("Tabular"));
          } else {
            reduxDispatch(setActiveTab("ICD 10 AM Index"));
            reduxDispatch(setActiveIcon("ICD"));
          }
        } else if (selectedCategory === "Procedure Codes") {
          if (isFromTabular) {
            reduxDispatch(setActiveTab("ACHI Tabular"));
            reduxDispatch(setActiveIcon("Tabular"));
          } else {
            reduxDispatch(setActiveTab("ACHI Index"));
            reduxDispatch(setActiveIcon("ICD"));
          }
        }
      }, 100);

      setTimeout(() => {
        setIsBackProcessing(false);
      }, 200);
      setTimeout(() => {
        focusSearchInput();
      }, 150);
    }, [
      isBackProcessing,
      currentPath,
      selectedParent,
      topLevelParent,
      slectedParntesandChilds,
      selectedCategory,
      localSelectedCodes,
      localState,
      showFinalScreen,
      initialSearchDone,
      reduxDispatch,
      normalizeCodeObject,
      dataSource,
      filterCodes,
    ]);

    const handleResetCurrentPathway = useCallback(() => {
      const safeSnapshot = pathwayStartSnapshot || {
        "Principal Diagnosis": [],
        "Additional Diagnoses": [],
        "Procedure Codes": [],
      };

      const resetState = {
        ...localState,
        searchTerm: "",
        selectedIndex: null,
        filteredCodes: [],
        currentCodeSearchData: null,
        showFinalScreen: false,
        selectedFinalCode: null,
        initialSearchDone: false,
        selectedParent: null,
        topLevelParent: null,
        lastAddedCode: null,
        originalCode: null,
        currentPath: [],
        pendingInstructions: [],
        currentInstructionIndex: 0,
        currentWarningMessage: null,
        processingSpecialCodes: false,
        specialCodesToProcess: [],
        currentSpecialCode: null,
        originalSpecialCodesWithSymbols: [],
        allHyphenCodesWithPendingMorphology: [],
        pendingMorphologyFromHyphen: null,
        pendingManifestationAfterInstructions: false,
        manifestationWarningMessage: null,
        pendingDirectCode: null,
        chapter19Info: null,
        pendingInjuryAfterInstructions: false,
        pendingInjuryCode: null,
        nextRangeToProcess: null,
        injuryFlowTracker: [],
        injuryCodeFlowTracker: [],
        initialInjuryState: true,
        isInjuryWithoutActivityFlow: false,
        finalRangeExecuted: false,
        showError: false,
        selectedCodes: safeSnapshot,
      };

      reduxDispatch(updateLocalState(resetState));
      // Only remove history added during this pathway, not previous ones
      reduxDispatch(removeCurrentPathwayFromHistory(pathwayHistoryCheckpoint));
      reduxDispatch(setRawSearchQuery(""));
      reduxDispatch(setSearchByCodeId(null));
      reduxDispatch(handleResetSelectedDetails());
      reduxDispatch(resetInstructions());
      reduxDispatch(clearWarningMessage());
      reduxDispatch(clearPendingInstructions());
      reduxDispatch(setInjuryFlowTracker([]));
      reduxDispatch(setInjuryCodeFlowTracker([]));
      reduxDispatch(setInitialInjuryState(true));
      reduxDispatch(setIsInjuryWithoutActivityFlow(false));
      reduxDispatch(setFinalRangeExecuted(false));
      reduxDispatch(setPendingInjuryAfterInstructions(false));
      reduxDispatch(setPendingInjuryCode(null));

      setPathwayStartSnapshot(null);
      setPathwayHistoryCheckpoint(0);

      window.history.replaceState(resetState, "", window.location.pathname);

      setTimeout(() => {
        focusSearchInput();
      }, 100);
    }, [
      localState,
      pathwayStartSnapshot,
      pathwayHistoryCheckpoint,
      reduxDispatch,
    ]);
    // Helper — use this as the onClick for BOTH cancel buttons:
    const handleCancelClick = () => {
      const isMidPathway =
        hasAnyCodes ||
        searchTerm.length > 0 ||
        initialSearchDone ||
        (currentPath && currentPath.length > 0);

      if (isMidPathway) {
        setShowCancelModal(true);
      } else {
        // no codes selected, cancel immediately
        const newState = {
          ...localState,
          pendingInstructions: [],
          searchTerm: "",
          showFinalScreen: false,
          selectedFinalCode: null,
          currentWarningMessage: null,
          showError: false,
          currentInstructionIndex: 0,
        };
        reduxDispatch(updateLocalState(newState));
        reduxDispatch(setRawSearchQuery(""));
        reduxDispatch(setSearchByCodeId(null));
        reduxDispatch(handleResetSelectedDetails());
        reduxDispatch(resetInstructions());
        reduxDispatch(setInjuryCodeFlowTracker([]));
        sessionStorage.setItem("newcycle", "false");
        setShowCodeSearch(false);
      }
    };
    // Add this function to extract cluster IDs from existing codes
    const extractExistingClusterIds = useCallback(() => {
      const existingClusterIds = new Set();

      // Check if there are existing codes in sessionStorage or from props
      const existingFinalCode = sessionStorage.getItem("finalCode");

      if (existingFinalCode) {
        try {
          const parsedData = JSON.parse(existingFinalCode);

          // Extract from principal diagnoses
          if (parsedData.principal && Array.isArray(parsedData.principal)) {
            parsedData.principal.forEach((item) => {
              if (item.code && item.code.length >= 2) {
                // Extract cluster ID (second character after COF number)
                const clusterId = item.code.charAt(1);
                if (/[A-Z]/.test(clusterId)) {
                  existingClusterIds.add(clusterId);
                }
              }
            });
          }

          // Extract from additional diagnoses
          if (parsedData.additional && Array.isArray(parsedData.additional)) {
            parsedData.additional.forEach((item) => {
              if (item.code && item.code.length >= 2) {
                const clusterId = item.code.charAt(1);
                if (/[A-Z]/.test(clusterId)) {
                  existingClusterIds.add(clusterId);
                }
              }
            });
          }

          // Extract from procedures
          if (parsedData.procedure && Array.isArray(parsedData.procedure)) {
            parsedData.procedure.forEach((item) => {
              if (item.code && item.code.length >= 2) {
                const clusterId = item.code.charAt(1);
                if (/[A-Z]/.test(clusterId)) {
                  existingClusterIds.add(clusterId);
                }
              }
            });
          }
        } catch (error) {
          // console.error("Error parsing existing finalCode:", error);
        }
      }

      return Array.from(existingClusterIds).sort();
    }, []);

    const startNewCluster = useCallback(() => {
      // Extract existing cluster IDs from stored codes
      const existingClusterIds = extractExistingClusterIds();

      // Get next cluster ID considering existing ones
      const newClusterId = clusterIdGenerator.getNext(existingClusterIds);

      // Update Redux state
      reduxDispatch(setCurrentClusterId(newClusterId));
      reduxDispatch(
        addToClusterHistory({
          id: newClusterId,
          timestamp: new Date().toISOString(),
          existingIds: existingClusterIds,
        }),
      );

      return newClusterId;
    }, [reduxDispatch, extractExistingClusterIds]);
    // const handleFinalize = useCallback(async () => {
    //   try {
    //     reduxDispatch(handleResetSearch());
    //     reduxDispatch(setActiveTab("ICD 10 AM Index"));
    //     reduxDispatch(setActiveIcon("ICD"));
    //     setIsFetchingData1(true);

    //     let clusterIdToUse = currentClusterId;
    //     if (!clusterIdToUse) {
    //       clusterIdToUse = startNewCluster();
    //     }

    //     const allTabsData = Object.entries(localSelectedCodes).reduce(
    //       (acc, [category, codes]) => {
    //         const codesForTab = [];
    //         const processedKeys = new Set();

    //         // First, collect all codes
    //         codes.forEach((selectedCode) => {
    //           const clean = (val) => (val || "").replace(/[()]/g, "");
    //           const description = clean(selectedCode.rawDescription || "");
    //           const name = clean(selectedCode.rawName || "");
    //           const key = `${name}-${description}`;

    //           if (!processedKeys.has(key)) {
    //             const codesArray = description
    //               .split("†")
    //               .map((codePart) => codePart.trim().replace(/\*$/, ""));
    //             const finalCodesArray =
    //               codesArray.length > 1
    //                 ? codesArray
    //                 : [description.trim().replace(/\*$/, "")];

    //             if (selectedCode.isFromTabular) {
    //               // For tabular codes: prefix the code with cluster ID
    //               const baseCode = finalCodesArray[0] || name;
    //               // const prefixedCode = `${clusterIdToUse}${name}`;
    //               const prefixedCode = `${name}`;

    //               codesForTab.push({
    //                 code: prefixedCode, // This will be like "AI23.2"
    //                 codes: [prefixedCode],
    //                 rawName: selectedCode.rawName,
    //                 rawDescription: selectedCode.rawDescription,
    //                 // Add a flag to indicate if it starts with T
    //                 startsWithT: selectedCode.rawDescription?.startsWith("T"),
    //               });
    //             } else {
    //               // For index codes: prefix each code in the array
    //               // const prefixedCodes = finalCodesArray.map(code =>
    //               //   `${clusterIdToUse}${code}`
    //               // );
    //               const prefixedCodes = finalCodesArray.map(
    //                 (code) => `${code}`,
    //               );

    //               codesForTab.push({
    //                 code: `${name || ""}`,
    //                 codes: prefixedCodes,
    //                 rawName: selectedCode.rawName,
    //                 rawDescription: selectedCode.rawDescription,
    //                 // Add a flag to indicate if it starts with T
    //                 startsWithT: selectedCode.rawDescription?.startsWith("T"),
    //               });
    //             }
    //             processedKeys.add(key);
    //           }
    //         });

    //         // Now sort the codes: T codes first, then others
    //         const sortedCodesForTab = codesForTab.sort((a, b) => {
    //           if (a.startsWithT && !b.startsWithT) return -1;
    //           if (!a.startsWithT && b.startsWithT) return 1;
    //           return 0;
    //         });

    //         // Remove the temporary flag before storing
    //         const finalCodes = sortedCodesForTab.map(
    //           ({ startsWithT, ...rest }) => rest,
    //         );

    //         acc[category] = finalCodes;
    //         return acc;
    //       },
    //       {},
    //     );

    //     if (onFinalizeCode) {
    //       onFinalizeCode(allTabsData, clusterIdToUse);
    //     }

    //     // Store the final codes
    //     sessionStorage.finalCode = JSON.stringify(allTabsData);

    //     reduxDispatch(clearCodePathHistory());

    //     // Wait for fetchData1() to complete before proceeding
    //     await fetchData1();

    //     const finalcode = sessionStorage.getItem("finalCode");

    //     setfilecode(allTabsData);

    //     handleCodeAssistAdding(finalcode);
    //     setShowCodeSearch(false);
    //     //  Reset all search state so next code search starts fresh
    //     reduxDispatch(
    //       updateLocalState({
    //         searchTerm: "",
    //         initialSearchDone: false,
    //         showFinalScreen: false,
    //         selectedFinalCode: null,
    //         currentWarningMessage: null,
    //         currentCodeSearchData: [],   // ← clears old search results
    //         filteredCodes: [],           // ← clears old filtered list
    //         currentPath: [],
    //         pendingInstructions: [],
    //         currentInstructionIndex: 0,
    //         showError: false,
    //       })
    //     );
    //   } catch (error) {
    //     console.error("Error in handleFinalize:", error);
    //   } finally {
    //     setIsFetchingData1(false);
    //   }
    // }, [
    //   onFinalizeCode,
    //   setfilecode,
    //   setShowCodeSearch,
    //   localSelectedCodes,
    //   fetchData1,
    //   reduxDispatch,
    //   currentClusterId,
    //   startNewCluster,
    //   updateLocalState,
    // ]);
    const getCodePriority = (code) => {
      if (!code) return 4;

      const upper = code.toUpperCase();

      // Priority 1: T codes
      if (upper.startsWith("T")) return 1;

      // // Priority 2: C or D codes
      // if (
      //   upper.startsWith("C") ||
      //   (upper.startsWith("D") && isCancerRange(code))
      // )
      //   return 2;

      // Priority 3: codes with "/"
      if (code.includes("/")) return 3;

      // Priority 4: everything else
      return 4;
    };
    const extractPrimaryCode = (item) => {
      if (item.codes?.length) return item.codes[0];
      return item.code;
    };
    const handleFinalize = useCallback(async () => {
      try {
        reduxDispatch(handleResetSearch());
        reduxDispatch(setActiveTab("ICD 10 AM Index"));
        reduxDispatch(setActiveIcon("ICD"));
        setIsFetchingData1(true);

        let clusterIdToUse = currentClusterId;
        if (!clusterIdToUse) {
          clusterIdToUse = startNewCluster();
        }

        const allTabsData = Object.entries(localSelectedCodes).reduce(
          (acc, [category, codes]) => {
            const codesForTab = [];
            const processedKeys = new Set();

            // First, collect all codes
            codes.forEach((selectedCode) => {
              const clean = (val) => (val || "").replace(/[()]/g, "");
              const description = clean(selectedCode.rawDescription || "");
              const name = clean(selectedCode.rawName || "");
              const key = `${name}-${description}`;

              if (
                (category === "Procedure Codes" &&
                  !isCodeInAchiList(selectedCode)) ||
                !processedKeys.has(key)
              ) {
                const codesArray = description
                  .split("†")
                  .map((codePart) => codePart.trim().replace(/\*$/, ""));
                const finalCodesArray =
                  codesArray.length > 1
                    ? codesArray
                    : [description.trim().replace(/\*$/, "")];

                if (selectedCode.isFromTabular) {
                  // For tabular codes: prefix the code with cluster ID
                  const baseCode = finalCodesArray[0] || name;
                  // const prefixedCode = `${clusterIdToUse}${name}`;
                  const prefixedCode = `${name}`;

                  codesForTab.push({
                    code: prefixedCode, // This will be like "AI23.2"
                    codes: [prefixedCode],
                    rawName: selectedCode.rawName,
                    rawDescription: selectedCode.rawDescription,
                    // Add a flag to indicate if it starts with T
                    startsWithT: selectedCode.rawDescription?.startsWith("T"),
                  });
                } else {
                  // For index codes: prefix each code in the array
                  // const prefixedCodes = finalCodesArray.map(code =>
                  //   `${clusterIdToUse}${code}`
                  // );
                  const prefixedCodes = finalCodesArray.map(
                    (code) => `${code}`,
                  );
                  //  Check if rawDescription has morphology + ICD code together like "(M8000/3) C80.9"
                  const alreadyExists = (codeValue) =>
                    codesForTab.some(
                      (item) => item.rawDescription === codeValue,
                    );
                  // console.log(description, "morph");

                  const morphAndCodeMatch = description.match(
                    /^(M\d+(?:\.\d+)?\/\d)\s+([A-Z]\d+[\d.]*\*?)$/i,
                  );

                  if (morphAndCodeMatch) {
                    const morphCode = morphAndCodeMatch[1]; // "M8000/3"
                    const icdCode = morphAndCodeMatch[2]; // "C80.9"
                    if (!alreadyExists(icdCode)) {
                      codesForTab.push({
                        code: selectedCode.rawName,
                        codes: [icdCode],
                        rawName: selectedCode.rawName,
                        rawDescription: icdCode, // or customize if needed
                        startsWithT: false,
                      });
                    }

                    //  Push Morphology code separately
                    if (!alreadyExists(morphCode)) {
                      codesForTab.push({
                        code: selectedCode.rawName,
                        codes: [morphCode],
                        rawName: selectedCode.rawName,
                        rawDescription: morphCode, // or customize if needed
                        startsWithT: false,
                      });
                    }
                  } else {
                    codesForTab.push({
                      code: `${name || ""}`,
                      codes: prefixedCodes,
                      rawName: selectedCode.rawName,
                      rawDescription: selectedCode.rawDescription,
                      // Add a flag to indicate if it starts with T
                      startsWithT: selectedCode.rawDescription?.startsWith("T"),
                    });
                  }
                }
                processedKeys.add(key);
              }
            });

            // Now sort the codes: T codes first, then others
            let sortedCodesForTab = codesForTab;

            // if (
            //   category === "Principal Diagnosis" ||
            //   category === "Additional Diagnoses"
            // ) {
            //   sortedCodesForTab = codesForTab.sort((a, b) => {
            //     const codeA = extractPrimaryCode(a);
            //     const codeB = extractPrimaryCode(b);

            //     const priorityA = getCodePriority(codeA);
            //     const priorityB = getCodePriority(codeB);

            //     if (priorityA !== priorityB) {
            //       return priorityA - priorityB;
            //     }

            //     return 0;
            //   });
            // }
            if (
              category === "Principal Diagnosis" ||
              category === "Additional Diagnoses"
            ) {
              const prioritySorted = [...codesForTab].sort((a, b) => {
                const codeA = extractPrimaryCode(a);
                const codeB = extractPrimaryCode(b);

                return getCodePriority(codeA) - getCodePriority(codeB);
              });

              sortedCodesForTab = rearrangeMorphGrouping(prioritySorted);
            }
            // Remove the temporary flag before storing
            const finalCodes = sortedCodesForTab.map(
              ({ startsWithT, ...rest }) => rest,
            );

            acc[category] = finalCodes;
            return acc;
          },
          {},
        );
        /* sequencing principal and additional codes while adding to coding screen completed*/
        const principal = allTabsData["Principal Diagnosis"] || [];
        const additional = allTabsData["Additional Diagnoses"] || [];

        // remove duplicate code from additional if already in principle
        // Remove diagnosis codes from Additional if they already exist in Principal
        const principalSet = new Set(
          principal.map((item) => extractPrimaryCode(item)),
        );

        const filteredAdditional = additional.filter(
          (item) => !principalSet.has(extractPrimaryCode(item)),
        );

        // allTabsData["Additional Diagnoses"] = filteredAdditional;
        //////////////////////////////////////////
        const isTCode = (item) => {
          const code = extractPrimaryCode(item);
          return code?.startsWith("T");
        };

        // Split
        const principalT = principal.filter(isTCode);
        const principalNonT = principal.filter((item) => !isTCode(item));

        const additionalT = additional.filter(isTCode);
        const additionalNonT = additional.filter((item) => !isTCode(item));

        // Apply ONLY under your strict condition
        const shouldRearrange =
          principal.length > 1 && // more than 1 code in principal
          principalT.length > 0 && // has T code in principal
          principalNonT.length > 0 && // has non-T also in principal
          additionalT.length > 0; // additional has T code

        if (shouldRearrange) {
          allTabsData["Principal Diagnosis"] = [
            ...principalT,
            ...additionalT, // move additional T → principal
          ];

          allTabsData["Additional Diagnoses"] = [
            ...principalNonT, // move principal non-T → additional
            ...additionalNonT,
          ];
        } else {
          // IMPORTANT: preserve original data
          allTabsData["Principal Diagnosis"] = principal;
          allTabsData["Additional Diagnoses"] = filteredAdditional;
        }
        /* sequencing principal and additional codes while adding to coding screen completed */

        // Remove invalid codes where codes is empty, e.g. codes: [""]
        Object.keys(allTabsData).forEach((category) => {
          allTabsData[category] = allTabsData[category].filter(
            (item) =>
              Array.isArray(item.codes) &&
              item.codes.some((code) => code?.trim()),
          );
        });
        if (onFinalizeCode) {
          onFinalizeCode(allTabsData, clusterIdToUse);
        }

        // Store the final codes
        sessionStorage.finalCode = JSON.stringify(allTabsData);

        reduxDispatch(clearCodePathHistory());

        // Wait for fetchData1() to complete before proceeding
        await fetchData1();

        const finalcode = sessionStorage.getItem("finalCode");

        setfilecode(allTabsData);
        handleCodeAssistAdding(finalcode);
        setPathwayStartSnapshot(null);
        setPathwayHistoryCheckpoint(0);
        setShowCodeSearch(false);
        //  Reset all search state so next code search starts fresh
        reduxDispatch(
          updateLocalState({
            searchTerm: "",
            initialSearchDone: false,
            showFinalScreen: false,
            selectedFinalCode: null,
            currentWarningMessage: null,
            currentCodeSearchData: [], // ← clears old search results
            filteredCodes: [], // ← clears old filtered list
            currentPath: [],
            pendingInstructions: [],
            currentInstructionIndex: 0,
            showError: false,
          }),
        );
      } catch (error) {
        // console.error("Error in handleFinalize:", error);
      } finally {
        setIsFetchingData1(false);
      }
    }, [
      onFinalizeCode,
      setfilecode,
      setShowCodeSearch,
      localSelectedCodes,
      fetchData1,
      reduxDispatch,
      currentClusterId,
      startNewCluster,
      updateLocalState,
    ]);
    useEffect(() => {
      if (selectedIndex !== null) {
        const timer = setTimeout(() => {
          handleContinue(selectedIndex);
        }, 100);

        return () => clearTimeout(timer);
      }
    }, [selectedIndex]);
    // Keep search input always focused after any navigation/state change
    useEffect(() => {
      const timer = setTimeout(() => {
        if (!showFinalScreen) focusSearchInput();
      }, 50);
      return () => clearTimeout(timer);
    }, [initialSearchDone, filteredCodes, showFinalScreen, focusSearchInput]);

    const currentCategory = selectedCategory;
    const filterChildCodes = useCallback((term, codes) => {
      if (!term || term.length < 2) return codes;
      const normalizedTerm = normalizeSearchText(term);
      const normalizeText = normalizeSearchText;

      // Always return ALL prefix matches — never stop at exact match
      // Exact match gets sorted to top instead
      const prefixMatches = codes.filter((child) => {
        const name = normalizeText(child.rawName || child.name || "");
        const description = normalizeText(
          child.rawDescription || child.description || "",
        );
        return (
          name.startsWith(normalizedTerm) ||
          description.startsWith(normalizedTerm)
        );
      });

      if (prefixMatches.length === 0)
        return codes.filter((child) => {
          const name = normalizeText(child.rawName || child.name || "");
          const description = normalizeText(
            child.rawDescription || child.description || "",
          );
          return (
            name.includes(normalizedTerm) ||
            description.includes(normalizedTerm)
          );
        });

      // Sort exact matches to top, but keep all prefix matches visible
      return prefixMatches.sort((a, b) => {
        const aName = normalizeText(a.rawName || a.name || "");
        const bName = normalizeText(b.rawName || b.name || "");
        const aExact = aName === normalizedTerm;
        const bExact = bName === normalizedTerm;
        if (aExact && !bExact) return -1;
        if (!aExact && bExact) return 1;
        return aName.localeCompare(bName);
      });
    }, []);

    const selectDisplayedCode = useCallback(
      (targetCode, originalIndex) => {
        if (!targetCode || originalIndex === -1) return;
        reduxDispatch(setSelectedIndex(originalIndex));
        reduxDispatch(
          handleSelectedCodeDetails({
            category: selectedCategory,
            code_id:
              targetCode.isSeeAlsoOption && targetCode.referenceId
                ? targetCode.referenceId
                : targetCode.id || null,
            value: targetCode.isFromTabular
              ? targetCode.rawDescription
              : targetCode.rawName,
            isFromTabular: !!targetCode.isFromTabular,
            acs: targetCode.acs,
            nca: targetCode.nca,
            code_first_instruction: targetCode.code_first_instruction || [],
            use_additional_instruction:
              targetCode.use_additional_instruction || [],
            codealsowhenperf_instruction:
              targetCode.codealsowhenperf_instruction || [],
            children: targetCode.children || [],
            codeto_instruction: targetCode.codeto_instruction || [],
          }),
        );
        isKeyboardNavigatingRef.current = false;
        setHighlightedIndex(-1);
      },
      [reduxDispatch, selectedCategory],
    );

    const getActiveDisplayedCodes = useCallback(() => {
      const base = filteredCodes?.filter(Boolean) || [];
      return initialSearchDone && childFilterTerm
        ? filterChildCodes(childFilterTerm, base)
        : base;
    }, [filteredCodes, initialSearchDone, childFilterTerm, filterChildCodes]);

    const getDisplayLabel = useCallback((code) => {
      if (!code) return "";
      if (code._matchedVia === "abbreviation") return code._abbr || "";
      if (code.isDirectCode) return `${code.rawName} - ${code.rawDescription}`;
      if (code.isFromTabular) return code.rawDescription || code.rawName || "";
      return code.rawName || code.rawDescription || "";
    }, []);
    const handleAddDiagnosisFromFinal = useCallback(() => {
      if (!showFinalScreen) return;

      reduxDispatch(
        updateLocalState({
          ...localState,
          pendingInstructions: [],
          currentWarningMessage: null,
          nextRangeToProcess: null,
        }),
      );
      reduxDispatch(setInitialInjuryState(true));
      sessionStorage.setItem("newcycle", "false");
      handleTabChange("Additional Diagnoses");
      reduxDispatch(setInjuryFlowTracker([]));

      setTimeout(() => {
        focusSearchInput();
      }, 150);
    }, [
      showFinalScreen,
      localState,
      reduxDispatch,
      handleTabChange,
      focusSearchInput,
    ]);

    const handleAddProcedureFromFinal = useCallback(() => {
      if (!showFinalScreen) return;

      setTimeout(() => {
        reduxDispatch(setActiveTab("ACHI Index"));
        reduxDispatch(setActiveIcon("ICD"));
      }, 0);
      handleTabChange("Procedure Codes");

      setTimeout(() => {
        focusSearchInput();
      }, 150);
    }, [showFinalScreen, reduxDispatch, handleTabChange, focusSearchInput]);

// ---- The actual global handler, wired via window, not JSX onKeyDown ----
useEffect(() => {
  const handler = (e) => {
    const active = document.activeElement;
    const tag = active?.tagName;
    const inputType = tag === "INPUT" ? (active.type || "text").toLowerCase() : null;
    const isTypingField =
      tag === "TEXTAREA" ||
      (tag === "INPUT" &&
        ["text", "search", "email", "number", "password", "tel", "url"].includes(inputType));
    const isRadioTarget = tag === "INPUT" && inputType === "radio";
    const isEmptyTextField = isTypingField && !active.value;
const isCtrlAltCombo = e.ctrlKey && e.altKey && !e.shiftKey && !e.metaKey;
    const isOwnSearchInput = active === searchInputRef.current;
 
    // For any other typing field (e.g. CodeBook's input), let , and . type normally.
    const allowNavShortcut = !isTypingField || isOwnSearchInput;
 
    // Escape — explicit way to blur, still supported, but no longer required
    // for anything else to work.
    if (e.key === "Escape" && isTypingField) {
  e.preventDefault();
  // Keep this one as is - Escape should blur
  suppressAutoFocusRef.current = true;
  active.blur();
  return;
}
    if (e.altKey && !e.ctrlKey && !e.shiftKey && !e.metaKey && e.code === "KeyC") {
      e.preventDefault();
      handleResetCurrentPathway();
      return;
    }
    if (e.altKey && !e.ctrlKey && !e.shiftKey && !e.metaKey && e.code === "KeyD") {
  if (showFinalScreen) {
    e.preventDefault();
    handleAddDiagnosisFromFinal();
  }
  return;
}
if (e.altKey && !e.ctrlKey && !e.shiftKey && !e.metaKey && e.code === "KeyP") {
  if (showFinalScreen) {
    e.preventDefault();
    handleAddProcedureFromFinal();
  }
  return;
}
 
 
//     if (e.key === "Backspace" && (isEmptyTextField || !isTypingField)) {
//   if (!isBackProcessing && (currentPath?.length > 0 || selectedParent !== null || showFinalScreen)) {
//     e.preventDefault();
//     // DON'T blur the input - keep focus
//     // suppressAutoFocusRef.current = true; // REMOVE THIS LINE
//     handleBack();
//     // Refocus the input after navigation
//     setTimeout(() => {
//       const input = document.querySelector(".search-input");
//       if (input) input.focus();
//     }, 50);
//   }
//   return;
// }
 
if (e.key === "," && allowNavShortcut) {
  e.preventDefault();
  e.stopPropagation();
  if (!isBackProcessing && (currentPath?.length > 0 || selectedParent !== null || showFinalScreen)) {
    // Set a flag before calling handleBack
    window.__keyboardBack = true;
    handleBack();
    setTimeout(() => {
      window.__keyboardBack = false;
    }, 500);
  }
  return;
}
 
if (e.key === "." && allowNavShortcut) {
  e.preventDefault();
  if (forwardStackRef.current.length > 0) {
    const nextState = { ...forwardStackRef.current.pop(), selectedIndex: null };
    reduxDispatch(updateLocalState(nextState));
    pushHistoryState(nextState);
    setTimeout(focusSearchInput, 50);
  }
  return;
}
 
if (e.ctrlKey && (e.key === "ArrowLeft" || e.key === "ArrowRight")) {
  e.preventDefault();
 
  if (e.key === "ArrowLeft") {
    if (!isBackProcessing && (currentPath?.length > 0 || selectedParent !== null || showFinalScreen)) {
      handleBack();
      setTimeout(focusSearchInput, 50);
    }
  } else {
    if (forwardStackRef.current.length > 0) {
      const nextState = forwardStackRef.current.pop();
      reduxDispatch(updateLocalState(nextState));
      pushHistoryState(nextState);
      setTimeout(focusSearchInput, 50);
    }
  }
  return;
}
    const canUseListShortcuts = true;
 
  const displayedNow = getActiveDisplayedCodes();
  const hasHighlighted =
    highlightedIndex >= 0 && highlightedIndex < displayedNow.length;
 
  if ((e.key === " " || e.key === "Spacebar") && hasHighlighted) {
    if (isRadioTarget) return;
    e.preventDefault();
    const targetCode = displayedNow[highlightedIndex];
    const originalIndex = filteredCodes.findIndex(
      (c) => c.id === targetCode.id,
    );
    if (originalIndex !== -1) selectDisplayedCode(targetCode, originalIndex);
    return;
  }
// Ctrl+number OR Ctrl+Alt+number → select Nth item
const isCtrlNumberCombo =
  e.ctrlKey && !e.shiftKey && !e.metaKey && /^[0-9]$/.test(e.key);
 
if (isCtrlNumberCombo) {
  e.preventDefault();
  e.stopPropagation();
  const targetIdx = Number(e.key);
  if (targetIdx < displayedNow.length) {
    const targetCode = displayedNow[targetIdx];
    const originalIndex = filteredCodes.findIndex(
      (c) => c.id === targetCode.id,
    );
    if (originalIndex !== -1) {
      isKeyboardNavigatingRef.current = true;
      setHighlightedIndex(targetIdx);
      selectDisplayedCode(targetCode, originalIndex);
    }
  }
  return;
}
    if (e.key === "Enter" && !isTypingField) {
      const displayed = getActiveDisplayedCodes();
      if (highlightedIndex >= 0 && highlightedIndex < displayed.length) {
        e.preventDefault();
        const targetCode = displayed[highlightedIndex];
        const originalIndex = filteredCodes.findIndex((c) => c.id === targetCode.id);
        if (originalIndex !== -1) selectDisplayedCode(targetCode, originalIndex);
      }
      return;
    }
 
    // Letter-jump: same dual condition as Space.
    if (isCtrlAltCombo && /^[a-zA-Z]$/.test(e.key) && canUseListShortcuts) {
      const displayed = getActiveDisplayedCodes();
      if (!displayed.length) return;
      const letter = e.key.toLowerCase();
      const startFrom = highlightedIndex >= 0 ? highlightedIndex + 1 : 0;
      for (let i = 0; i < displayed.length; i++) {
        const idx = (startFrom + i) % displayed.length;
        const label = getDisplayLabel(displayed[idx]).trim().toLowerCase();
        if (label.startsWith(letter)) {
          e.preventDefault();
          isKeyboardNavigatingRef.current = true; // entering browse mode via letter-jump too
          setHighlightedIndex(idx);
          setTimeout(() => {
            document.getElementById(`code-${idx}`)?.scrollIntoView({ block: "nearest", behavior: "smooth" });
          }, 0);
          break;
        }
      }
    }
  };
 
  window.addEventListener("keydown", handler);
  return () => window.removeEventListener("keydown", handler);
}, [
  isBackProcessing,
  currentPath,
  selectedParent,
  showFinalScreen,
  handleBack,
  highlightedIndex,
  filteredCodes,
  getActiveDisplayedCodes,
  selectDisplayedCode,
  getDisplayLabel,
  reduxDispatch,
  focusSearchInput,
  handleResetCurrentPathway,
  handleAddDiagnosisFromFinal,
  handleAddProcedureFromFinal,
]);



    const handleGlobalListKeyDown = useCallback(
      (e) => {
        const target = e.target;
        const tag = target?.tagName;
        const inputType =
          tag === "INPUT" ? (target.type || "text").toLowerCase() : null;

        // Don't hijack anything while the user is actually typing text/codes.
        const isTypingField =
          tag === "TEXTAREA" ||
          (tag === "INPUT" &&
            [
              "text",
              "search",
              "email",
              "number",
              "password",
              "tel",
              "url",
            ].includes(inputType));
        if (isTypingField) return;

        const isRadioTarget = tag === "INPUT" && inputType === "radio";

        if (e.key === "Backspace" || e.key === ",") {
          e.preventDefault();
          if (
            !isBackProcessing &&
            (currentPath?.length > 0 ||
              selectedParent !== null ||
              showFinalScreen)
          ) {
            handleBack();
          }
          return;
        }

        if (e.key === ".") {
          e.preventDefault();
          // No-op until the user has gone Back at least once — there's nothing
          // ahead in the browser history stack yet.
          window.history.forward();
          return;
        }

        if (e.key === " " || e.key === "Spacebar") {
          if (isRadioTarget) return; // let the native radio toggle happen
          e.preventDefault();
          const displayed = getActiveDisplayedCodes();
          if (highlightedIndex >= 0 && highlightedIndex < displayed.length) {
            const targetCode = displayed[highlightedIndex];
            const originalIndex = filteredCodes.findIndex(
              (c) => c.id === targetCode.id,
            );
            if (originalIndex !== -1)
              selectDisplayedCode(targetCode, originalIndex);
          }
          return;
        }

        if (/^[a-zA-Z]$/.test(e.key)) {
          const displayed = getActiveDisplayedCodes();
          if (!displayed.length) return;
          const letter = e.key.toLowerCase();
          const startFrom = highlightedIndex >= 0 ? highlightedIndex + 1 : 0;
          for (let i = 0; i < displayed.length; i++) {
            const idx = (startFrom + i) % displayed.length;
            const label = getDisplayLabel(displayed[idx]).trim().toLowerCase();
            if (label.startsWith(letter)) {
              e.preventDefault();
              setHighlightedIndex(idx);
              setTimeout(() => {
                document.getElementById(`code-${idx}`)?.scrollIntoView({
                  block: "nearest",
                  behavior: "smooth",
                });
              }, 0);
              break;
            }
          }
          return;
        }
        // Tab / Shift+Tab: intentionally untouched — native focus order already
        // moves through the radios and buttons correctly.
      },
      [
        isBackProcessing,
        currentPath,
        selectedParent,
        showFinalScreen,
        handleBack,
        highlightedIndex,
        filteredCodes,
        getActiveDisplayedCodes,
        selectDisplayedCode,
        getDisplayLabel,
      ],
    );
    useEffect(() => {
      if (
        initialSearchDone &&
        slectedParntesandChilds &&
        slectedParntesandChilds.parentName
      ) {
        const childTermName =
          slectedParntesandChilds.rawName || slectedParntesandChilds.name || "";
        reduxDispatch(setChildFilterTerm(childTermName));
      } else {
        reduxDispatch(setChildFilterTerm(""));
      }
    }, [slectedParntesandChilds, initialSearchDone, reduxDispatch]);

    const codeList = useMemo(() => {
      const selectedCode = localState.selectedCode;

      const hasInstructions =
        (Array.isArray(slectedParntesandChilds?.code_first_instruction) &&
          slectedParntesandChilds.code_first_instruction.length > 0) ||
        (Array.isArray(slectedParntesandChilds?.use_additional_instruction) &&
          slectedParntesandChilds.use_additional_instruction.length > 0) ||
        (Array.isArray(slectedParntesandChilds?.codeto_instruction) &&
          slectedParntesandChilds.codeto_instruction.length > 0);

      const hasNextParentOptions = filteredCodes?.some(
        (code) => code.isNextParentOption,
      );

      const shouldFilterChildren = initialSearchDone && childFilterTerm;

      // Filter out codes that are in injuryCodeFlowTracker
      const filteredCodesWithoutInjuryTracker =
        filteredCodes?.filter((code) => code) || [];

      const displayedCodes = shouldFilterChildren
        ? filterChildCodes(childFilterTerm, filteredCodesWithoutInjuryTracker)
        : filteredCodesWithoutInjuryTracker;

      const getCodeId = (code) => {
        if (code.id) return code.id;
        if (code.isParentOption && code.children?.[0]?.id)
          return code.children[0].id;
        if (code.parent?.id) return code.parent.id;
        if (code.isUnspecified && code.originalCode?.id)
          return code.originalCode.id;
        return null;
      };

      return (
        <>
          {localState.currentWarningMessage && (
            <p className="text-center py-1 px-2 mb-2 small code-search-warning">
              ℹ️ {localState?.currentWarningMessage} ?
            </p>
          )}

          {displayedCodes.length > 0 ? (
            <ListGroup className="codesearch-codeassist">
              {displayedCodes.map((code, index) => {
                // const displayLabel = code.isFromTabular
                //   ? code.rawDescription || code.rawName
                //   : code.rawName || code.rawDescription;
                const displayLabel = code._displayLabelOverride
                  ? code._displayLabelOverride
                  : code._matchedVia === "abbreviation"
                    ? code._abbr
                    : code.isDirectCode
                      ? `${code.rawName} - ${code.rawDescription}`
                      : code.isFromTabular
                        ? code.rawDescription || code.rawName
                        : code.rawName || code.rawDescription;
                const typeLabel = !initialSearchDone
                  ? code._matchedVia === "abbreviation"
                    ? `Abbr: ${code._abbr}`
                    : code.see
                      ? code.see
                      : code.parentName || "Lead Term"
                  : null;
                const codeId = getCodeId(code);
                const originalIndex =
                  filteredCodesWithoutInjuryTracker.findIndex(
                    (c) => c.id === code.id,
                  );

                return (
                  <ListGroup.Item
                    // key={codeId || index}
                    key={
                      code._abbrIndex !== undefined
                        ? `abbr-${code.id}-${code._abbrIndex}`
                        : codeId || index
                    }
                    className={`d-flex align-items-center justify-content-between ${
                      index === highlightedIndex
                        ? "bg-light border-primary"
                        : ""
                    }`}
                    onClick={() => {
                      reduxDispatch(setSelectedIndex(originalIndex));
                      reduxDispatch(
                        handleSelectedCodeDetails({
                          category: selectedCategory,
                          code_id:
                            code.isSeeAlsoOption && code.referenceId
                              ? code.referenceId
                              : codeId,
                          value: code.isFromTabular
                            ? code.rawDescription
                            : code.rawName,
                          isFromTabular: !!code.isFromTabular,
                          acs: code.acs,
                          nca: code.nca,
                          code_first_instruction:
                            code.code_first_instruction || [],
                          use_additional_instruction:
                            code.use_additional_instruction || [],
                          codealsowhenperf_instruction:
                            code.codealsowhenperf_instruction || [],
                          children: code.children || [],
                          codeto_instruction: code.codeto_instruction || [],
                          manual_added: code.manual_added || [],
                        }),
                      );
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="d-flex align-items-center flex-grow-1">
                      {index < 10 && (
                        <span
                          className="code-index-badge me-2"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minWidth: "20px",
                            height: "20px",
                            borderRadius: "4px",
                            background: "#e9ecef",
                            color: "#495057",
                            fontSize: "11px",
                            fontWeight: 600,
                            flexShrink: 0,
                          }}
                          title={`Ctrl+${index} to select`}
                        >
                          {index}
                        </span>
                      )}
                      <Form.Check
                        type="radio"
                        name="codeOptions"
                        id={`code-${index}`}
                        className="list-item-noActive"
                        label={
                          code.isUnspecified || code.isSeeAlsoUnspecified
                            ? `Unspecified (${displayLabel})`
                            : code.isNextParentOption
                              ? code.rawName
                              : displayLabel
                        }
                        checked={selectedIndex === originalIndex}
                        onChange={() => {
                          reduxDispatch(setSelectedIndex(originalIndex));
                          reduxDispatch(
                            handleSelectedCodeDetails({
                              category: selectedCategory,
                              code_id:
                                code.isSeeAlsoOption && code.referenceId
                                  ? code.referenceId
                                  : codeId,
                              value: code.isFromTabular
                                ? code.rawDescription
                                : code.rawName,
                              isFromTabular: !!code.isFromTabular,
                              acs: code.acs,
                              nca: code.nca,
                              code_first_instruction:
                                code.code_first_instruction || [],
                              use_additional_instruction:
                                code.use_additional_instruction || [],
                              codealsowhenperf_instruction:
                                code.codealsowhenperf_instruction || [],
                              children: code.children || [],
                              codeto_instruction: code.codeto_instruction || [],
                              manual_added: code.manual_added || [],
                              // see:code.see,
                            }),
                          );
                          setTimeout(() => {
                            document.querySelector(".search-input")?.focus();
                          }, 150);
                        }}
                      />
                    </div>
                    {typeLabel && (
                      <small
                        className="badge rounded-pill bg-light text-muted border"
                        style={{ fontSize: "0.75rem" }}
                      >
                        {typeLabel}
                      </small>
                    )}
                  </ListGroup.Item>
                );
              })}
            </ListGroup>
          ) : shouldFilterChildren && childFilterTerm ? (
            <p className="text-muted text-center mt-4">
              No matching child codes found
            </p>
          ) : null}
        </>
      );
    }, [
      filteredCodes,
      selectedIndex,
      initialSearchDone,
      selectedCategory,
      childFilterTerm,
      filterChildCodes,
      reduxDispatch,
      localState.currentWarningMessage,
      highlightedIndex,
      injuryCodeFlowTracker, // Add this dependency
    ]);
    const getDisplayed = (term, list) =>
      term
        ? filterChildCodes(term, list?.filter(Boolean) || [])
        : list?.filter(Boolean) || [];

    const renderSearchAndList = () => (
      <>
        {!initialSearchDone && (
          <div className="search-bar-code mt-3">
            <>
              <FaSearch className="codebook-search-icon" />
              <Form.Control
                type="text"
                className="search-input"
                placeholder="Search..."
                ref={searchInputRef}
                disabled={isLockedCoder}
                onFocus={() => {
                  suppressAutoFocusRef.current = false;
                }}
                value={searchTerm}
                onChange={(e) => {
                  isKeyboardNavigatingRef.current = false;
                  const newSearchTerm = e.target.value;
                  const shouldClearResults = newSearchTerm.length < 3;

                  reduxDispatch(
                    updateLocalState({
                      searchTerm: newSearchTerm,
                      topLevelParent: null,
                      currentPath: [],
                      initialSearchDone: false,
                      filteredCodes: shouldClearResults ? [] : filteredCodes,
                    }),
                  );
                  if (newSearchTerm.length === 0) {
                    reduxDispatch(setRawSearchQuery(""));
                    reduxDispatch(setSearchByCodeId(null));
                  }
                  window.history.replaceState(
                    {
                      ...localState,
                      searchTerm: newSearchTerm,
                      topLevelParent: null,
                      currentPath: [],
                      filteredCodes: shouldClearResults ? [] : filteredCodes,
                    },
                    "",
                    window.location.pathname,
                  );
                }}
                onKeyDown={(e) => {
                  const displayed = filteredCodes;
                  const isCtrlAltCombo =
                    e.ctrlKey && e.altKey && !e.shiftKey && !e.metaKey;
                  const isCtrlNumberCombo =
                    e.ctrlKey &&
                    !e.shiftKey &&
                    !e.metaKey &&
                    /^[0-9]$/.test(e.key);

                  if (isCtrlNumberCombo && displayed.length > 0) {
                    e.preventDefault();
                    e.stopPropagation();

                    const targetIndex = parseInt(e.key, 10);
                    const targetCode = displayed[targetIndex];
                    if (!targetCode) return;

                    reduxDispatch(setSelectedIndex(targetIndex));
                    reduxDispatch(
                      handleSelectedCodeDetails({
                        category: selectedCategory,
                        code_id: targetCode.id,
                        value: targetCode.isFromTabular
                          ? targetCode.rawDescription
                          : targetCode.rawName,
                        isFromTabular: !!targetCode.isFromTabular,
                        acs: targetCode.acs,
                        nca: targetCode.nca,
                        code_first_instruction:
                          targetCode.code_first_instruction || [],
                        use_additional_instruction:
                          targetCode.use_additional_instruction || [],
                        codealsowhenperf_instruction:
                          targetCode.codealsowhenperf_instruction || [],
                        children: targetCode.children || [],
                        codeto_instruction: targetCode.codeto_instruction || [],
                        manual_added: targetCode.manual_added || [],
                      }),
                    );
                    setHighlightedIndex(-1);
                    return;
                  }
                  // For initial search, we need to filter the codes based on search term
                  if (displayed.length === 0) return;
                  if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                    e.preventDefault();
                    e.stopPropagation();
                    isKeyboardNavigatingRef.current = true;
                    setHighlightedIndex((prev) => {
                      let next;
                      if (e.key === "ArrowDown") {
                        next = (prev + 1) % displayed.length;
                      } else {
                        next = (prev - 1 + displayed.length) % displayed.length;
                      }

                      // console.log("Navigation - from:", prev, "to:", next);

                      setTimeout(() => {
                        const element = document.getElementById(`code-${next}`);
                        if (element) {
                          element.scrollIntoView({
                            block: "nearest",
                            behavior: "smooth",
                          });
                        }
                      }, 0);

                      return next;
                    });

                    return;
                  }

                  // ENTER selection
                  if (e.key === "Enter") {
                    e.preventDefault();
                    e.stopPropagation();
                    let targetIndex = highlightedIndex;

                    // If nothing is highlighted and there are items, select the first one
                    if (targetIndex === -1 && displayed.length > 0) {
                      targetIndex = 0;
                    }

                    if (targetIndex === -1 || targetIndex >= displayed.length) {
                      return;
                    }

                    // Get the code directly from displayed
                    const targetCode = displayed[targetIndex];

                    if (!targetCode) {
                      return;
                    }

                    // Use the targetIndex directly since displayed IS filteredCodes
                    const originalIndex = targetIndex;

                    if (
                      originalIndex !== -1 &&
                      originalIndex < filteredCodes.length
                    ) {
                      reduxDispatch(setSelectedIndex(originalIndex));

                      reduxDispatch(
                        handleSelectedCodeDetails({
                          category: selectedCategory,
                          code_id: targetCode.id,
                          value: targetCode.isFromTabular
                            ? targetCode.rawDescription
                            : targetCode.rawName,
                          isFromTabular: !!targetCode.isFromTabular,
                          acs: targetCode.acs,
                          nca: targetCode.nca,
                          code_first_instruction:
                            targetCode.code_first_instruction || [],
                          use_additional_instruction:
                            targetCode.use_additional_instruction || [],
                          codealsowhenperf_instruction:
                            targetCode.codealsowhenperf_instruction || [],
                          children: targetCode.children || [],
                          codeto_instruction:
                            targetCode.codeto_instruction || [],
                          manual_added: targetCode.manual_added || [],
                        }),
                      );

                      setHighlightedIndex(-1);
                    } else {
                      // console.log("Invalid originalIndex:", originalIndex);
                    }
                  }
                }}
              />
            </>
          </div>
        )}
        {selectedParent && initialSearchDone && topLevelParent && (
          <div className="selected-parent-codeassist">
            <p>
              {slectedParntesandChilds?._type === "child" ? (
                <>
                  <span
                    style={{
                      fontWeight: "bold",
                      fontSize: "15px",
                      color: "black",
                    }}
                  >
                    Selected Parent:
                  </span>{" "}
                  {topLevelParent?.parentName}
                </>
              ) : (
                <>
                  <span
                    style={{
                      fontWeight: "bold",
                      fontSize: "15px",
                      color: "black",
                    }}
                  >
                    Selected Parent:
                  </span>
                  {selectedParent.isFromTabular
                    ? selectedParent.rawDescription || selectedParent.rawName
                    : selectedParent.rawName || selectedParent.rawDescription}
                </>
              )}
            </p>
          </div>
        )}

        {initialSearchDone && (
          <div className="search-bar-code mt-3">
            <FaSearch className="codebook-search-icon" />
            <Form.Control
              type="text"
              className="search-input"
              ref={searchInputRef}
              onFocus={() => {
                suppressAutoFocusRef.current = false;
              }}
              placeholder="Search..."
              value={childFilterTerm}
              // autoFocus // ← handles the auto-focus suggestion too
              onChange={(e) =>
                reduxDispatch(setChildFilterTerm(e.target.value))
              }
              onKeyDown={(e) => {
                const displayed = getDisplayed(childFilterTerm, filteredCodes);

                // Digit selection — must be FIRST, before anything else touches the input
                const isCtrlNumberCombo =
                  e.ctrlKey &&
                  !e.shiftKey &&
                  !e.metaKey &&
                  /^[0-9]$/.test(e.key);

                if (isCtrlNumberCombo && displayed.length > 0) {
                  e.preventDefault();
                  e.stopPropagation();

                  const targetIndex = parseInt(e.key, 10);
                  const targetCode = displayed[targetIndex];
                  if (!targetCode) return;

                  const originalIndex = filteredCodes.findIndex(
                    (c) => c.id === targetCode.id,
                  );
                  if (originalIndex === -1) return;

                  reduxDispatch(setSelectedIndex(originalIndex));
                  reduxDispatch(
                    handleSelectedCodeDetails({
                      category: selectedCategory,
                      code_id: targetCode.id,
                      value: targetCode.isFromTabular
                        ? targetCode.rawDescription
                        : targetCode.rawName,
                      isFromTabular: !!targetCode.isFromTabular,
                      acs: targetCode.acs,
                      nca: targetCode.nca,
                      code_first_instruction:
                        targetCode.code_first_instruction || [],
                      use_additional_instruction:
                        targetCode.use_additional_instruction || [],
                      codealsowhenperf_instruction:
                        targetCode.codealsowhenperf_instruction || [],
                      children: targetCode.children || [],
                      codeto_instruction: targetCode.codeto_instruction || [],
                      manual_added: targetCode.manual_added || [],
                    }),
                  );
                  setHighlightedIndex(-1);
                  return;
                }

                if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                  e.preventDefault();
                  if (displayed.length === 0) return;

                  setHighlightedIndex((prev) => {
                    const next =
                      e.key === "ArrowDown"
                        ? prev < displayed.length - 1
                          ? prev + 1
                          : 0
                        : prev > 0
                          ? prev - 1
                          : displayed.length - 1;

                    // Use `next` not `highlightedIndex` (stale closure)
                    setTimeout(() => {
                      document
                        .getElementById(`code-${next}`)
                        ?.scrollIntoView({ block: "nearest" });
                    }, 0);

                    return next;
                  });

                  // Scroll into view
                  setTimeout(() => {
                    document
                      .getElementById(`code-${highlightedIndex}`)
                      ?.scrollIntoView({ block: "nearest" });
                  }, 0);
                }

                if (e.key === "Enter") {
                  // Pick highlighted item if arrow-navigated, else pick if only 1 result
                  const targetIndex =
                    highlightedIndex >= 0
                      ? highlightedIndex
                      : displayed.length === 1
                        ? 0
                        : -1;
                  if (targetIndex === -1) return;

                  const targetCode = displayed[targetIndex];
                  const originalIndex = filteredCodes.findIndex(
                    (c) => c.id === targetCode.id,
                  );

                  if (originalIndex !== -1) {
                    reduxDispatch(setSelectedIndex(originalIndex));
                    reduxDispatch(
                      handleSelectedCodeDetails({
                        category: selectedCategory,
                        code_id: targetCode.id,
                        value: targetCode.isFromTabular
                          ? targetCode.rawDescription
                          : targetCode.rawName,
                        isFromTabular: !!targetCode.isFromTabular,
                        acs: targetCode.acs,
                        nca: targetCode.nca,
                        code_first_instruction:
                          targetCode.code_first_instruction || [],
                        use_additional_instruction:
                          targetCode.use_additional_instruction || [],
                        codealsowhenperf_instruction:
                          targetCode.codealsowhenperf_instruction || [],
                        children: targetCode.children || [],
                        codeto_instruction: targetCode.codeto_instruction || [],
                        manual_added: targetCode.manual_added || [],
                      }),
                    );
                    setHighlightedIndex(-1); // reset after selection
                  }
                }
              }}
            />
          </div>
        )}

        {codeList}
        {searchTerm.length >= 3 && filteredCodes.length === 0 && (
          <p className="text-muted text-center mt-4">No results found</p>
        )}
      </>
    );

    const handleCodeSearchRef = (acsCode) => {
      reduxDispatch(updateAcsCode(null));
      setTimeout(() => {
        reduxDispatch(updateAcsCode(acsCode));
      }, 0);
      reduxDispatch(setActiveTab("Australian Coding Standard"));
      reduxDispatch(setActiveIcon("Coding"));
    };
    const handleNationalSearchRef = (ncaCode) => {
      reduxDispatch(updateNcaCode(null));
      setTimeout(() => {
        reduxDispatch(updateNcaCode(ncaCode));
      }, 0);
      reduxDispatch(setActiveIcon("References"));
      reduxDispatch(setActiveTab("National Coding Advice"));
    };

    const findCodeDescriptionInTabular = useCallback(
      (codeName, category) => {
        const cleanedCode = codeName?.replace(/[()]/g, "");
        if (category === "Procedure Codes") {
          return findCodeInTabularData(cleanedCode, achiTabularData13);
        } else {
          return findCodeInTabularData(cleanedCode, icd10amTabularData13);
        }
      },
      [icd10amTabularData13, achiTabularData13],
    );
    // Enhanced function to find code details including ACS and NCA
    const findCodeDetailsInTabular = useCallback(
      (codeName, category) => {
        const cleanedCode = codeName?.replace(/[()]/g, "");
        if (category === "Procedure Codes") {
          return findCodeInTabularDataWithDetails(
            cleanedCode,
            achiTabularData13,
          );
        } else {
          return findCodeInTabularDataWithDetails(
            cleanedCode,
            icd10amTabularData13,
          );
        }
      },
      [icd10amTabularData13, achiTabularData13],
    );

    const findCodeInTabularDataWithDetails = (codeName, tabularData) => {
      if (!tabularData || !Array.isArray(tabularData)) return null;

      const normalizedCode = codeName?.trim().toUpperCase();
      if (!normalizedCode) return null;

      let exactMatch = null;
      let rangeMatch = null;
      let acsInfo = null;
      let ncaInfo = null;

      const searchRecursive = (items) => {
        for (const item of items) {
          const itemCode = item.name?.trim().toUpperCase();
          const itemDesc = item.description || item.rawDescription;

          // 1. Check for EXACT match first
          if (itemCode === normalizedCode) {
            exactMatch = itemDesc;
            // Capture ACS and NCA from the matching item
            if (item.acs) acsInfo = item.acs;
            if (item.nca) ncaInfo = item.nca;
            return true; // Stop searching when exact match found
          }

          // 2. Check for range match
          if (itemCode && itemCode.includes("-")) {
            const [rangeStart, rangeEnd] = itemCode
              .split("-")
              .map((part) => part.trim().toUpperCase());

            if (
              rangeStart &&
              rangeEnd &&
              normalizedCode >= rangeStart &&
              normalizedCode <= rangeEnd
            ) {
              rangeMatch = itemDesc;
              // For range matches, ACS/NCA might be at the range level
              if (item.acs) acsInfo = item.acs;
              if (item.nca) ncaInfo = item.nca;
              // Continue searching for exact match within the range
            }
          }

          // Continue searching in nested structures
          if (item.children) {
            if (searchRecursive(item.children)) return true;
          }
          if (item.subcodes) {
            if (searchRecursive(item.subcodes)) return true;
          }
          if (item.codes) {
            if (searchRecursive(item.codes)) return true;
          }
        }
        return false;
      };

      searchRecursive(tabularData);

      // Return description first, then range match, along with ACS/NCA info
      return {
        description: exactMatch || rangeMatch || null,
        acs: acsInfo,
        nca: ncaInfo,
      };
    };

    const findCodeInTabularData = (codeName, tabularData) => {
      if (!tabularData || !Array.isArray(tabularData)) return null;

      const normalizedCode = codeName?.trim().toUpperCase();
      if (!normalizedCode) return null;

      let exactMatch = null;
      let rangeMatch = null;

      const searchRecursive = (items) => {
        for (const item of items) {
          const itemCode = item.name?.trim().toUpperCase();
          const itemDesc = item.description || item.rawDescription;

          // 1. Check for EXACT match first
          if (itemCode === normalizedCode) {
            exactMatch = itemDesc;
            return true; // Stop searching when exact match found
          }

          // 2. Check for range match (but don't stop searching yet)
          if (itemCode && itemCode.includes("-")) {
            const [rangeStart, rangeEnd] = itemCode
              .split("-")
              .map((part) => part.trim().toUpperCase());

            if (
              rangeStart &&
              rangeEnd &&
              normalizedCode >= rangeStart &&
              normalizedCode <= rangeEnd
            ) {
              rangeMatch = itemDesc; // Store range match but keep looking for exact
            }
          }

          // Continue searching in nested structures
          if (item.children) {
            if (searchRecursive(item.children)) return true;
          }
          if (item.subcodes) {
            if (searchRecursive(item.subcodes)) return true;
          }
          if (item.codes) {
            if (searchRecursive(item.codes)) return true;
          }
        }
        return false;
      };

      searchRecursive(tabularData);

      // Return exact match first, then range match, then null
      return exactMatch || rangeMatch || null;
    };
    const rearrangeTCodesFirst = (codes = []) => {
      const tCodes = [];
      const otherCodes = [];
      const CdCodes = [];
      const mCodes = [];

      codes.forEach((code) => {
        if (code.rawDescription?.startsWith("T")) {
          tCodes.push(code);
        }
        // else if (
        //   code.rawDescription?.startsWith("C") ||
        //   (code.rawDescription?.startsWith("D") &&
        //     isCancerRange(code.rawDescription))
        // ) {
        //   CdCodes.push(code);
        // } else if (/^\(M\d+\/\d\)/i.test(code.rawName)) {
        //   mCodes.push(code);
        // }
        else {
          otherCodes.push(code);
        }
      });

      return [...tCodes, ...otherCodes];
    };

    // const rearrangeCodesByPriority = (codes) => {
    //   const getPriority = (code) => {
    //     let value = code.isFromTabular ? code.rawName : code.rawDescription;

    //     value = (value || "").replace(/[()]/g, "").trim();

    //     if (value.startsWith("T")) return 1;
    //     // if (
    //     //   (value.startsWith("C") || value.startsWith("D")) &&
    //     //   isCancerRange(value)
    //     // ) {
    //     //   return 2;
    //     // }
    //     // if (code.isMorphologyCode || /^M\d+\/\d+/i.test(value)) {
    //     //   return 3;
    //     // }
    //     return 2;
    //   };

    //   return [...codes].sort((a, b) => {
    //     const priorityDiff = getPriority(a) - getPriority(b);

    //     if (priorityDiff !== 0) return priorityDiff;

    //     return (a.originalIndex ?? 0) - (b.originalIndex ?? 0);
    //   });
    // };

    const rearrangeByMorphGroup = (codes) => {
      if (!codes || codes.length === 0) return codes;

      // ─── STEP 1: Separate T codes out first (highest priority) ───────────────
      const tCodes = codes.filter((code) => {
        const val = code.isFromTabular
          ? code.rawName || ""
          : code.rawDescription || "";
        return val.replace(/[()]/g, "").trim().startsWith("T");
      });

      const nonTCodes = codes.filter((code) => {
        const val = code.isFromTabular
          ? code.rawName || ""
          : code.rawDescription || "";
        return !val.replace(/[()]/g, "").trim().startsWith("T");
      });

      // ─── STEP 2: Run morph grouping ONLY on non-T codes ──────────────────────
      const rearranged = rearrangeMorphGrouping(nonTCodes);

      // ─── STEP 3: T codes first, then morph-grouped rest ──────────────────────
      return [...tCodes, ...rearranged];
    };
    //     // ─── Internal morph grouping (same logic, now separated cleanly) ─────────────
    const BEHAVIOUR_RANGES = {
      "/0": [{ from: "D10", to: "D36.9" }],
      "/1": [{ from: "D37", to: "D48.9" }],
      "/2": [{ from: "D00", to: "D09.9" }],
      "/3": [
        { from: "C00", to: "C76.9" },
        { from: "C80", to: "C96.9" },
      ],
      "/6": [{ from: "C77", to: "C79.9" }],
    };

    const isMorphCodeCheck = (code) => {
      const raw = (code.rawDescription || code.rawName || "")
        .replace(/[()]/g, "")
        .trim();
      return (
        code.isMorphologyCode === true ||
        code.isMorphologyPart === true ||
        /^M\d+\/\d+/i.test(raw)
      );
    };

    const getMorphSuffix = (code) => {
      const raw = (code.rawDescription || code.rawName || "")
        .replace(/[()]/g, "")
        .trim();
      const m = raw.match(/M\d+\/(\d+)/i);
      return m ? `/${m[1]}` : null;
    };

    const getIcdValue = (code) => {
      const val = code.isFromTabular
        ? code.rawName || ""
        : code.rawDescription || "";
      return val.replace(/[()†*\-]/g, "").trim();
    };

    const icdInRange = (icdValue, rangeList) => {
      const base = icdValue.match(/^([A-Z])(\d+)/i);
      if (!base) return false;
      const letter = base[1].toUpperCase();
      const num = parseFloat(base[2]);
      return rangeList.some(({ from, to }) => {
        return (
          letter === from[0].toUpperCase() &&
          letter === to[0].toUpperCase() &&
          num >= parseFloat(from.slice(1)) &&
          num <= parseFloat(to.slice(1))
        );
      });
    };

    const getMatchingSuffix = (code, presentSuffixes) => {
      const val = getIcdValue(code);
      if (!val) return null;
      for (const suffix of presentSuffixes) {
        const ranges = BEHAVIOUR_RANGES[suffix];
        if (ranges && icdInRange(val, ranges)) return suffix;
      }
      return null;
    };

    const rearrangeMorphGrouping = (codes) => {
      if (!codes || codes.length === 0) return codes;

      // Separate morphs and non-morphs, preserving original indices
      const morphItems = [];
      const icdItems = []; // { item, originalIndex }

      codes.forEach((item, idx) => {
        if (isMorphCodeCheck(item)) {
          morphItems.push({ item, originalIndex: idx });
        } else {
          icdItems.push({ item, originalIndex: idx });
        }
      });

      // For each morph, find the BEST matching ICD
      // "Best" = closest ICD in original order (before or after),
      // preferring the one immediately before (natural pairing)
      const morphToIcd = new Map(); // morphIndex -> icdOriginalIndex it belongs to

      morphItems.forEach(({ item: morph, originalIndex: morphIdx }) => {
        const suffix = getMorphSuffix(morph);
        const ranges = BEHAVIOUR_RANGES[suffix];
        if (!ranges) return;

        // Find all ICDs that match this morph's range
        const matchingIcds = icdItems.filter(({ item: icd }) => {
          const icdVal = getIcdValue(icd);
          return /^[A-Z]\d{2}/i.test(icdVal) && icdInRange(icdVal, ranges);
        });

        if (matchingIcds.length === 0) return;

        // Pick the closest ICD to this morph in original order
        // Prefer ICD that comes just before the morph (most natural grouping)
        let bestIcd = null;
        let bestDistance = Infinity;
        let bestIsBefore = false;

        matchingIcds.forEach(({ item: icd, originalIndex: icdIdx }) => {
          const distance = Math.abs(icdIdx - morphIdx);
          const isBefore = icdIdx < morphIdx;

          // Prefer closer; if equal distance, prefer the one before
          if (
            distance < bestDistance ||
            (distance === bestDistance && isBefore && !bestIsBefore)
          ) {
            bestDistance = distance;
            bestIcd = icdIdx;
            bestIsBefore = isBefore;
          }
        });

        if (bestIcd !== null) {
          morphToIcd.set(morphIdx, bestIcd);
        }
      });

      // Build result: walk ICD items in original order,
      // after each ICD, inject its matched morphs
      const result = [];
      const usedMorphIndices = new Set();

      // Build reverse map: icdOriginalIndex -> morph items
      const icdToMorphs = new Map();
      morphToIcd.forEach((icdOrigIdx, morphOrigIdx) => {
        if (!icdToMorphs.has(icdOrigIdx)) icdToMorphs.set(icdOrigIdx, []);
        icdToMorphs.get(icdOrigIdx).push(morphOrigIdx);
      });
      // ICDs that already have a morphology assigned
      const icdsWithMorphs = new Set([...morphToIcd.values()]);
      const processedIcds = new Set();
      icdItems.forEach(({ item: icd, originalIndex: icdIdx }) => {
        if (processedIcds.has(icdIdx)) return;

        result.push(icd);
        processedIcds.add(icdIdx);

        const matchedMorphIndices = icdToMorphs.get(icdIdx) || [];

        matchedMorphIndices
          .sort((a, b) => a - b)
          .forEach((morphOrigIdx) => {
            const morphItem = morphItems.find(
              (m) => m.originalIndex === morphOrigIdx,
            );

            if (!morphItem) return;

            const suffix = getMorphSuffix(morphItem.item);
            const ranges = BEHAVIOUR_RANGES[suffix];

            // Find cancer ICDs that belong to same behaviour
            // but do not already have their own morphology
            const extraIcds = icdItems.filter(({ item, originalIndex }) => {
              if (
                originalIndex === icdIdx ||
                icdsWithMorphs.has(originalIndex) ||
                processedIcds.has(originalIndex)
              ) {
                return false;
              }

              const icdVal = getIcdValue(item);

              return /^[A-Z]\d{2}/i.test(icdVal) && icdInRange(icdVal, ranges);
            });

            // Insert additional cancers BEFORE morphology
            extraIcds.forEach(({ item, originalIndex }) => {
              result.push(item);
              processedIcds.add(originalIndex);
            });

            if (!usedMorphIndices.has(morphOrigIdx)) {
              result.push(morphItem.item);
              usedMorphIndices.add(morphOrigIdx);
            }
          });
      });

      // Append any morphs that didn't match any ICD (orphan morphs)
      morphItems.forEach(({ item: morph, originalIndex: morphOrigIdx }) => {
        if (!usedMorphIndices.has(morphOrigIdx)) {
          result.push(morph);
        }
      });

      return result;
    };
    const renderFinalScreen = () => {
      let hasDisplayError = showError;

      // Enhanced deduplication function
      const principalCodes = localSelectedCodes?.["Principal Diagnosis"] || [];

      // this for no duplicate code repeat in the additional if alraedy exist in the principle
      const getDiagnosisCode = (code) => {
        return code.isFromTabular
          ? code.rawName?.trim()
          : code.rawDescription?.trim();
      };

      const principalDiagnosisSet = new Set(
        principalCodes.map(getDiagnosisCode),
      );

      const isPrincipalDiagnosisEmpty = principalCodes.length === 0;
      const getUniqueCodes = (codes, category) => {
        if (category === "Procedure Codes") {
          const seen = new Set();
          return codes
            .filter((code) => {
              if (!isCodeInAchiList(code)) return true; // not in list → always allow (repeats OK)
              const key = code.rawName || code.rawDescription;
              if (seen.has(key)) return false; // in list → dedupe
              seen.add(key);
              return true;
            })
            .map((code, index) => ({ ...code, originalIndex: index }));
        }
        // FIRST: Remove duplicates based on rawDescription
        const uniqueByDescription = [];
        const seenDescriptions = new Set();

        for (const code of codes) {
          const descKey = code.rawDescription || "";
          if (!seenDescriptions.has(descKey)) {
            seenDescriptions.add(descKey);
            uniqueByDescription.push(code);
          }
        }
        // Use the deduplicated array for the rest of your logic
        const dedupedCodes = uniqueByDescription;

        const seen = new Map(); // Use Map to store by normalized key
        const uniqueCodes = [];
        const specialCodeGroups = new Map();

        if (localState?.originalSpecialCodesWithSymbols?.length) {
          localState.originalSpecialCodesWithSymbols.forEach((specialCode) => {
            const match = specialCode.match(/^([A-Z]\d+(?:\.\d+)?)([-*†]?)$/i);
            if (match) {
              const base = match[1];
              const symbol = match[2] || "";
              const normalizedBase = base.replace(/(\.\d)0*$/, "$1");

              if (!specialCodeGroups.has(normalizedBase)) {
                specialCodeGroups.set(normalizedBase, []);
              }
              specialCodeGroups
                .get(normalizedBase)
                .push({ base, symbol, original: specialCode });
            }
          });
        }

        const expandedCodes = dedupedCodes.flatMap((code) => {
          const raw = (code.rawDescription || "").trim();
          const match = raw.match(/^(\(?M\d+\/\d+\)?)\s+([A-Z]\d+[\d.]*)$/i);
          if (!match) return [code];

          const morphCode = match[1]; // M8000/3 or (M8000/3)
          const icdCode = match[2]; // C80.9

          return [
            { ...code, rawDescription: icdCode, isMorphologyPart: false },
            {
              ...code,
              rawDescription: morphCode,
              isMorphologyPart: true,
              isMorphologyCode: true,
            },
          ];
        });

        expandedCodes.forEach((code, index) => {
          // Handle special code symbols
          if (code?.rawName && specialCodeGroups.size > 0) {
            const currentName = code.rawName;
            if (code.isFromTabular) {
              const baseMatch = currentName.match(/^([A-Z]\d+(?:\.\d+)?)/i);
              if (baseMatch) {
                const tabularBase = baseMatch[1];
                const normalizedTabularBase = tabularBase.replace(
                  /(\.\d)0*$/,
                  "$1",
                );

                if (specialCodeGroups.has(normalizedTabularBase)) {
                  const specialCodes = specialCodeGroups.get(
                    normalizedTabularBase,
                  );
                  let bestSymbol = "";
                  let bestSpecialCode = null;

                  specialCodes.forEach((special) => {
                    if (special.symbol === "*") {
                      bestSymbol = "*";
                      bestSpecialCode = special;
                    } else if (special.symbol === "†" && bestSymbol !== "*") {
                      bestSymbol = "†";
                      bestSpecialCode = special;
                    }
                  });

                  if (bestSymbol) {
                    let newName = currentName;
                    if (!newName.endsWith(bestSymbol)) {
                      newName = newName + bestSymbol;
                    }
                    code = {
                      ...code,
                      rawName: newName,
                    };
                  }
                }
              }
            }
          }

          // Create a normalized key for deduplication
          let key;

          // Check if this is a morphology code
          const isMorphCode =
            code.isMorphologyCode ||
            code.isMorphologyPart ||
            (code.rawDescription && /^M\d+\/\d+/.test(code.rawDescription));

          if (isMorphCode) {
            // Normalize morphology code by removing parentheses and standardizing format
            const morphValue = code.rawDescription || code.rawName || "";
            const cleanMorph = morphValue.replace(/[()]/g, "").trim();
            key = `${category}-morphology-${cleanMorph}`;
          } else {
            // Regular code deduplication
            key = code.isFromTabular
              ? `${category}-${code.rawName}-${code.rawDescription}`
              : `${category}-${code.rawDescription}-${code.rawName}`;
          }

          if (!seen.has(key)) {
            seen.set(key, true);
            uniqueCodes.push({ ...code, originalIndex: index });
          }
        });
        return uniqueCodes;
      };

      const handleDeleteCode = (category, displayIndex) => {
        const updatedCodes = { ...localSelectedCodes };

        // Remove the code from the specified category
        updatedCodes[category] = updatedCodes[category].filter(
          (_, index) => index !== displayIndex,
        );

        // If we deleted from Principal Diagnosis and it becomes empty
        if (
          category === "Principal Diagnosis" &&
          updatedCodes["Principal Diagnosis"].length === 0
        ) {
          // Clear code path history
          reduxDispatch(clearCodePathHistory());

          // Check if there are codes in Additional Diagnoses
          const additionalCodes = updatedCodes["Additional Diagnoses"] || [];

          if (additionalCodes.length > 0) {
            // Move the first code from Additional Diagnoses to Principal Diagnosis
            const firstAdditionalCode = additionalCodes[0];
            const remainingAdditionalCodes = additionalCodes.slice(1);

            // Update Principal Diagnosis with the moved code
            updatedCodes["Principal Diagnosis"] = [firstAdditionalCode];

            // Update Additional Diagnosis with remaining codes
            updatedCodes["Additional Diagnoses"] = remainingAdditionalCodes;
          }
        }

        // Also handle the case where Principal is empty after other operations
        if (
          (!updatedCodes["Principal Diagnosis"] ||
            updatedCodes["Principal Diagnosis"].length === 0) &&
          updatedCodes["Additional Diagnoses"] &&
          updatedCodes["Additional Diagnoses"].length > 0
        ) {
          // Move the first Additional code to Principal
          const firstAdditionalCode = updatedCodes["Additional Diagnoses"][0];
          const remainingAdditionalCodes =
            updatedCodes["Additional Diagnoses"].slice(1);

          updatedCodes["Principal Diagnosis"] = [firstAdditionalCode];
          updatedCodes["Additional Diagnoses"] = remainingAdditionalCodes;
        }

        // Check if ALL codes across every category are now empty
        const allCodesGone = Object.values(updatedCodes).every(
          (codes) => !codes || codes.length === 0,
        );

        if (allCodesGone) {
          reduxDispatch(
            updateLocalState({
              selectedCodes: updatedCodes,
              showFinalScreen: false,
              searchTerm: "",
              selectedIndex: null,
              filteredCodes: [],
              currentCodeSearchData: null,
              initialSearchDone: false,
              selectedParent: null,
              topLevelParent: null,
              lastAddedCode: null,
              originalCode: null,
              currentPath: [],
              pendingInstructions: [],
              currentInstructionIndex: 0,
              currentWarningMessage: null,
              processingSpecialCodes: false,
              specialCodesToProcess: [],
              selectedCategory: "Principal Diagnosis",
            }),
          );
          reduxDispatch(clearCodePathHistory());
          reduxDispatch(setRawSearchQuery(""));
          reduxDispatch(setSearchByCodeId(null));
          reduxDispatch(handleResetSelectedDetails());
          reduxDispatch(resetInstructions());
          reduxDispatch(clearWarningMessage());
          reduxDispatch(setInjuryFlowTracker([]));
          reduxDispatch(setInjuryCodeFlowTracker([]));
          reduxDispatch(setInitialInjuryState(false));
          reduxDispatch(setIsInjuryWithoutActivityFlow(false));
          reduxDispatch(setFinalRangeExecuted(false));
          reduxDispatch(setPendingInjuryAfterInstructions(false));
          reduxDispatch(setPendingInjuryCode(null));
        } else {
          reduxDispatch(
            updateLocalState({
              selectedCodes: updatedCodes,
            }),
          );
        }
      };

      return (
        <div>
          {/* <ToastContainer autoClose={5000}/> */}
          {/* {hasDisplayError && (
            <p
              className="alert alert-danger text-center py-1 px-2 mb-2 small"
              role="alert"
            >
              ⚠️ selected value has a null code!
            </p>
          )} */}
          <div className="mt-2 finalcodesearchbuttons codesearch-codeassist">
            {Object.entries(localSelectedCodes)
              .filter(([_, codes]) => codes && codes.length > 0)
              .sort(([categoryA], [categoryB]) => {
                // Define the desired order
                const order = {
                  "Principal Diagnosis": 1,
                  "Additional Diagnoses": 2,
                  "Procedure Codes": 3,
                };

                // Get order values (default to 99 if   category not in order)
                const orderA = order[categoryA] || 99;
                const orderB = order[categoryB] || 99;

                return orderA - orderB;
              })
              .map(([category, codes]) => {
                // this for no duplicate code repeat in the additional if alraedy exist in the principle
                let displayCodes = codes;
                if (category === "Additional Diagnoses") {
                  displayCodes = codes.filter(
                    (code) =>
                      !principalDiagnosisSet.has(getDiagnosisCode(code)),
                  );
                  // if (displayCodes.length < codes.length) {
                  //   toast.warning("Code already exists in Principal Diagnosis.");
                  // }
                }
                // Don't render the category if nothing is left
                if (displayCodes.length === 0) {
                  return null;
                }

                const uniqueCodesRaw = getUniqueCodes(displayCodes, category);
                const uniqueCodes = rearrangeByMorphGroup(uniqueCodesRaw);
                // {console.log(localSelectedCodes,"localSelectedCodes")}
                return (
                  <div
                    key={category}
                    className="mb-3 codesearch-tab-name-change"
                  >
                    <p className="selectedcategorycodesearch">{category}</p>
                    <ListGroup>
                      {uniqueCodes.map((code, displayIndex) => {
                        const hasInvalidFields =
                          !code.rawName?.trim() || !code.rawDescription?.trim();
                        if (hasInvalidFields) {
                          if (!hasDisplayError) {
                            hasDisplayError = true;
                            reduxDispatch(
                              updateLocalState({ showError: true }),
                            );
                            setTimeout(() => {
                              reduxDispatch(
                                updateLocalState({ showError: false }),
                              );
                            }, 3000);
                          }
                          return null;
                        }

                        // Get code details including ACS and NCA from tabular data
                        const lookupKey = code.isFromTabular
                          ? code.rawName
                          : code.rawDescription;
                        const codeDetails = findCodeDetailsInTabular(
                          lookupKey,
                          category,
                        );

                        const finalDescription =
                          codeDetails?.description || code.rawDescription;
                        const codeDisplay = code.rawDescription;

                        // Prioritize tabular lookup, fallback to direct code values
                        const acsValue = codeDetails?.acs || code.acs;
                        const ncaValue = codeDetails?.nca || code.nca;

                        const hasInvalidDisplay =
                          !finalDescription?.trim() || !codeDisplay?.trim();
                        if (hasInvalidDisplay) {
                          if (!hasDisplayError) {
                            hasDisplayError = true;
                            reduxDispatch(
                              updateLocalState({ showError: true }),
                            );
                            setTimeout(() => {
                              reduxDispatch(
                                updateLocalState({ showError: false }),
                              );
                            }, 3000);
                          }
                          return null;
                        }

                        return (
                          <ListGroup.Item
                            key={`${category}-${displayIndex}`}
                            className="code-description-item d-flex justify-content-between align-items-start"
                          >
                            <div className="d-flex flex-column w-100">
                              <div className="d-flex justify-content-between align-items-center">
                                {!code.isFromTabular ? (
                                  <>
                                    <span className="description-span fw-bold">
                                      {codeDisplay}
                                    </span>
                                    <span className="code-span text-muted">
                                      {finalDescription}
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    <span className="description-span fw-bold">
                                      {code.rawName}
                                    </span>
                                    <span className="code-span text-muted">
                                      {codeDisplay}
                                    </span>
                                  </>
                                )}

                                <div className="d-flex gap-2">
                                  {/* ACS button - show if acsValue exists */}
                                  {acsValue && (
                                    <button
                                      className="acs-btn"
                                      onClick={() =>
                                        handleCodeSearchRef(acsValue)
                                      }
                                      title={`ACS Reference: ${acsValue}`}
                                    >
                                      ACS
                                    </button>
                                  )}

                                  {/* NCA button - show if ncaValue exists */}
                                  {ncaValue && (
                                    <button
                                      className="nca-btn"
                                      onClick={() =>
                                        handleNationalSearchRef(ncaValue)
                                      }
                                      title={`NCA Reference: ${ncaValue}`}
                                    >
                                      Advice
                                    </button>
                                  )}
                                  <DeleteOutlineIcon
                                    style={{
                                      cursor: "pointer",
                                      color: "#dc3545",
                                      fontSize: "20px",
                                    }}
                                    onClick={() =>
                                      handleDeleteCode(category, displayIndex)
                                    }
                                  />
                                </div>
                              </div>
                            </div>
                          </ListGroup.Item>
                        );
                      })}
                    </ListGroup>
                  </div>
                );
              })}
          </div>
          <div className="d-flex justify-content-between mt-4 finalcodesearchbuttons">
            <Button
              className="codeserach-back"
              onClick={handleBack}
              tabIndex={0}
              disabled={isBackProcessing}
            >
              {showFinalScreen && arrivedViaBackToFinal ? "" : "Back"}
            </Button>
            <Button
              className="codesearch-continue1 d-flex align-items-center justify-content-center"
              onClick={handleFinalize}
            >
              {isFetchingData1 || enableFinalize ? "Adding.." : "Continue"}{" "}
              <span className="continue-check">
                <FaCheck />
              </span>
            </Button>
            {
              <Button
                className="codesearch-continue1 d-flex align-items-center justify-content-center"
                onClick={() => {
                  reduxDispatch(
                    updateLocalState({
                      ...localState,
                      // selectedCodes: updatedSelectedCodes,
                      pendingInstructions: [],
                      currentWarningMessage: null,
                      nextRangeToProcess: null,
                    }),
                  );
                  reduxDispatch(setInitialInjuryState(true));
                  sessionStorage.setItem("newcycle", "false");
                  handleTabChange("Additional Diagnoses");
                  reduxDispatch(setInjuryFlowTracker([]));
                }}
              >
                Add Additional Diagnoses
              </Button>
            }
            {/* {isPrincipalDiagnosisEmpty && (
              <Button
                className="codesearch-continue1 d-flex align-items-center justify-content-center"
                onClick={() => {
                  reduxDispatch(
                    updateLocalState({
                      ...localState,
                      pendingInstructions: [],
                      currentWarningMessage: null,
                      nextRangeToProcess: null,
                    }),
                  );

                  reduxDispatch(setInitialInjuryState(true));

                  sessionStorage.setItem("newcycle", "false");

                  handleTabChange("Principal Diagnosis");

                  reduxDispatch(setInjuryFlowTracker([]));
                  reduxDispatch(clearCodePathHistory()); // reset path history
                  setPathwayStartSnapshot(null); //reset snapshot
                  setPathwayHistoryCheckpoint(0);
                }}
              >
                Add Principal Diagnosis
              </Button>
            )} */}
            {
              <Button
                className="codesearch-continue1 d-flex align-items-center justify-content-center"
                onClick={() => {
                  if (selectedCategory === "Procedure Codes") {
                    if (showFinalScreen) {
                      setTimeout(() => {
                        reduxDispatch(setActiveTab("ACHI Index"));
                        reduxDispatch(setActiveIcon("ICD"));
                      }, 0);
                    }
                  } else {
                    setTimeout(() => {
                      reduxDispatch(setActiveTab("ACHI Index"));
                      reduxDispatch(setActiveIcon("ICD"));
                    }, 0);
                  }
                  handleTabChange("Procedure Codes");
                }}
              >
                Add Procedure
              </Button>
            }
            <Button className="codeserach-back" onClick={handleCancelClick}>
              Cancel
            </Button>
            {/* <Button
              className="codeserach-back"
              onClick={handleBack}
              disabled={isBackProcessing}
            >
              {showFinalScreen && arrivedViaBackToFinal ? "" : "Back"}
            </Button> */}
          </div>
        </div>
      );
    };

    const hasAnyCodes = Object.values(localSelectedCodes).some(
      (codes) => codes && codes.length > 0,
    );

    const CancelConfirmModal = () =>
      showCancelModal ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => setShowCancelModal(false)}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "10px",
              padding: "28px 28px 22px",
              maxWidth: "400px",
              width: "90%",
              boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                marginBottom: "8px",
              }}
            >
              <span style={{ fontSize: "22px" }}>⚠️</span>
              <span
                style={{ fontSize: "17px", fontWeight: 600, color: "#1a1a1a" }}
              >
                Discard changes?
              </span>
            </div>
            <p
              style={{
                color: "#555",
                margin: "0 0 22px 0",
                fontSize: "14px",
                lineHeight: 1.6,
              }}
            >
              You have selected codes that haven't been finalized. Cancelling
              now will discard all your current progress.
            </p>
            <div
              style={{
                display: "flex",
                gap: "10px",
                justifyContent: "flex-end",
              }}
            >
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setShowCancelModal(false)}
              >
                Keep editing
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={() => {
                  setShowCancelModal(false);
                  // === the actual cancel logic (same as your existing cancel handler) ===
                  const newState = {
                    ...localState,
                    pendingInstructions: [],
                    searchTerm: "",
                    showFinalScreen: false,
                    selectedFinalCode: null,
                    currentWarningMessage: null,
                    showError: false,
                    currentInstructionIndex: 0,
                  };
                  reduxDispatch(updateLocalState(newState));
                  reduxDispatch(setRawSearchQuery(""));
                  reduxDispatch(setSearchByCodeId(null));
                  reduxDispatch(handleResetSelectedDetails());
                  reduxDispatch(resetInstructions());
                  reduxDispatch(setInjuryCodeFlowTracker([]));
                  sessionStorage.setItem("newcycle", "false");
                  setShowCodeSearch(false);
                }}
              >
                Discard &amp; cancel
              </Button>
            </div>
          </div>
        </div>
      ) : null;

    return (
      <Container className="w-100 max-w-md mx-auto codeserach-codeassit">
        <CancelConfirmModal />
        {showFinalScreen ? (
          <>
            {injuryFlowTracker.length > 0 && !noInjuryFlowState && (
              <div className="mb-3 mt-2 reprocess-injury-flow">
                <p className="mb-2 ">
                  Do you want to continue with injury coding?
                </p>
                <div className="d-flex gap-3">
                  <Form.Check
                    type="radio"
                    name="injuryContinue"
                    id="injury-yes"
                    label="Yes"
                    checked={localState.injuryContinueChoice === "yes"}
                    onChange={async () => {
                      sessionStorage.setItem("newcycle", "true");
                      reduxDispatch(setInitialInjuryState(false));
                      // Determine the final range based on injuryFlowTracker
                      let finalRange = "";
                      let warningMessage = "";

                      if (injuryFlowTracker.includes("withInjuries")) {
                        if (injuryFlowTracker.includes("withBurns")) {
                          finalRange = "T20-T31";
                          warningMessage = "Do you want to code more burns";
                        } else if (
                          injuryFlowTracker.includes("withFrostBites")
                        ) {
                          finalRange = "T33-T35";
                          warningMessage = "Do you want to code more injuries";
                        } else if (
                          injuryFlowTracker.includes("withPoisoning")
                        ) {
                          finalRange = "T33-T35";
                          warningMessage =
                            "Do you want to code any more poison codes";
                        } else {
                          finalRange =
                            "S00-S09,S10-S29,S20-S29,S30-S39,S40-S49,S50-S59,S60-S69,S70-S79,S80-S89,S90-S99,T00-T07,T08-T14,T20-T31,T33-T35,T66-T78,T79-T79,T80-T88,T89-T89,T90-T98";
                          warningMessage = "Do you want to code more injuries";
                        }
                      } else if (injuryFlowTracker.includes("withBurns")) {
                        finalRange = "T20-T31";
                        warningMessage = "Do you want to code more burns";
                      } else if (injuryFlowTracker.includes("withFrostBites")) {
                        finalRange = "T33-T35";
                        warningMessage = "Do you want to code more injuries";
                      } else if (injuryFlowTracker.includes("withPoisoning")) {
                        finalRange = "T33-T35";
                        warningMessage =
                          "Do you want to code any more poison codes";
                      } else if (
                        injuryFlowTracker.includes("witheffectsOfExternalCause")
                      ) {
                        finalRange = "T66-T78";
                        warningMessage =
                          "Do you want to code any effects of external cause";
                      } else if (
                        injuryFlowTracker.includes(
                          "withEarlyComplicationsOfTrauma",
                        )
                      ) {
                        finalRange = "T79-T79";
                        warningMessage =
                          "Do you want to code any early complications of trauma";
                      } else if (
                        injuryFlowTracker.includes(
                          "withComplicationsOfSurgicalAndMedicalCare",
                        )
                      ) {
                        finalRange = "T80-T88";
                        warningMessage =
                          "Do you want to code any complications from surgery and medical care";
                      } else if (
                        injuryFlowTracker.includes(
                          "withOtherComplicationsOfTrauma",
                        )
                      ) {
                        finalRange = "T89-T89";
                        warningMessage =
                          "Do you want to code any other complications of trauma";
                      } else if (
                        injuryFlowTracker.includes("withSequelaeOfInjuries")
                      ) {
                        finalRange = "T90-T98";
                        warningMessage =
                          "Do you want to code any more sequelae of injuries";
                      }
                      // Add more conditions as needed

                      if (!finalRange) {
                        return;
                      }

                      // Use the extracted function
                      const extensions =
                        await filterTabularCodesForMultipleRanges(
                          finalRange,
                          selectedCategory,
                        );

                      if (extensions.length > 0) {
                        // Create Yes/No options for the final range
                        const yesOption = {
                          rawName: "Yes",
                          rawDescription: "Yes",
                          name: "Yes",
                          description: "Yes",
                          children: [],
                          isFromTabular: false,
                          _isCustomOption: true,
                          _isYesOption: true,
                          _rangeToExecute: finalRange,
                          _warningMessage: warningMessage,
                          _isFinalRange: true,
                        };

                        const noOption = createNoOption();

                        // Reset to initial screen with the range options
                        const newState = {
                          ...localState,
                          injuryContinueChoice: "",
                          showFinalScreen: false,
                          initialSearchDone: true,
                          searchTerm: "",
                          selectedIndex: null,
                          currentCodeSearchData: [yesOption, noOption],
                          filteredCodes: extensions,
                          currentPath: [],
                          currentWarningMessage: warningMessage,
                          nextRangeToProcess: "externalCauseCodes",
                        };
                        // newState.nextRangeToProcess = "externalCauseCodes";
                        reduxDispatch(setInitialInjuryState(true));
                        reduxDispatch(updateLocalState(newState));
                        pushHistoryState(newState);
                      } else {
                        // console.log(
                        //   "No extensions found for final range:",
                        //   finalRange,
                        // );
                        // Handle case where no extensions are found
                      }
                    }}
                  />
                  <Form.Check
                    type="radio"
                    name="injuryContinue"
                    id="injury-no"
                    label="No"
                    checked={localState.injuryContinueChoice === "no"}
                    onChange={() => {
                      setNoInjuryFlowState(true);
                      reduxDispatch(
                        updateLocalState({
                          ...localState,
                          injuryContinueChoice: "no",
                        }),
                      );
                    }}
                  />
                </div>
              </div>
            )}
            {renderFinalScreen()}
          </>
        ) : (
          <>
            <Tabs
              activeKey={selectedCategory}
              id="justify-tab-example"
              className="mb-3"
              justify
              style={{ width: "fit-content" }}
              onSelect={handleTabChange}
            >
              <Tab
                eventKey={selectedCategory}
                title={selectedCategory}
                disabled
              >
                {renderSearchAndList()}
              </Tab>
            </Tabs>
            <div className="d-flex justify-content-between mt-4">
              {(selectedCategory === "Additional Diagnoses" ||
                selectedCategory === "Procedure Codes") &&
                !initialSearchDone && (
                  <Button
                    className="codesearch-continue1 d-flex align-items-center justify-content-center"
                    onClick={() => {
                      sessionStorage.setItem("newcycle", "false");
                      reduxDispatch(setInjuryFlowTracker([]));
                      reduxDispatch(
                        updateLocalState({
                          ...localState,
                          currentWarningMessage: null,
                          pendingInstructions: [],
                          nextRangeToProcess: null,
                        }),
                      );
                      reduxDispatch(setInitialInjuryState(true));
                      if (selectedCategory !== "Procedure Codes") {
                        setTimeout(() => {
                          reduxDispatch(setActiveTab("ACHI Index"));
                        }, 0);
                      }
                      handleTabChange(
                        selectedCategory === "Additional Diagnoses"
                          ? "Procedure Codes"
                          : "Additional Diagnoses",
                      );
                    }}
                  >
                    {selectedCategory === "Additional Diagnoses"
                      ? "Add Procedure"
                      : "Add Additional Diagnoses"}
                  </Button>
                )}
              {initialSearchDone && (
                <Button
                  className="codeserach-back"
                  onClick={handleBack}
                  disabled={
                    isBackProcessing || (!initialSearchDone && !showFinalScreen)
                  }
                >
                  {!initialSearchDone ? "" : "Back"}
                </Button>
              )}
              {/* Show reset only when user is mid-pathway */}
              {(initialSearchDone ||
                currentPath?.length > 0 ||
                searchTerm.length > 0) && (
                <Button
                  className="codeserach-back"
                  style={{
                    backgroundColor: "transparent",
                    borderColor: "#dc3545",
                    color: "#dc3545",
                  }}
                  onClick={handleResetCurrentPathway}
                >
                  Reset
                </Button>
              )}
              {!initialSearchDone &&
                Object.values(localSelectedCodes).some(
                  (codes) => codes && codes.length > 0,
                ) && (
                  <Button
                    className="codeserach-back"
                    onClick={() => {
                      const newState = {
                        ...localState,
                        showFinalScreen: true,
                        currentWarningMessage: null,
                        arrivedViaBackToFinal: true,
                        filteredCodes: localState.filteredCodes || [],
                      };
                      reduxDispatch(updateLocalState(newState));
                      // reduxDispatch(setArrivedViaBackToFinal(true));
                      pushHistoryState(newState);
                    }}
                  >
                    Go to Final Screen
                  </Button>
                )}
              <Button className="codeserach-back" onClick={handleCancelClick}>
                Cancel
              </Button>
            </div>
          </>
        )}
      </Container>
    );
  },
);

export default CodeSearch;
